import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation, useParams, useSearchParams, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import {
  Mail, Inbox, Settings, LogOut, User, Users, Plus, Trash2,
  ToggleLeft, ToggleRight, Clock, CheckCircle2, AlertCircle,
  Building, Menu, X, Shield, Bell, BookOpen, Calendar, CreditCard, PlusCircle,
  Sun, Moon, Monitor, Search, BookUser, Pencil, Forward, Play, Upload, Paperclip, Phone, ArrowRight, ChevronDown, ChevronRight,
  FileText, MessageSquare, Share2, Lock, Key, Copy, Image, AlertTriangle,
  RefreshCw, Server, Building2, UserPlus, Check, Link2, ArrowLeft, Landmark, Receipt, Cloud, ExternalLink,
  Minus, Send, ListChecks,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import Udlaesning from '@/components/Udlaesning';
import axios from 'axios';
import DOMPurify from 'dompurify';
import DocumentsView from '@/components/DocumentsView';
import BRAND, { BASE_URL, inboxEmail, inboxEndpoint } from '@/brand';
import Wordmark from '@/components/Wordmark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Tre tilstande for en videresendelse, udledt af det vi ved:
//   afsendt      — mailen er afleveret til mailserveren, intet svar endnu
//   leveret      — modtagersystemet har kvitteret (fx Pleo)
//   ikke_leveret — leveringsrapport retur: den naaede aldrig frem
// Aeldre bilag har ikke forwarded_at, men resolved_by fortaeller det samme.
function leveringsstatus(item) {
  if (item?.delivery_failed) return 'ikke_leveret';
  if (item?.delivered) return 'leveret';
  const videresendt = item?.forwarded_at
    || /^Regel:|^Videresendt til/.test(item?.resolved_by || '');
  return videresendt ? 'afsendt' : null;
}

// Systemkvitteringen fra modtageren (fx Pleo "Receipt matched") staar ved
// siden af leveringen — ikke i stedet for. De to siger noget forskelligt:
// mailserveren tog imod, OG systemet kunne bruge bilaget.
function MatchetMaerke({ item, lille }) {
  if (!item?.matched_at) return null;
  return (
    <Badge
      className={`bg-violet-100 dark:bg-violet-500/10 text-violet-800 dark:text-violet-400 border-violet-400 dark:border-violet-500/20 cursor-help${lille ? ' text-[10px]' : ''}`}
      title={`${item.matched_by || 'Modtagersystemet'} har matchet bilaget`
        + (item.matched_note ? ` — ${item.matched_note}` : '')
        + (item.matched_at ? ` · ${formatDate(item.matched_at)}` : '')}
    >
      Matchet{item.matched_by ? ` · ${item.matched_by}` : ''}
    </Badge>
  );
}

function LeveringsMaerke({ item, lille }) {
  const status = leveringsstatus(item);
  if (!status) return null;
  const stoerrelse = lille ? ' text-[10px]' : '';
  const modtager = (item.forwarded_to || []).join(', ');
  const afsendtTid = item.forwarded_at || item.resolved_at;
  if (status === 'ikke_leveret') return (
    <Badge
      className={`bg-red-100 dark:bg-red-500/10 text-red-800 dark:text-red-400 border-red-400 dark:border-red-500/20 cursor-help${stoerrelse}`}
      title={`Kunne ikke leveres${item.delivery_error ? ` — ${item.delivery_error}` : ''}${item.delivery_failed_at ? ` · ${formatDate(item.delivery_failed_at)}` : ''}`}
    >Ikke leveret</Badge>
  );
  if (status === 'leveret') return (
    <Badge
      className={`bg-sky-100 dark:bg-sky-500/10 text-sky-800 dark:text-sky-400 border-sky-400 dark:border-sky-500/20 cursor-help${stoerrelse}`}
      title={`${item.delivered_by || 'Modtagersystemet'} har kvitteret for modtagelsen`
        + (item.delivered_note ? ` — ${item.delivered_note}` : '')
        + (item.delivered_at ? ` · ${formatDate(item.delivered_at)}` : '')}
    >Leveret</Badge>
  );
  return (
    <Badge
      className={`bg-amber-100 dark:bg-amber-500/10 text-amber-800 dark:text-amber-400 border-amber-400 dark:border-amber-500/20 cursor-help${stoerrelse}`}
      title={`Afsendt${modtager ? ` til ${modtager}` : ''}`
        // Bilag videresendt foer vi begyndte at gemme forwarded_at har
        // tidspunktet i resolved_at — ellers stod de uden tidsstempel.
        + (afsendtTid ? ` · ${formatDate(afsendtTid)}` : '')
        + ' — afventer svar fra modtageren'}
    >Afsendt</Badge>
  );
}

// sms: om planen overhovedet sender SMS. Samme opdeling som TIERS_MED_SMS i
// server.py — aendres den ene, skal den anden med. Under Holding er et
// ubekraeftet nummer uden betydning, og saa rykker vi ikke for det.
function getTierLabel(tier) {
  const labels = {
    invited: { name: 'Inviteret', color: 'bg-teal-600',   limit: 0,  sms: false },
    free:    { name: 'Gratis',    color: 'bg-zinc-500',   limit: 1,  sms: false },
    solo:    { name: 'Solo',      color: 'bg-slate-500',  limit: 1,  sms: false },
    holding: { name: 'Holding',   color: 'bg-brand-500',   limit: 3,  sms: true  },
    pro:     { name: 'Pro',       color: 'bg-highlight-500', limit: 10, sms: true  },
    // legacy fallbacks
    basic:   { name: 'Basic',     color: 'bg-brand-500',   limit: 3,  sms: true  },
    premium: { name: 'Premium',   color: 'bg-highlight-500', limit: 10, sms: true  },
  };
  return labels[tier] || labels.solo;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Intl.DateTimeFormat('da-DK', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    }).format(new Date(dateStr));
  } catch {
    return dateStr;
  }
}

// ─── DigitalBox-designet ─────────────────────────────────────────────────────
//
// Indbakken i tre kolonner fra Figma (docs/design/digitalbox/app-mock). Slaas
// til af brandprofilens appDesign; hentpost koerer den foldbare liste som foer.
// Farverne db-* findes kun under DigitalBox — se index.css.

const TRE_KOLONNER = BRAND.appDesign === 'tre-kolonner';

// Kilden paa en sag, som kunden kender den — ikke som den hedder i databasen
const KILDE_NAVN = { memo: 'Digital Post', eboks: 'e-Boks', email: 'Bilagsadresse', manual: 'Upload' };
const kildeNavn = (item) => KILDE_NAVN[item?.source] || 'Post';

// Prikken paa kilde-tagget, saa man kan se hvor posten kom fra uden at laese:
// Digital Post i DigitalBox-turkis, e-Boks i roedt som deres eget maerke.
const KILDE_PRIK = { memo: 'bg-db-label', eboks: 'bg-red-500', email: 'bg-db-route', manual: 'bg-slate-400' };
// ── Byggeklodser til DigitalBox' undersider ──
// Figma-landingssidens kort: et lyst med grafik over titel og tekst, og et
// moerkt med raekker. Undersiderne bygges af dem, saa de ligner indbakken.

const DB_KNAP = 'inline-flex items-center justify-center gap-[7px] px-3 py-[7px] rounded-lg border border-th-border text-sm font-medium leading-5 text-th-text hover:bg-th-hover transition-colors disabled:opacity-50';
const DB_KNAP_PRIMAER = 'inline-flex items-center justify-center gap-[7px] px-3 py-[7px] rounded-lg bg-db-ink text-white text-sm font-medium leading-5 hover:opacity-90 transition-opacity disabled:opacity-50';
const DB_IKONKNAP = 'p-2 rounded-lg text-th-text-muted hover:bg-th-hover hover:text-th-text transition-colors';

function DbSideHoved({ titel, tekst, children, className = 'mb-8' }) {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-end justify-between gap-4 ${className}`}>
      <div className="min-w-0">
        <h1 className="font-heading text-[32px] sm:text-[40px] font-semibold leading-[1.1] tracking-[-0.8px] text-th-text">{titel}</h1>
        {tekst && <p className="mt-2 text-base leading-6 text-th-text-muted">{tekst}</p>}
      </div>
      {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
    </div>
  );
}

// Lyst kort: grafik (children) foroverst, titel og tekst under, som i designet
function DbKort({ grafik, titel, tekst, handlinger, children, className = '' }) {
  return (
    <div className={`rounded-[20px] bg-th-inset p-6 sm:p-7 flex flex-col gap-6 ${className}`}>
      {grafik && <div className="flex flex-col gap-2">{grafik}</div>}
      {(titel || handlinger) && (
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {titel && <h2 className="font-heading text-lg sm:text-xl font-semibold leading-snug tracking-[-0.3px] text-th-text break-words">{titel}</h2>}
            {tekst && <div className="mt-2 text-[15px] leading-6 text-th-text-muted">{tekst}</div>}
          </div>
          {handlinger && <div className="flex items-center gap-1 shrink-0">{handlinger}</div>}
        </div>
      )}
      {children}
    </div>
  );
}

// Hvid "chip" inde i det lyse kort: ikon, titel og undertekst
function DbChip({ ikon: Ikon, titel, tekst, className = '' }) {
  return (
    <div className={`flex items-center gap-3 bg-th-card border border-th-border-light rounded-xl px-4 py-3 ${className}`}>
      {Ikon && <Ikon size={16} className="shrink-0 text-th-text" />}
      <div className="min-w-0">
        <p className="text-sm font-medium leading-5 text-th-text truncate">{titel}</p>
        {tekst && <p className="text-xs leading-4 text-th-text-muted truncate">{tekst}</p>}
      </div>
    </div>
  );
}

// Initialer i en cirkel — avataren fra designets moerke kort
const initialer = (tekst) => (tekst || '?').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean).map(d => d[0]).slice(0, 2).join('').toUpperCase() || '?';
function DbAvatar({ tekst, farve = 'bg-db-route' }) {
  return (
    <span className={`w-9 h-9 rounded-full ${farve} text-white text-xs font-semibold flex items-center justify-center shrink-0`}>
      {initialer(tekst)}
    </span>
  );
}

function DbSwitch({ til, onSkift, titel }) {
  return (
    <button type="button" onClick={onSkift} title={titel} aria-pressed={til}
      className={`relative w-9 h-5 rounded-full transition-colors shrink-0 ${til ? 'bg-db-label' : 'bg-th-border'}`}>
      <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${til ? 'translate-x-4' : ''}`} />
    </button>
  );
}

// Tallinjen fra designet: trinene som turkise cirkler paa én linje. aktiv er
// det trin, man staar paa (1-baseret); stoerre end antal = alle er gjort.
function DbTrinLinje({ antal, aktiv, className = 'mb-8' }) {
  const andel = antal > 1 ? Math.min(1, Math.max(0, (aktiv - 1) / (antal - 1))) : 1;
  return (
    <div className={`relative flex items-center justify-between ${className}`}>
      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-th-border-light" />
      <div className="absolute left-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-db-label transition-all" style={{ width: `${andel * 100}%` }} />
      {Array.from({ length: antal }, (_, i) => {
        const nr = i + 1;
        const faerdig = nr < aktiv;
        const nu = nr === aktiv;
        return (
          <span key={nr} className={`relative w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold transition-colors ${
            faerdig || nu ? 'bg-db-label text-white' : 'bg-th-card border-2 border-th-border text-th-text-muted'
          } ${nu ? 'ring-4 ring-db-label-soft' : ''}`}>
            {faerdig ? <Check size={16} /> : nr}
          </span>
        );
      })}
    </div>
  );
}

// Det moerke kort fra designet: hvordan en kanal er forbundet, og de tre
// trin, der skal til. Vises i "Tilføj selskab", hvor man skifter mellem
// Digital Post og e-Boks. Trinene foelger guiden paa /opret.
const KANALER = {
  digitalpost: {
    navn: 'Digital Post', ikon: Landmark, undertekst: 'Det offentlige',
    forbindelse: ['Klientcertifikat', 'Klientcertifikat'],
    trin: [
      { titel: `Opret ${BRAND.name} som modtagersystem`, tekst: 'I Digital Post Admin' },
      { titel: 'Giv adgang i Rettighedsportalen', tekst: 'Kræver en person med tegningsret' },
      { titel: 'Posten lander i indbakken', tekst: 'Og I inviterer kolleger og revisor' },
    ],
    overskrift: 'Tilsluttet ad den officielle vej',
    forklaring: `${BRAND.name} er oprettet som modtagersystem, og adgangen gives via Rettighedsportalen. Forbindelsen verificeres med klientcertifikater begge veje — ikke med en adgangskode, der kan deles rundt.`,
  },
  eboks: {
    navn: 'e-Boks', ikon: Mail, undertekst: 'e-Boks Erhverv',
    // Ingen tekniske maerker her: kunden skal bare se, at brevene kommer ind
    forbindelse: null,
    trin: [
      { titel: 'Log ind på e-Boks Erhverv', tekst: 'Med MitID, som den bruger der er knyttet til virksomheden' },
      { titel: 'Opsæt integration til system via API (XML)', tekst: 'Under Automatisk videresendelse af post' },
      { titel: 'Indsæt jeres modtage-URL og test', tekst: 'Testbeskeden lander i indbakken' },
    ],
    overskrift: 'e-Boks hentes direkte ind',
    forklaring: `Beskeder fra banker, løn og forsikring sendes fra e-Boks Erhverv til ${BRAND.name} gennem e-Boks' egen integration, og vedhæftningerne gemmes. Ingen videresendte mails og ingen manuel upload.`,
  },
};

function DbTilslutning({ kanal = 'digitalpost' }) {
  const k = KANALER[kanal];
  const Ikon = k.ikon;
  const maerke = (tekst, moerk) => (
    <span className={`px-2.5 py-1 rounded-md text-xs font-semibold whitespace-nowrap ${moerk ? 'bg-white/10 text-white' : 'bg-db-label text-db-ink'}`}>{tekst}</span>
  );
  return (
    <div className="rounded-[20px] bg-db-ink p-5 sm:p-8 flex flex-col gap-7 text-white">
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_minmax(120px,1fr)_1fr] items-center gap-3">
        <div className="rounded-2xl bg-white/[0.08] p-5">
          <Ikon size={20} className="mb-3" />
          <p className="font-heading text-lg font-semibold leading-6">{k.navn}</p>
          <p className="text-sm text-white/60">{k.undertekst}</p>
        </div>
        {k.forbindelse ? (
          <div className="flex sm:flex-col items-center justify-center gap-2 sm:gap-3">
            <div className="flex flex-col items-center gap-1.5 w-full">{maerke(k.forbindelse[0], false)}<span className="hidden sm:block h-[3px] w-full rounded-full bg-db-label" /></div>
            <div className="flex flex-col items-center gap-1.5 w-full"><span className="hidden sm:block h-[3px] w-full rounded-full bg-white" />{maerke(k.forbindelse[1], true)}</div>
          </div>
        ) : (
          // Et brev paa vej ad linjen — samme billede som i landingssidens grafik
          <div className="relative flex items-center justify-center h-10">
            <span className="hidden sm:block absolute inset-x-0 top-1/2 -translate-y-1/2 h-[3px] rounded-full bg-db-label" />
            <span className="relative flex items-center justify-center w-10 h-8 rounded-md bg-white text-db-ink shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
              <Mail size={18} />
            </span>
          </div>
        )}
        <div className="rounded-2xl bg-db-label p-5 text-db-ink">
          <p className="font-heading text-lg font-semibold leading-6">{BRAND.name}</p>
          <p className="text-sm font-medium">Modtagersystem</p>
        </div>
      </div>

      <div>
        <p className="text-sm text-white/60 mb-3">Opsætning, én gang</p>
        <ol className="divide-y divide-white/15">
          {k.trin.map((t, i) => (
            <li key={t.titel} className="flex items-start gap-3.5 py-3.5 first:pt-0 last:pb-0">
              <span className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold border-2 border-db-label">{i + 1}</span>
              <div>
                <p className="text-base font-medium leading-6">{t.titel}</p>
                <p className="text-sm text-white/60">{t.tekst}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div>
        <h2 className="font-heading text-xl sm:text-2xl font-semibold leading-tight tracking-[-0.4px]">{k.overskrift}</h2>
        <p className="mt-2 text-[15px] leading-6 text-white/70 max-w-2xl">{k.forklaring}</p>
      </div>
    </div>
  );
}

// Sagens gang, som designet viser den: Ulæst -> Læst -> Færdigbehandlet, og ved
// siden af hvem den er tildelt og hvor mange noter den har. I listen udelades
// "Læst": der siger den manglende blaa prik det samme med mindre stoej.
function StatusTags({ item, visLaest = false }) {
  const noter = (item?.notes || []).length;
  return (
    <>
      {item.resolved ? <DbTag variant="faerdig">Færdigbehandlet</DbTag>
        : !item.read ? <DbTag variant="ulaest">Ulæst</DbTag>
        : visLaest ? <DbTag>Læst</DbTag> : null}
      {item.assigned_to_name && !item.resolved && (
        <DbTag variant="route">Tildelt {item.assigned_to_name.split(/\s+/)[0]}</DbTag>
      )}
      {noter > 0 && <DbTag>{noter === 1 ? '1 note' : `${noter} noter`}</DbTag>}
    </>
  );
}

// Kilden som et ikon, man genkender uden at laese: bygningen for det
// offentlige, brevet for e-Boks, kvitteringen for bilag og papiret for upload.
const KILDE_IKON = { memo: Landmark, eboks: Mail, email: Receipt, manual: FileText };

// I listen staar kun det vigtigste maerke, saa den kan overskues: det der
// kraever handling foerst, saa det nye, saa hvem der har sagen.
function ListeStatus({ item }) {
  if (item.resolved) return <DbTag variant="faerdig">Færdigbehandlet</DbTag>;
  if (item.pending_forward) return <DbTag variant="advarsel">Afventer</DbTag>;
  if (leveringsstatus(item) === 'ikke_leveret') return <DbTag variant="fejl">Ikke leveret</DbTag>;
  if (!item.read) return <DbTag variant="ulaest">Ny</DbTag>;
  if (item.assigned_to_name) return <DbTag variant="route">Tildelt {item.assigned_to_name.split(/\s+/)[0]}</DbTag>;
  return null;
}

const KildeTag = ({ item }) => (
  <DbTag variant="moerk" prik={KILDE_PRIK[item?.source] || 'bg-slate-400'}>{kildeNavn(item)}</DbTag>
);

// "Skattestyrelsen <noreply@skat.dk>" -> "Skattestyrelsen". Uden navn staar
// adressen tilbage, og uden adresse emnet: listen maa aldrig vise en tom raekke.
function afsenderNavn(item) {
  const fra = (item?.from_address || '').trim();
  const navn = fra.replace(/<[^>]*>/, '').replace(/^["']|["']$/g, '').trim();
  return navn || fra.replace(/[<>]/g, '') || item?.subject || '(Ukendt afsender)';
}

// Listen viser tid i dag, "I går" i gaar og ellers datoen — som i designet
function kortTid(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const nu = new Date();
  const dagStart = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dage = Math.round((dagStart(nu) - dagStart(d)) / 86400000);
  if (dage === 0) return d.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
  if (dage === 1) return 'I går';
  return d.toLocaleDateString('da-DK', {
    day: 'numeric', month: 'short', ...(d.getFullYear() !== nu.getFullYear() ? { year: 'numeric' } : {}),
  }).replace('.', '');
}

const formatCvr = (cvr) => (/^\d{8}$/.test(cvr || '') ? cvr.replace(/(\d{2})(?=\d)/g, '$1 ') : cvr || '');

// "Printed label. Every source, state and destination on the page wears one."
// Tre varianter i designet: lys (kilde og format), moerk (kilden paa den aabne
// sag) og route (hvor sagen er sendt hen).
function DbTag({ variant = 'lys', children, title, prik: prikFarve, className = '' }) {
  const flade = {
    lys: 'bg-th-card border border-th-border text-th-text',
    moerk: 'bg-db-ink text-white',
    route: 'bg-db-route-soft text-db-ink',
    ulaest: 'bg-db-label text-white',
    faerdig: 'bg-db-label-soft text-db-ink',
    advarsel: 'bg-amber-100 text-amber-900',
    fejl: 'bg-red-100 text-red-800',
  }[variant];
  const prik = prikFarve || { moerk: 'bg-db-label', route: 'bg-db-route', faerdig: 'bg-db-label', advarsel: 'bg-amber-500', fejl: 'bg-red-500' }[variant];
  return (
    <span title={title} className={`inline-flex items-center gap-1.5 px-[9px] py-[5px] rounded-[5px] text-xs font-semibold leading-4 tracking-[0.24px] whitespace-nowrap ${flade} ${className}`}>
      {prik && <span className={`db-prik w-1.5 h-1.5 rounded-full shrink-0 ${prik}`} />}
      {children}
    </span>
  );
}

// ─── Theme Switcher ──────────────────────────────────────────────────────────

function ThemeSwitcher({ kompakt = false }) {
  const { theme, setTheme } = useTheme();
  const options = [
    { value: 'light', icon: Sun, label: 'Lys' },
    { value: 'system', icon: Monitor, label: 'System' },
    { value: 'dark', icon: Moon, label: 'Mork' },
  ];
  return (
    <div className={`flex items-center gap-1 bg-th-input rounded-lg p-1${kompakt ? ' w-full' : ''}`}>
      {options.map(({ value, icon: Icon, label }) => (
        <button
          key={value}
          onClick={() => setTheme(value)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${kompakt ? 'flex-1 justify-center ' : ''}${
            theme === value
              ? 'bg-th-hover text-th-text shadow-sm'
              : 'text-th-text-muted hover:text-th-text-secondary'
          }`}
          title={label}
        >
          <Icon size={14} />
          {!kompakt && <span className="hidden sm:inline">{label}</span>}
        </button>
      ))}
    </div>
  );
}

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ user, logout, mobileOpen, setMobileOpen, permissions }) {
  const tierInfo = getTierLabel(user?.subscription_tier);
  const navigate = useNavigate();
  const location = useLocation();
  const permissionsLoaded = permissions !== null;
  const role = permissions?.role || 'owner';
  const allowedPages = permissions?.allowed_pages || [];
  const canSee = (page) => permissionsLoaded && (role === 'owner' || allowedPages.length === 0 || allowedPages.includes(page));

  // navigate og ikke window.location: det sidste er en fuld browser-navigation,
  // saa hele appen blev hentet, parset og bygget op igen ved hvert menuklik —
  // med tomme lister og et blink, indtil kaldene var hjemme igen.
  const navBtn = (path, icon, label) => (
    <button
      onClick={() => { setMobileOpen(false); navigate(path); }}
      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
        location.pathname === path || (path === '/indbakke' && location.pathname.startsWith('/indbakke/'))
          ? 'bg-th-hover text-th-text'
          : 'text-th-text-secondary hover:bg-th-hover hover:text-th-text'
      }`}
    >
      {icon}
      <span>{label}</span>
    </button>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`always-dark fixed left-0 top-0 h-full w-64 bg-[#111827] border-r border-th-border p-6 flex flex-col z-50 transition-transform lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <button onClick={() => navigate('/indbakke')} className="flex items-center gap-3 mb-8 hover:opacity-80 transition-opacity">
          <Mail size={32} className="text-brand-500" />
          <span className="text-xl font-heading font-bold text-th-text">
            <Wordmark />
          </span>
        </button>

        <nav className="space-y-2 flex-1">
          {!permissionsLoaded ? (
            <div className="space-y-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-11 rounded-lg bg-th-hover/40 animate-pulse" />
              ))}
            </div>
          ) : (
            <>
              {canSee('indbakke') && navBtn('/indbakke', <Inbox size={20} />, 'Indbakke')}
              {canSee('selskaber') && navBtn('/selskaber', <Building size={20} />, 'Mine selskaber')}
              {canSee('modtagere') && navBtn('/modtagere', <Users size={20} />, 'Modtagere')}
              {canSee('videresend') && navBtn('/videresend', <Settings size={20} />, 'Videresendelse')}
              {canSee('adressebog') && navBtn('/adressebog', <BookUser size={20} />, 'Adressebog')}
              {canSee('dokumenter') && navBtn('/dokumenter', <FileText size={20} />, 'Dokumenter')}
            </>
          )}
          {user?.is_admin && (
            <div className="pt-3 mt-3 border-t border-th-border space-y-2">
              <p className="text-xs uppercase tracking-wider text-th-text-faint font-semibold mb-2 px-3">Admin</p>
              {navBtn('/admin', <Users size={20} />, 'Abonnenter')}
              {navBtn('/admin/indbakke', <Inbox size={20} />, 'Alt indbakke')}
            </div>
          )}
        </nav>

        <div>
          {navBtn('/profil', <User size={20} />, 'Min profil')}
          {navBtn('/api-adgang', <Key size={20} />, 'API')}

          <Card className="p-4 bg-th-input border-th-border-light mt-3 mb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold">Abonnement</span>
              <Badge className={`${tierInfo.color} text-white`}>{tierInfo.name}</Badge>
            </div>
            <p className="text-xs text-th-text-secondary truncate">{user?.email}</p>
          </Card>

          <div className="my-3"><ThemeSwitcher /></div>

          <Button
            onClick={logout}
            variant="ghost"
            className="w-full flex items-center gap-2 text-th-text-secondary hover:text-th-text hover:bg-th-hover"
          >
            <LogOut size={20} />
            Log ud
          </Button>
        </div>
      </aside>
    </>
  );
}

// DigitalBox' sidebar: lys, med selskaberne oeverst som i designet. Tallene
// kommer fra indbakken (ulaesteTal pr. CVR), saa de altid stemmer med listen;
// uden for indbakken staar selskaberne uden tal i stedet for et gammelt tal.
function DbSidebar({ user, logout, mobileOpen, setMobileOpen, permissions, ulaesteTal }) {
  const tierInfo = getTierLabel(user?.subscription_tier);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [selskaber, setSelskaber] = useState([]);
  const [selskabsListe, setSelskabsListe] = useState(false);
  const permissionsLoaded = permissions !== null;
  const role = permissions?.role || 'owner';
  const allowedPages = permissions?.allowed_pages || [];
  const canSee = (page) => permissionsLoaded && (role === 'owner' || allowedPages.length === 0 || allowedPages.includes(page));

  useEffect(() => {
    axios.get(`${API}/companies`).then(r => setSelskaber(r.data || [])).catch(() => {});
  }, []);

  const iIndbakken = location.pathname === '/indbakke' || location.pathname.startsWith('/indbakke/');
  const filter = searchParams.get('filter') || 'all';
  const gaaTil = (sti) => { setMobileOpen(false); navigate(sti); };

  const overskrift = (tekst) => (
    <p className="pl-2 pb-1 text-xs font-semibold leading-4 tracking-[0.72px] uppercase text-th-text-muted">{tekst}</p>
  );
  const visning = ({ sti, ikon: Ikon, tekst, aktiv }) => (
    <button key={sti} onClick={() => gaaTil(sti)}
      className={`w-full flex items-center gap-2.5 px-2 py-[7px] rounded-lg text-sm font-medium leading-5 transition-colors text-left ${
        aktiv ? 'bg-db-ink text-white' : 'text-th-text-muted hover:bg-th-card hover:text-th-text'
      }`}>
      <Ikon size={16} className="shrink-0" />
      <span className="truncate">{tekst}</span>
    </button>
  );

  return (
    <>
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}
      <aside className={`fixed left-0 top-0 h-full w-[248px] bg-th-sidebar border-r border-th-border-light px-4 py-[22px] flex flex-col gap-6 z-50 overflow-y-auto transition-transform lg:translate-x-0 min-[1400px]:static min-[1400px]:shrink-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <button onClick={() => gaaTil('/indbakke')} className="self-start pl-1.5 hover:opacity-80 transition-opacity">
          {/* Moerk tekst paa lyst, hvid paa moerkt — "Box"-kassen er ens i begge */}
          <img src={`/brand/${BRAND.key}/logo-app.svg`} width="98" height="24" alt={BRAND.name} className="dark:hidden" />
          <img src={`/brand/${BRAND.key}/logo-app-moerk.svg`} width="98" height="24" alt={BRAND.name} className="hidden dark:block" />
        </button>

        {canSee('indbakke') && selskaber.length > 0 && (() => {
          const samlet = Object.values(ulaesteTal || {}).reduce((x, y) => x + y, 0);
          const valgtSelskab = selskaber.find(c => location.pathname === `/indbakke/${c.id}`);
          const alleAktiv = location.pathname === '/indbakke';
          const raekke = ({ noegle, aktiv, Ikon, tekst, tal, onClick, ekstra = null }) => (
            <button key={noegle} onClick={onClick}
              className={`w-full flex items-center gap-2.5 pl-2 pr-2.5 py-2 rounded-lg border text-sm font-medium leading-5 text-left transition-colors ${
                aktiv ? 'bg-th-card border-th-border-light text-th-text' : 'border-transparent text-th-text-muted hover:bg-th-card hover:text-th-text'
              }`}>
              <Ikon size={16} className="shrink-0" />
              <span className="flex-1 min-w-0 truncate">{tekst}</span>
              {tal > 0 && (
                <span className={`shrink-0 px-[7px] py-px rounded-[10px] text-xs font-semibold leading-4 tracking-[0.24px] ${aktiv ? 'bg-db-label text-white' : 'bg-th-border-light text-th-text'}`}>{tal}</span>
              )}
              {ekstra}
            </button>
          );
          const alleRaekke = (luk) => raekke({ noegle: 'alle', aktiv: alleAktiv, Ikon: Inbox, tekst: 'Alle', tal: samlet,
            onClick: () => { luk?.(); gaaTil('/indbakke'); } });
          const selskabsRaekker = (luk) => selskaber.map(c => raekke({
            noegle: c.id, aktiv: valgtSelskab?.id === c.id, Ikon: Building2, tekst: c.name, tal: ulaesteTal?.[c.cvr] || 0,
            onClick: () => { luk?.(); gaaTil(`/indbakke/${c.id}`); } }));

          // Fra tre selskaber samles de i ét felt, der foldes ud — ellers
          // skubber listen resten af menuen ud af syne
          if (selskaber.length >= 3) {
            return (
              <div className="flex flex-col gap-1">
                {overskrift('Selskaber')}
                <button onClick={() => setSelskabsListe(v => !v)} aria-expanded={selskabsListe}
                  className="w-full flex items-center gap-2.5 pl-2 pr-2.5 py-2 rounded-lg border border-th-border-light bg-th-card text-sm font-medium leading-5 text-left text-th-text">
                  {valgtSelskab ? <Building2 size={16} className="shrink-0" /> : <Inbox size={16} className="shrink-0" />}
                  <span className="flex-1 min-w-0 truncate">{valgtSelskab ? valgtSelskab.name : 'Alle selskaber'}</span>
                  {(valgtSelskab ? ulaesteTal?.[valgtSelskab.cvr] || 0 : samlet) > 0 && (
                    <span className="shrink-0 px-[7px] py-px rounded-[10px] text-xs font-semibold leading-4 tracking-[0.24px] bg-db-label text-white">
                      {valgtSelskab ? ulaesteTal?.[valgtSelskab.cvr] : samlet}
                    </span>
                  )}
                  <ChevronDown size={16} className={`shrink-0 text-th-text-muted transition-transform ${selskabsListe ? 'rotate-180' : ''}`} />
                </button>
                {selskabsListe && (
                  <div className="flex flex-col gap-0.5 max-h-60 overflow-y-auto rounded-lg border border-th-border-light bg-th-body p-1">
                    {alleRaekke(() => setSelskabsListe(false))}
                    {selskabsRaekker(() => setSelskabsListe(false))}
                  </div>
                )}
              </div>
            );
          }
          return (
            <div className="flex flex-col gap-1">
              {overskrift('Selskaber')}
              {/* Faelles indbakke for alle selskaber, med det samlede antal ulaeste */}
              {selskaber.length > 1 && alleRaekke()}
              {selskabsRaekker()}
            </div>
          );
        })()}

        <nav className="flex flex-col gap-0.5">
          {overskrift('Visninger')}
          {!permissionsLoaded ? (
            [...Array(4)].map((_, i) => <div key={i} className="h-9 rounded-lg bg-th-card animate-pulse" />)
          ) : (
            <>
              {canSee('indbakke') && visning({ sti: '/indbakke', ikon: Inbox, tekst: 'Indbakke',
                aktiv: iIndbakken && !['mine', 'resolved'].includes(filter) })}
              {canSee('indbakke') && visning({ sti: '/indbakke?filter=mine', ikon: UserPlus, tekst: 'Tildelt til mig',
                aktiv: iIndbakken && filter === 'mine' })}
              {canSee('indbakke') && visning({ sti: '/indbakke?filter=resolved', ikon: Check, tekst: 'Færdigbehandlet',
                aktiv: iIndbakken && filter === 'resolved' })}
              {canSee('videresend') && visning({ sti: '/videresend', ikon: Forward, tekst: 'Regler for videresendelse',
                aktiv: location.pathname === '/videresend' })}
            </>
          )}
        </nav>

        {permissionsLoaded && (
          <nav className="flex flex-col gap-0.5">
            {overskrift('Opsætning')}
            {canSee('selskaber') && visning({ sti: '/selskaber', ikon: Building, tekst: 'Mine selskaber', aktiv: location.pathname === '/selskaber' })}
            {canSee('modtagere') && visning({ sti: '/modtagere', ikon: Users, tekst: 'Modtagere', aktiv: location.pathname === '/modtagere' })}
            {canSee('adressebog') && visning({ sti: '/adressebog', ikon: BookUser, tekst: 'Adressebog', aktiv: location.pathname === '/adressebog' })}
            {canSee('dokumenter') && visning({ sti: '/dokumenter', ikon: FileText, tekst: 'Dokumenter', aktiv: location.pathname.startsWith('/dokumenter') })}
          </nav>
        )}

        {user?.is_admin && (
          <nav className="flex flex-col gap-0.5">
            {overskrift('Admin')}
            {visning({ sti: '/admin', ikon: Users, tekst: 'Abonnenter', aktiv: location.pathname === '/admin' })}
            {visning({ sti: '/admin/onboarding', ikon: ListChecks, tekst: 'Onboarding', aktiv: location.pathname === '/admin/onboarding' })}
            {visning({ sti: '/admin/selskaber', ikon: Building, tekst: 'Alle selskaber', aktiv: location.pathname === '/admin/selskaber' })}
            {visning({ sti: '/admin/indbakke', ikon: Inbox, tekst: 'Alt indbakke', aktiv: location.pathname === '/admin/indbakke' })}
          </nav>
        )}

        <div className="mt-auto flex flex-col gap-3">
          {/* Profilen hoerer til brugeren, ikke til opsaetningen — derfor hernede
              ved abonnementet og log ud */}
          {visning({ sti: '/profil', ikon: User, tekst: 'Min profil', aktiv: location.pathname === '/profil' })}
          <div className="p-3 rounded-lg bg-th-card border border-th-border-light">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold tracking-[0.72px] uppercase text-th-text-muted">Abonnement</span>
              <Badge className={`${tierInfo.color} text-white`}>{tierInfo.name}</Badge>
            </div>
            <p className="text-xs text-th-text-muted truncate">{user?.email}</p>
          </div>
          <ThemeSwitcher kompakt />
          <button onClick={logout}
            className="w-full flex items-center gap-2.5 px-2 py-[7px] rounded-lg text-sm font-medium text-th-text-muted hover:bg-th-card hover:text-th-text transition-colors">
            <LogOut size={16} /> Log ud
          </button>
        </div>
      </aside>
    </>
  );
}

// ─── PDF Viewer (scales to fit container) ────────────────────────────────────

function PdfViewer({ url, filename }) {
  return (
    <div className="rounded-lg border border-th-border-light overflow-hidden">
      {/* Mobile: fixed 360px width centered, Desktop: full width */}
      <div className="sm:hidden flex justify-center bg-gray-100 dark:bg-gray-900 overflow-hidden">
        <iframe
          src={url}
          title={filename}
          className="border-0 h-[70vh]"
          style={{ width: '360px' }}
        />
      </div>
      <iframe
        src={url}
        title={filename}
        className="hidden sm:block w-full border-0 h-[60vh] lg:h-[700px]"
      />
      <div className="bg-th-hover border-t border-th-border-light px-3 py-2 flex items-center justify-between">
        <span className="text-xs text-th-text-muted truncate">{filename}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 bg-btn hover:bg-btn-hover text-btn-fg px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
        >
          Åbn PDF
        </a>
      </div>
    </div>
  );
}

// ─── Expanded Message ─────────────────────────────────────────────────────────

// ─── Noter paa en sag ─────────────────────────────────────────────────────────

function NoteThread({ item, currentEmail, draft, setDraft, saving, onAdd, onDelete }) {
  const notes = item.notes || [];
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('da-DK', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return ''; }
  };

  return (
    <div className="px-3 pb-3 pt-3 border-t border-th-border">
      <div className="flex items-center gap-2 mb-2">
        <MessageSquare size={14} className="text-th-text-muted" />
        <span className="text-xs font-semibold uppercase tracking-wider text-th-text-muted">
          Noter{notes.length > 0 ? ` (${notes.length})` : ''}
        </span>
      </div>

      {notes.length > 0 && (
        <div className="space-y-2 mb-3">
          {notes.map(n => (
            <div key={n.id} className="group flex items-start gap-2 bg-th-hover border border-th-border rounded-lg px-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-th-text whitespace-pre-wrap break-words">{n.text}</p>
                <p className="text-[11px] text-th-text-muted mt-1">
                  {n.author_name || n.author_email} · {fmt(n.created_at)}
                </p>
              </div>
              {n.author_email === currentEmail && (
                <button
                  onClick={e => { e.stopPropagation(); onDelete(item, n.id); }}
                  title="Slet note"
                  className="shrink-0 p-1 rounded text-th-text-faint hover:text-red-400 hover:bg-th-border transition-colors sm:opacity-0 sm:group-hover:opacity-100"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-start gap-2">
        <textarea
          value={draft} onChange={e => setDraft(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && draft.trim()) {
              e.preventDefault(); onAdd(item, draft);
            }
          }}
          maxLength={2000} rows={2}
          placeholder="Skriv en note til sagen…"
          className="flex-1 px-2.5 py-2 rounded-lg bg-th-input border border-th-border-light text-sm text-th-text placeholder:text-th-text-muted resize-none focus:outline-none focus:border-brand-500"
        />
        <button
          onClick={e => { e.stopPropagation(); onAdd(item, draft); }}
          disabled={saving || !draft.trim()}
          className="shrink-0 px-3 py-2 rounded-lg text-xs font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-40"
        >
          {saving ? 'Gemmer…' : 'Tilføj'}
        </button>
      </div>
    </div>
  );
}

// Brødteksten er et stykke HTML klippet ind i vores egen side, så her holdes
// en stram allowlist: intet stilark, ingen ukendte tags. Hele dokumenter
// (HTML-bilag) vises derimod i en sandkasse og renses af
// MAIL_DOKUMENT_RENS herunder, hvor afsenderens eget stilark må blive.
const MAIL_HTML_RENS = {
  // header/footer/section m.fl. er rene beholdere uden opførsel. Uden dem
  // pilles nyere mails fra hinanden, mens sikkerheden er den samme.
  ALLOWED_TAGS: ['p', 'br', 'b', 'i', 'em', 'strong', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'div', 'span', 'img', 'pre', 'code',
    'blockquote', 'hr', 'header', 'footer', 'section', 'article', 'main', 'figure', 'figcaption', 'small', 'u'],
  ALLOWED_ATTR: ['href', 'src', 'alt', 'style', 'class', 'target', 'width', 'height', 'colspan', 'rowspan'],
  ALLOW_DATA_ATTR: false,
};

const erHtmlBilag = (navn) => /\.html?$/i.test(navn || '');

// En 'fixed' popup tegnes ud fra ikonets plads og kan derfor lande halvt uden
// for skærmen — og så er knapperne i bunden ikke til at klikke på. Vi måler
// boksen, når den er tegnet, og skubber den ind. Ref'en er med vilje en
// inline-funktion: den kaldes igen ved hver gentegning, så boksen også
// flyttes, når noten folder sig ud og gør den højere.
const holdPaaSkaermen = (el) => {
  if (!el) return;
  const kant = 8;
  const r = el.getBoundingClientRect();
  if (r.bottom > window.innerHeight - kant) {
    el.style.top = `${Math.max(kant, window.innerHeight - kant - r.height)}px`;
  } else if (r.top < kant) {
    el.style.top = `${kant}px`;
  }
};

// Et HTML-bilag er et helt dokument med sit eget stilark — bredde, skrifter,
// farver. Fjerner vi <style>, står brevet tilbage som løs tekst og ligner
// ikke det, afsenderen sendte. Derfor beholdes stilarket, og dokumentet
// vises i stedet i en sandkasse-iframe, hvor hverken CSS eller andet kan nå
// resten af siden. Alt der kan handle af sig selv ryger ud.
const MAIL_DOKUMENT_RENS = {
  WHOLE_DOCUMENT: true,
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'base', 'link'],
  FORBID_ATTR: ['srcdoc', 'formaction', 'ping', 'http-equiv'],
};

// .htm-bilag fra ældre afsendersystemer er ofte ISO-8859-1. Vi læser bytes og
// vælger tegnsæt selv: UTF-8 først, og kun hvis den afviser indholdet, falder
// vi tilbage til windows-1252 — ellers ville æ, ø og å blive til �.
function afkodHtmlBilag(buffer) {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('windows-1252').decode(buffer);
  }
}

function ExpandedMessage({ item, adminMode = false }) {
  const [activeTab, setActiveTab] = useState(0);
  const [fileUrls, setFileUrls] = useState({});
  const [htmlBilag, setHtmlBilag] = useState({});   // filnavn -> indhold, tomt = fejlet
  const [bilagHoejde, setBilagHoejde] = useState({});
  const [body, setBody] = useState(item.body ?? null);

  const nonHtmlAttachments = (item.attachments || []).filter(a => a.filename !== 'main_document.html');
  // Brødteksten sendes ikke med i listen (den fylder 76% af svaret) — listen
  // bærer kun has_body, og teksten hentes når beskeden foldes ud.
  const hasBody = item.has_body !== undefined ? item.has_body : !!item.body;

  // Build tabs: "Besked" tab if HTML body, then one per non-HTML attachment
  const tabs = [];
  if (hasBody) tabs.push({ type: 'body', label: 'Besked' });
  nonHtmlAttachments.forEach(a => tabs.push({ type: 'file', label: a.filename, attachment: a }));

  const tokenEndpoint = adminMode
    ? (id) => `${API}/admin/inbox/${id}/file-token`
    : (id) => `${API}/inbox/${id}/file-token`;

  // Fetch download tokens for all file tabs (hooks must be called unconditionally)
  useEffect(() => {
    if (tabs.length === 0) return;
    nonHtmlAttachments.forEach(a => {
      if (fileUrls[a.filename]) return;
      axios.get(tokenEndpoint(item.id), { params: { filename: a.filename } })
        .then(r => {
          setFileUrls(prev => ({ ...prev, [a.filename]: `${API}/download/${r.data.token}` }));
        })
        .catch(() => {});
    });
  }, [item.id]);

  // HTML-bilag vises inline i stedet for at blive downloadet. Filen hentes som
  // bytes og renses her i browseren; serveren bliver ved med at udlevere den
  // som "attachment", så fremmed HTML aldrig kan køre på vores eget domæne.
  useEffect(() => {
    nonHtmlAttachments.filter(a => erHtmlBilag(a.filename)).forEach(a => {
      const url = fileUrls[a.filename];
      if (!url || htmlBilag[a.filename] !== undefined) return;
      axios.get(url, { responseType: 'arraybuffer' })
        .then(r => setHtmlBilag(prev => ({ ...prev, [a.filename]: afkodHtmlBilag(r.data) })))
        .catch(() => setHtmlBilag(prev => ({ ...prev, [a.filename]: '' })));
    });
  }, [fileUrls, item.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Brødteksten hentes først her, hvor beskeden faktisk er foldet ud.
  // Admin-visningen får den med i listen i forvejen, så der hentes intet.
  useEffect(() => {
    if (adminMode || !hasBody || body !== null) return;
    let cancelled = false;
    axios.get(`${API}/inbox/${item.id}/body`)
      .then(r => { if (!cancelled) setBody(r.data?.body || ''); })
      .catch(() => { if (!cancelled) setBody(''); });
    return () => { cancelled = true; };
  }, [item.id, hasBody, body, adminMode]);

  const currentTab = tabs[activeTab] || tabs[0];

  // Udlaesningen staar oeverst: det er den, man skal tage stilling til.
  // Selve dokumentet nedenfor er dokumentationen for det, der staar her.
  // Den vises ogsaa naar der ingen faneblade er — en sag kan vaere udlaest,
  // uden at der er en broedtekst at vise.
  const udlaesning = <Udlaesning item={item} />;
  if (tabs.length === 0) {
    return (item.fa_status || item.fa_har_data)
      ? <div className="px-3 sm:px-6 pb-4 sm:pb-5 pt-1 min-w-0 overflow-hidden">{udlaesning}</div>
      : null;
  }

  return (
    <div className="px-3 sm:px-6 pb-4 sm:pb-5 pt-1 min-w-0 overflow-hidden">
      {udlaesning}
      {/* DigitalBox: bilagene som kort i stedet for faner. Med mange PDF'er
          blev fanerne til en lang raekke navne, og under beskeden stod én
          navnloes "Åbn PDF" pr. fil klistret sammen. Her har hvert bilag sit
          navn, sin type og sin egen knap til at aabne det i et nyt vindue. */}
      {TRE_KOLONNER && tabs.length > 1 && (
        <div className="grid gap-2 sm:grid-cols-2 mb-4">
          {tabs.map((tab, i) => {
            const aktiv = activeTab === i;
            const navn = tab.type === 'body' ? 'Besked' : tab.label;
            const ext = tab.type === 'file' ? (tab.label.split('.').pop() || '').toUpperCase() : '';
            const type = tab.type === 'body' ? 'Selve beskeden'
              : erHtmlBilag(tab.label) ? 'HTML-dokument' : ext === 'PDF' ? 'PDF' : ext ? `${ext}-fil` : 'Fil';
            const url = tab.type === 'file' ? fileUrls[tab.label] : null;
            const Ikon = tab.type === 'body' ? Mail : FileText;
            return (
              <div key={i} role="button" tabIndex={0} aria-pressed={aktiv}
                onClick={() => setActiveTab(i)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActiveTab(i); } }}
                className={`flex items-center gap-3 min-w-0 rounded-[10px] border px-3 py-2.5 cursor-pointer transition-colors ${
                  aktiv ? 'border-db-route bg-db-route-soft text-db-ink' : 'border-th-border-light bg-th-card text-th-text hover:bg-th-hover'
                }`}>
                <span className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${aktiv ? 'bg-white' : 'bg-th-inset'}`}>
                  <Ikon size={17} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-sm font-medium leading-5 truncate" title={navn}>{navn}</span>
                  <span className={`block text-xs leading-4 ${aktiv ? 'text-db-ink/70' : 'text-th-text-muted'}`}>{type}</span>
                </span>
                {url && (
                  <a href={url} target="_blank" rel="noreferrer" title={`Åbn ${navn} i et nyt vindue`}
                    onClick={e => e.stopPropagation()}
                    className={`p-1.5 rounded-md shrink-0 transition-colors ${aktiv ? 'hover:bg-white' : 'text-th-text-muted hover:bg-th-hover hover:text-th-text'}`}>
                    <ExternalLink size={15} />
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tab bar */}
      {!TRE_KOLONNER && tabs.length > 1 && (
        <div className="flex gap-1 mb-3 border-b border-th-border-light overflow-x-auto scrollbar-hide">
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActiveTab(i)}
              className={`px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors rounded-t-lg -mb-px border-b-2 whitespace-nowrap shrink-0 ${
                activeTab === i
                  ? 'text-brand-400 border-brand-500 bg-th-hover/50'
                  : 'text-th-text-secondary border-transparent hover:text-th-text-secondary hover:border-th-border-hover'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      {currentTab.type === 'body' && (
        <div className="bg-white text-slate-900 rounded-lg p-2 sm:p-4 text-sm max-h-[60vh] overflow-auto">
          <style>{`.mail-body * { max-width: 100% !important; width: auto !important; box-sizing: border-box !important; } .mail-body table { table-layout: fixed !important; } .mail-body img { height: auto !important; }`}</style>
          {body === null && <p className="text-th-text-muted text-sm">Indlæser besked…</p>}
          <div className="mail-body break-words" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body || '', MAIL_HTML_RENS) }} />
        </div>
      )}

      {currentTab.type === 'file' && (
        <div className="min-w-0 overflow-hidden">
          {fileUrls[currentTab.attachment.filename] ? (
            <>
              {currentTab.attachment.filename.toLowerCase().endsWith('.pdf') && (
                <PdfViewer url={fileUrls[currentTab.attachment.filename]} filename={currentTab.attachment.filename} />
              )}
              {/* HTML-bilag: vist med afsenderens eget stilark i en sandkasse */}
              {erHtmlBilag(currentTab.attachment.filename) && (
                <div>
                  <div className="bg-white rounded-lg max-h-[70vh] overflow-auto">
                    {htmlBilag[currentTab.attachment.filename] === undefined
                      ? <p className="text-slate-500 text-sm p-4">Indlæser…</p>
                      : htmlBilag[currentTab.attachment.filename] === ''
                        ? <p className="text-slate-500 text-sm p-4">Filen kunne ikke vises — hent den herunder.</p>
                        : (
                          <iframe
                            key={currentTab.attachment.filename}
                            title={currentTab.attachment.filename}
                            srcDoc={DOMPurify.sanitize(htmlBilag[currentTab.attachment.filename], MAIL_DOKUMENT_RENS)}
                            /* Ingen allow-scripts: intet i dokumentet kan køre. allow-same-origin
                               er kun med, for at vi kan måle højden — uden scripts giver den
                               ingen rettigheder væk. allow-popups holder links klikbare. */
                            sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                            onLoad={e => {
                              try {
                                const d = e.target.contentDocument;
                                const h = Math.max(d?.documentElement?.scrollHeight || 0, d?.body?.scrollHeight || 0);
                                if (h) setBilagHoejde(prev => (prev[currentTab.attachment.filename] === h ? prev : { ...prev, [currentTab.attachment.filename]: h }));
                              } catch { /* kan vi ikke måle, beholder vi standardhøjden */ }
                            }}
                            style={{ width: '100%', height: (bilagHoejde[currentTab.attachment.filename] || 600) + 'px', border: 0, display: 'block', background: '#fff' }}
                          />
                        )}
                  </div>
                  <div className="mt-2 text-center">
                    <a href={fileUrls[currentTab.attachment.filename]} target="_blank" rel="noreferrer"
                       className="text-xs text-th-text-muted hover:text-brand-400 hover:underline">
                      Hent {currentTab.attachment.filename}
                    </a>
                  </div>
                </div>
              )}
              {/* Non-PDF files */}
              {!currentTab.attachment.filename.toLowerCase().endsWith('.pdf') && !erHtmlBilag(currentTab.attachment.filename) && (
                <div className="bg-th-hover border border-th-border-light rounded-lg p-4 text-sm text-th-text-secondary text-center">
                  <a
                    href={fileUrls[currentTab.attachment.filename]}
                    target="_blank"
                    rel="noreferrer"
                    className="text-brand-400 hover:underline"
                  >
                    Download {currentTab.attachment.filename}
                  </a>
                </div>
              )}
            </>
          ) : (
            <div className="text-th-text-muted text-sm py-4 text-center">Indlæser...</div>
          )}
        </div>
      )}

      {/* PDF button shown only when viewing body tab and there are PDF attachments.
          DigitalBox har knappen paa hvert bilagskort i stedet. */}
      {!TRE_KOLONNER && currentTab.type === 'body' && nonHtmlAttachments.filter(a => a.filename.toLowerCase().endsWith('.pdf')).map(a => (
        fileUrls[a.filename] && (
          <a
            key={a.filename}
            href={fileUrls[a.filename]}
            target="_blank"
            rel="noreferrer"
            className="mt-2 inline-flex items-center gap-1.5 bg-btn hover:bg-btn-hover text-btn-fg px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
          >
            Åbn PDF
          </a>
        )
      ))}

      {item.message_id && (
        <p className="text-xs text-th-text-faint font-mono mt-3">ID: {item.message_id}</p>
      )}
    </div>
  );
}

// ─── Upload Bilag Dialog ──────────────────────────────────────────────────────

// Dagen i dag, som den ser ud paa brugerens ur — ikke i UTC. toISOString()
// giver UTC-datoen, og fra midnat til kl. 02 dansk sommertid (kl. 01 om
// vinteren) er den stadig i gaar. Bilagsdatoen er noget, bogholderiet skal
// kunne genkende, saa den maa ikke afhaenge af, hvornaar paa doegnet filen
// bliver lagt op.
function iDagLokal() {
  const d = new Date();
  const to = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${to(d.getMonth() + 1)}-${to(d.getDate())}`;
}

function UploadBilagDialog({ companies, onClose, onUploaded }) {
  const today = iDagLokal();
  const [cvr, setCvr] = useState(companies[0]?.cvr || '');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [dato, setDato] = useState(today);
  const [belob, setBelob] = useState('');
  const [fil, setFil] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!fil) { toast.error('Vælg en fil'); return; }
    if (!beskrivelse.trim()) { toast.error('Udfyld beskrivelse'); return; }
    if (!cvr) { toast.error('Vælg selskab'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('cvr', cvr);
      fd.append('beskrivelse', beskrivelse.trim());
      fd.append('dato', dato);
      fd.append('belob', belob);
      fd.append('fil', fil);
      await axios.post(`${API}/inbox/upload-bilag`, fd);
      toast.success('Bilag uploadet');
      onUploaded();
      onClose();
    } catch {
      toast.error('Upload fejlede');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-th-card border border-th-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Paperclip size={18} className="text-brand-500" />
            <h2 className="text-lg font-semibold text-th-text">Upload bilag</h2>
          </div>
          <button onClick={onClose} className="text-th-text-muted hover:text-th-text transition-colors"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">Selskab</label>
            <select
              value={cvr}
              onChange={e => setCvr(e.target.value)}
              className="w-full px-3 py-2 rounded-lg text-sm bg-th-input border border-th-border text-th-text focus:outline-none focus:border-brand-500"
              required
            >
              {companies.map(c => <option key={c.id} value={c.cvr}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">Beskrivelse <span className="text-red-400">*</span></label>
            <Input
              value={beskrivelse}
              onChange={e => setBeskrivelse(e.target.value)}
              placeholder="F.eks. Faktura fra leverandør"
              className="bg-th-input border-th-border text-th-text"
              required
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-th-text-secondary mb-1">Dato</label>
              <Input
                type="date"
                value={dato}
                onChange={e => setDato(e.target.value)}
                className="bg-th-input border-th-border text-th-text"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-th-text-secondary mb-1">Beløb (kr.)</label>
              <Input
                value={belob}
                onChange={e => setBelob(e.target.value)}
                placeholder="0,00"
                className="bg-th-input border-th-border text-th-text"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-th-text-secondary mb-1">Fil <span className="text-red-400">*</span></label>
            <div
              onClick={() => fileRef.current?.click()}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 border-dashed cursor-pointer transition-colors ${fil ? 'border-brand-500 bg-brand-500/5' : 'border-th-border hover:border-brand-400 bg-th-input'}`}
            >
              <Upload size={18} className={fil ? 'text-brand-500' : 'text-th-text-muted'} />
              <span className={`text-sm truncate ${fil ? 'text-th-text' : 'text-th-text-muted'}`}>
                {fil ? fil.name : 'Klik for at vælge fil (PDF, billede m.m.)'}
              </span>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.csv,.xlsx,.xls,.doc,.docx,.txt,.xml"
                onChange={e => setFil(e.target.files[0] || null)}
              />
            </div>
            {fil && <p className="text-xs text-th-text-muted mt-1">{(fil.size / 1024).toFixed(0)} KB</p>}
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
              Annuller
            </button>
            <button type="submit" disabled={saving} className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors disabled:opacity-50">
              {saving ? 'Uploader...' : 'Upload bilag'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

// ─── Inbox View ───────────────────────────────────────────────────────────────

function InboxView({ onUlaesteTal }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false); // serverside søgning undervejs
  const [forwardPopup, setForwardPopup] = useState(null); // item id (ephemeral dropdown)
  const [forwardPopupPos, setForwardPopupPos] = useState({ top: 0, left: 0 });
  const [quickFwdConfirm, setQuickFwdConfirm] = useState(null); // {item, contact} after successful quick-forward
  const [assignPopup, setAssignPopup] = useState(null); // item id
  const [assignNote, setAssignNote] = useState(''); // note skrevet i tildelings-popup
  const [assignTarget, setAssignTarget] = useState(null); // valgt modtager — afventer bekraeftelse
  const [notePopup, setNotePopup] = useState(null); // item id — åben note-tråd/tilføj-note
  const [noteDraft, setNoteDraft] = useState(''); // tekst i "tilføj note"-feltet
  const [savingNote, setSavingNote] = useState(false);
  const [inboxRecipients, setInboxRecipients] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const { companyId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const expanded = searchParams.get('msg');
  const filter = searchParams.get('filter') || 'all';
  const search = searchParams.get('q') || '';

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };
  const setExpanded = (id) => updateParams({ msg: id });
  const setFilter = (f) => updateParams({ filter: f === 'all' ? null : f });
  const setSearch = (q) => updateParams({ q });

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/companies`),
      axios.get(`${API}/contacts`),
      axios.get(`${API}/recipients/assignable`),
    ]).then(([companiesRes, contactsRes, recipientsRes]) => {
      setCompanies(companiesRes.data);
      setContacts(contactsRes.data);
      setInboxRecipients(recipientsRes.data || []);
    }).catch(() => {});
  }, []);

  // Selve indbakken hentes her — også ved første indlæsning, så der kun er
  // ét sted der sætter listen. Søgningen ligger nu på serveren, fordi
  // brødteksten ikke længere sendes med i listen; en søgning i browseren
  // ville ellers holde op med at finde tekst inde i beskederne.
  useEffect(() => {
    const term = search.trim();
    const timer = setTimeout(() => {
      if (term) setSearching(true);
      axios.get(`${API}/inbox`, { params: term ? { q: term } : {} })
        .then(r => setItems(r.data))
        .catch(() => toast.error('Kunne ikke hente indbakke'))
        .finally(() => { setSearching(false); setLoading(false); });
    }, term ? 300 : 0);  // vent på ro ved tastaturet, men ikke ved indlæsning
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Ulaeste pr. CVR til DigitalBox' sidebar. Regnes her, hvor listen er, saa
  // tallet i sidebaren altid er det samme som i listen.
  useEffect(() => {
    if (!onUlaesteTal) return;
    const tal = {};
    items.forEach(x => { if (!x.read && !x.resolved && x.cvr) tal[x.cvr] = (tal[x.cvr] || 0) + 1; });
    onUlaesteTal(tal);
  }, [items, onUlaesteTal]);

  const stripUnique = (s) => {
    // Fjern datoer, årstal, måneder og løbenumre så reglen matcher generisk
    const months = 'januar|februar|marts|april|maj|juni|juli|august|september|oktober|november|december';
    return s
      .replace(/\d{1,2}[\.\-\/]\d{1,2}[\.\-\/]\d{2,4}/g, '')   // dd.mm.yyyy / dd-mm-yy
      .replace(/\d{4}[\-\/]\d{1,2}[\-\/]\d{1,2}/g, '')          // yyyy-mm-dd
      .replace(new RegExp(`(${months})\\s*\\d{0,4}`, 'gi'), '')  // "maj 2026", "januar"
      .replace(/\b(20\d{2}|19\d{2})\b/g, '')                    // årstal 19xx/20xx
      .replace(/\b\d{5,}\b/g, '')                               // løbenumre (5+ cifre)
      .replace(/\b\d{1,2}\.\s*(kvartal|kv\.?)\b/gi, '')         // "1. kvartal"
      .replace(/\s{2,}/g, ' ')                                   // dobbelt mellemrum
      .replace(/[\s\-·]+$/g, '')                                 // trailing whitespace/bindestreg
      .trim();
  };

  const quickForwardItem = async (item, contact) => {
    try {
      const r = await axios.post(`${API}/inbox/${item.id}/quick-forward`, {
        email: contact.email,
        contact_name: contact.name,
      });
      // Vent med at opdatere indbakken (så mailen ikke forsvinder med det
      // samme) — reloadInbox() kaldes først når "opret regel"-dialogen lukkes.
      // fromEmail er kontaktens egen afsenderadresse — tom når kontakten
      // ikke har valgt en. Den følger med over i en regel, så reglen sender
      // fra samme afsender som den enkelte videresendelse gjorde.
      setQuickFwdConfirm({ item, contact, fromEmail: r.data?.from_email || '' });
    } catch {
      toast.error('Kunne ikke videresende');
    }
  };

  // Luk "opret regel"-dialogen OG opdater indbakken (mailen forsvinder nu).
  const closeQuickFwdConfirm = () => {
    setQuickFwdConfirm(null);
    reloadInbox();
  };

  const createRuleFromConfirm = () => {
    if (!quickFwdConfirm) return;
    const { item, contact, fromEmail } = quickFwdConfirm;
    const cleaned = stripUnique(item.subject || '');
    const escaped = cleaned.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const params = new URLSearchParams({
      name: `${cleaned || 'Regel'} → ${contact.name}`,
      forward_to: contact.email,
      subject_match: escaped,
      cvr_filter: item.cvr || '',
      sender_filter: item.from_address || '',
      // Tomt felt betyder "brug selskabets egen adresse" — derfor sendes det
      // kun med, når kontakten faktisk har valgt en afsender.
      ...(fromEmail ? { from_email: fromEmail } : {}),
    });
    setQuickFwdConfirm(null);
    navigate(`/videresend?${params.toString()}`);
  };

  // Genindlæs med den aktive søgning, ellers ville listen springe tilbage
  // til alt indhold midt i en søgning
  const reloadInbox = async () => {
    const term = search.trim();
    const res = await axios.get(`${API}/inbox`, { params: term ? { q: term } : {} });
    setItems(res.data);
  };

  const approveForward = async (e, item) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/inbox/${item.id}/approve-forward`);
      toast.success('Videresendelse godkendt');
      await reloadInbox();
    } catch { toast.error('Kunne ikke godkende'); }
  };

  const rejectForward = async (e, item) => {
    e.stopPropagation();
    try {
      await axios.post(`${API}/inbox/${item.id}/reject-forward`);
      toast.success('Videresendelse afvist');
      await reloadInbox();
    } catch { toast.error('Kunne ikke afvise'); }
  };

  const companyByCvr = companies.reduce((acc, c) => { acc[c.cvr] = c; return acc; }, {});
  const companyById = companies.reduce((acc, c) => { acc[c.id] = c; return acc; }, {});
  const selectedCompany = companyId ? companyById[companyId] : null;
  const filtered = selectedCompany ? items.filter(x => x.cvr === selectedCompany.cvr) : items;

  const selectCompany = (c) => {
    if (c) navigate(`/indbakke/${c.id}`);
    else navigate('/indbakke');
  };

  const markRead = async (e, item) => {
    e.stopPropagation();
    await axios.patch(`${API}/inbox/${item.id}/read`);
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, read: true } : x));
  };

  const markUnread = async (e, item) => {
    e.stopPropagation();
    await axios.patch(`${API}/inbox/${item.id}/unread`);
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, read: false } : x));
  };

  const toggleResolved = async (e, item) => {
    e.stopPropagation();
    const res = await axios.patch(`${API}/inbox/${item.id}/resolved`);
    setItems(prev => prev.map(x => x.id === item.id ? { ...x, resolved: res.data.resolved, resolved_by: res.data.resolved_by, resolved_at: res.data.resolved_at } : x));
  };

  const deleteItem = async (e, item) => {
    e.stopPropagation();
    if (!window.confirm('Slet denne besked?')) return;
    await axios.delete(`${API}/inbox/${item.id}`);
    setItems(prev => prev.filter(x => x.id !== item.id));
    toast.success('Besked slettet');
  };

  const assignItem = async (item, email) => {
    // Optimistisk: opdater UI med det samme — backend kan være langsom (sender
    // notifikations-email). Navn slås op lokalt fra modtagerlisten.
    const localName = email
      ? (inboxRecipients.find(r => r.email === email)?.name || email)
      : null;
    const note = email ? assignNote.trim() : '';
    const prevItems = items;
    setItems(prev => prev.map(x => x.id === item.id
      ? { ...x, assigned_to_email: email || null, assigned_to_name: localName }
      : x));
    setAssignPopup(null);
    setAssignNote('');
    setAssignTarget(null);
    toast.success(email ? `Tildelt ${localName}` : 'Tildeling fjernet');
    try {
      const res = await axios.patch(`${API}/inbox/${item.id}/assign`, { assigned_to_email: email || null, note: note || null });
      // Synkronisér med backendens autoritative navn (kan afvige fra lokalt) + note-tråd
      setItems(prev => prev.map(x => x.id === item.id
        ? { ...x, assigned_to_email: res.data.assigned_to_email, assigned_to_name: res.data.assigned_to_name, notes: res.data.notes ?? x.notes }
        : x));
    } catch (err) {
      // Rul tilbage ved fejl
      setItems(prevItems);
      toast.error('Kunne ikke tildele — prøv igen');
    }
  };

  const addNote = async (item, text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return;
    setSavingNote(true);
    try {
      const res = await axios.post(`${API}/inbox/${item.id}/notes`, { text: trimmed });
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, notes: res.data.notes } : x));
      setNoteDraft('');
      toast.success('Note tilføjet');
    } catch (err) {
      toast.error('Kunne ikke gemme note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (item, noteId) => {
    try {
      const res = await axios.delete(`${API}/inbox/${item.id}/notes/${noteId}`);
      setItems(prev => prev.map(x => x.id === item.id ? { ...x, notes: res.data.notes } : x));
    } catch (err) {
      toast.error(err.response?.status === 403 ? 'Du kan kun slette dine egne noter' : 'Kunne ikke slette note');
    }
  };

  // Videresend- og tildel-popuppen bruges baade af den foldbare liste og af
  // detaljevisningens vaerktoejslinje (DigitalBox). Én kopi, saa de to ikke
  // kan glide fra hinanden.
  const aabnVideresend = (e, item) => {
    e.stopPropagation();
    if (forwardPopup === item.id) { setForwardPopup(null); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    const POPUP_W = 260;
    const POPUP_MAX_H = Math.min(window.innerHeight - 32, 480);
    // X: forsøg højre-aligned, ellers klem ind i viewport
    let left = Math.min(rect.right, window.innerWidth - 8);
    left = Math.max(POPUP_W + 8, left);
    // Y: under knappen hvis der er plads, ellers over
    let top = rect.bottom + 4;
    if (top + POPUP_MAX_H > window.innerHeight - 8) {
      top = Math.max(8, rect.top - POPUP_MAX_H - 4);
    }
    setForwardPopupPos({ top, left, maxHeight: POPUP_MAX_H });
    setForwardPopup(item.id);
  };
  const aabnTildel = (e, item) => {
    e.stopPropagation();
    if (assignPopup === item.id) { setAssignPopup(null); setAssignTarget(null); setAssignNote(''); return; }
    const rect = e.currentTarget.getBoundingClientRect();
    // Samme placering som videresend-popup'en: under knappen når
    // der er plads, ellers over den. holdPaaSkaermen retter
    // resten efter den faktiske højde.
    const POPUP_W = 260;
    const POPUP_MAX_H = Math.min(window.innerHeight - 32, 520);
    let left = Math.max(POPUP_W + 8, Math.min(rect.right, window.innerWidth - 8));
    let top = rect.bottom + 4;
    if (top + POPUP_MAX_H > window.innerHeight - 8) {
      top = Math.max(8, rect.top - POPUP_MAX_H - 4);
    }
    setForwardPopupPos({ top, left });
    setAssignPopup(item.id);
  };
  const videresendPopup = (item) => (forwardPopup === item.id && createPortal(
                        <>
                          <div className="fixed z-[9999] bg-th-sidebar border border-th-border rounded-lg shadow-xl py-2 w-[260px] overflow-y-auto"
                            style={{ top: forwardPopupPos.top, left: forwardPopupPos.left, maxHeight: forwardPopupPos.maxHeight, transform: 'translateX(-100%)' }}
                            onClick={e => e.stopPropagation()}>
                            <p className="px-3 py-1 text-xs text-th-text-muted font-semibold uppercase tracking-wider sticky top-0 bg-th-sidebar">Videresend til</p>
                            {contacts.length === 0 ? (
                              <p className="px-3 py-2 text-xs text-th-text-muted">Ingen kontakter — <a href="/adressebog" className="text-brand-400 hover:underline">tilføj i adressebog</a></p>
                            ) : contacts.map(c => (
                              <button key={c.id} onClick={() => { setForwardPopup(null); quickForwardItem(item, c); }}
                                className="w-full text-left px-3 py-2 text-sm text-th-text-secondary hover:bg-th-hover hover:text-th-text transition-colors flex items-center gap-2">
                                <User size={14} className="text-th-text-muted shrink-0" />
                                <div className="min-w-0">
                                  <span className="block truncate">{c.name}</span>
                                  <span className="block text-xs text-th-text-muted truncate">{c.email}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                          <div className="fixed inset-0 z-[9998]" onClick={e => { e.stopPropagation(); setForwardPopup(null); }} />
                        </>,
                        document.body
                      ));
  const tildelPopup = (item) => (assignPopup === item.id && createPortal(
                          <>
                            <div ref={holdPaaSkaermen}
                              className="fixed z-[9999] bg-th-sidebar border border-th-border rounded-lg shadow-xl py-2 min-w-[200px] flex flex-col"
                              style={{ top: forwardPopupPos.top, left: forwardPopupPos.left, transform: 'translateX(-100%)', maxHeight: 'calc(100vh - 16px)' }}
                              onClick={e => e.stopPropagation()}>
                              <p className="px-3 py-1 text-xs text-th-text-muted font-semibold uppercase tracking-wider shrink-0">Overdrag til</p>
                              {/* Kun listen ruller. Noten og knapperne bliver stående,
                                  så "Overdrag" kan nås, uanset hvor mange modtagere der er. */}
                              <div className="overflow-y-auto min-h-0">
                              {item.assigned_to_email && (
                                <button onClick={e => { e.stopPropagation(); assignItem(item, null); }} className="w-full text-left px-3 py-2 text-sm text-red-400 hover:bg-th-hover transition-colors">
                                  Fjern tildeling
                                </button>
                              )}
                              {inboxRecipients.map(r => (
                                <button key={r.id} onClick={e => { e.stopPropagation(); setAssignTarget(assignTarget === r.email ? null : r.email); }}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-th-hover hover:text-th-text transition-colors flex items-center gap-2 ${(assignTarget || item.assigned_to_email) === r.email ? 'text-brand-400' : 'text-th-text-secondary'}`}>
                                  <User size={14} className="text-th-text-muted shrink-0" />
                                  <div className="min-w-0">
                                    <span className="block truncate">{r.name}</span>
                                    <span className="block text-xs text-th-text-muted truncate">{r.email}</span>
                                  </div>
                                  {(assignTarget || item.assigned_to_email) === r.email && <span className="ml-auto text-brand-400 text-xs">✓</span>}
                                </button>
                              ))}
                              </div>

                              {/* Valgfri note der foelger med i mailen til modtageren */}
                              {assignTarget && (
                                <div className="px-3 pt-2 pb-1 border-t border-th-border mt-1 shrink-0">
                                  <textarea
                                    autoFocus value={assignNote} onChange={e => setAssignNote(e.target.value)}
                                    onClick={e => e.stopPropagation()}
                                    maxLength={2000} rows={3}
                                    placeholder="Note til modtageren (valgfri)…"
                                    className="w-full px-2.5 py-2 rounded-lg bg-th-input border border-th-border-light text-sm text-th-text placeholder:text-th-text-muted resize-none focus:outline-none focus:border-brand-500"
                                  />
                                  <p className="text-[11px] text-th-text-muted mt-1">
                                    Noten sendes med i mailen og gemmes på sagen.
                                  </p>
                                  <div className="flex gap-2 mt-2 mb-1">
                                    <button onClick={e => { e.stopPropagation(); setAssignTarget(null); setAssignNote(''); }}
                                      className="flex-1 px-2 py-1.5 rounded-lg text-xs font-medium border border-th-border text-th-text-secondary hover:bg-th-hover transition-colors">
                                      Fortryd
                                    </button>
                                    <button onClick={e => { e.stopPropagation(); assignItem(item, assignTarget); }}
                                      className="flex-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors">
                                      Overdrag
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div className="fixed inset-0 z-[9998]" onClick={e => { e.stopPropagation(); setAssignPopup(null); setAssignTarget(null); setAssignNote(''); }} />
                          </>,
                          document.body
                        ));

  // Kvitteringen efter en hurtig videresendelse — faelles for begge visninger
  const bekraeftVideresendelse = (quickFwdConfirm && createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/40" onClick={closeQuickFwdConfirm} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-th-sidebar border border-th-border rounded-xl shadow-2xl p-6 w-[340px] text-center">
        <CheckCircle2 size={32} className="mx-auto text-green-400 mb-3" />
        <p className="text-th-text font-semibold mb-1">Bilag videresendt</p>
        <p className="text-sm text-th-text-secondary mb-5">
          Sendt til {quickFwdConfirm.contact.name} og markeret som løst
        </p>
        <div className="flex gap-3">
          <button onClick={createRuleFromConfirm}
            className="flex-1 bg-btn hover:bg-btn-hover-strong text-btn-fg text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
            Opret regel?
          </button>
          <button onClick={closeQuickFwdConfirm}
            className="flex-1 bg-th-hover hover:bg-th-border text-th-text-secondary text-sm font-medium rounded-lg px-4 py-2.5 transition-colors">
            Nej tak
          </button>
        </div>
      </div>
    </>,
    document.body
  ));

  // Serveren har allerede filtreret på søgeordet — den søger i emne, afsender,
  // brødtekst og CVR. Her filtreres kun på det valgte selskab.
  const searched = filtered;
  const unreadCount = searched.filter(x => !x.read && !x.resolved).length;
  const resolvedCount = searched.filter(x => x.resolved).length;
  const pendingCount = searched.filter(x => x.pending_forward && !x.resolved).length;
  const openCount = searched.filter(x => !x.resolved).length;
  const myEmail = user?.email || '';
  const mineCount = searched.filter(x => !x.resolved && x.assigned_to_email === myEmail).length;
  const displayed = filter === 'resolved' ? searched.filter(x => x.resolved)
    : filter === 'unread' ? searched.filter(x => !x.read && !x.resolved)
    : filter === 'pending' ? searched.filter(x => x.pending_forward && !x.resolved)
    : filter === 'mine' ? searched.filter(x => !x.resolved && x.assigned_to_email === myEmail)
    : searched.filter(x => !x.resolved);

  // DigitalBox: pil op/ned gaar gennem posten i den liste, man ser — samme
  // raekkefoelge og filter. Staar fokus i et felt (soegning, noter) eller er en
  // dialog aaben, tilhoerer tasterne dem. Hooken staar foer if'en nedenfor, saa
  // raekkefoelgen af hooks er den samme for begge brands.
  useEffect(() => {
    if (!TRE_KOLONNER) return undefined;
    const tast = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      const fokus = document.activeElement;
      if (fokus && (fokus.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(fokus.tagName))) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      if (!displayed.length) return;
      const nu = displayed.findIndex(x => x.id === expanded);
      const ny = nu === -1 ? 0 : Math.max(0, Math.min(displayed.length - 1, nu + (e.key === 'ArrowDown' ? 1 : -1)));
      e.preventDefault();
      if (ny === nu) return;
      const item = displayed[ny];
      setExpanded(item.id);
      if (!item.read) markRead({ stopPropagation: () => {} }, item);
      document.querySelector(`[data-sag="${item.id}"]`)?.scrollIntoView({ block: 'nearest' });
    };
    window.addEventListener('keydown', tast);
    return () => window.removeEventListener('keydown', tast);
  }, [displayed, expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── DigitalBox: tre kolonner ───────────────────────────────────────────────
  // Samme data, filtre og handlinger som den foldbare liste herunder; kun
  // opstillingen er en anden. Den valgte sag er ?msg= som foer, saa et link
  // til en sag virker i begge visninger.
  if (TRE_KOLONNER) {
    const valgt = expanded ? items.find(x => x.id === expanded) : null;
    const vaelg = (item) => {
      setExpanded(item.id);
      if (!item.read) markRead({ stopPropagation: () => {} }, item);
    };
    const valgtSelskab = valgt?.cvr ? companyByCvr[valgt.cvr] : null;
    const knap = 'inline-flex items-center gap-[7px] pl-[11px] pr-3 py-[7px] rounded-lg border border-th-border text-sm font-medium leading-5 text-th-text hover:bg-th-hover transition-colors';
    const ikonKnap = 'p-2 rounded-lg text-th-text-muted hover:bg-th-hover hover:text-th-text transition-colors';
    const filtre = [
      { key: 'all', label: 'Åbne', count: openCount },
      { key: 'unread', label: 'Ulæste', count: unreadCount },
      { key: 'pending', label: 'Afventer', count: pendingCount },
      { key: 'mine', label: 'Mine', count: mineCount },
      { key: 'resolved', label: 'Færdige', count: resolvedCount },
    ];

    return (
      <div className="flex h-full min-h-0 bg-th-card">
        {showUpload && companies.length > 0 && (
          <UploadBilagDialog companies={companies} onClose={() => setShowUpload(false)} onUploaded={reloadInbox} />
        )}

        {/* Beskedliste — paa mobil hele bredden, indtil en sag er valgt */}
        <section className={`w-full lg:w-[392px] shrink-0 lg:border-r border-th-border-light flex-col min-h-0 ${valgt ? 'hidden lg:flex' : 'flex'}`}>
          <div className="px-[18px] pt-[18px] pb-[14px] flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="font-heading text-xl font-semibold leading-[26px] tracking-[-0.1px] text-th-text shrink-0">Indbakke</h1>
                {selectedCompany && (
                  // Kun navnet — CVR'et staar paa sagerne. Et langt navn afkortes med …
                  <DbTag title={selectedCompany.name} className="min-w-0 max-w-full">
                    <span className="truncate">{selectedCompany.name}</span>
                  </DbTag>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-sm text-th-text-muted">{unreadCount} ulæste</span>
                {companies.length > 0 && (
                  <button onClick={() => setShowUpload(true)} title="Upload bilag" className={ikonKnap}>
                    <Upload size={16} />
                  </button>
                )}
              </div>
            </div>
            <div className="relative">
              <Search size={15} className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${searching ? 'text-db-route animate-pulse' : 'text-th-text-muted'}`} />
              <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Søg i afsender, emne og bilag"
                className="w-full pl-8 pr-8 py-2 rounded-lg bg-th-inset border border-th-border-light text-sm leading-5 text-th-text placeholder:text-th-text-muted focus:outline-none focus:border-db-route" />
              {search && (
                <button onClick={() => setSearch('')} title="Ryd søgning" className="absolute right-2 top-1/2 -translate-y-1/2 text-th-text-muted hover:text-th-text">
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide -mx-1">
              {filtre.map(f => (
                <button key={f.key} onClick={() => setFilter(f.key)}
                  className={`px-2 py-1 rounded-md text-xs font-semibold tracking-[0.24px] whitespace-nowrap transition-colors ${
                    filter === f.key ? 'bg-db-ink text-white' : 'text-th-text-muted hover:bg-th-hover hover:text-th-text'
                  }`}>
                  {f.label}{f.count > 0 && <span className="ml-1 opacity-70">{f.count}</span>}
                </button>
              ))}
            </div>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            {loading ? (
              <p className="px-[18px] py-10 text-center text-sm text-th-text-muted">Indlæser…</p>
            ) : displayed.length === 0 ? (
              <div className="px-[18px] py-12 text-center border-t border-th-border-light">
                <Inbox size={32} className="mx-auto mb-3 text-th-text-faint" />
                <p className="text-sm text-th-text-muted">{filtered.length === 0 ? 'Ingen post endnu' : 'Ingen beskeder matcher filteret'}</p>
              </div>
            ) : displayed.map(item => {
              const aktiv = valgt?.id === item.id;
              const ulaest = !item.read && !item.resolved;
              const Ikon = KILDE_IKON[item.source] || Inbox;
              return (
                <button key={item.id} data-sag={item.id} onClick={() => vaelg(item)}
                  className={`w-full text-left flex items-center gap-3 px-[18px] py-[14px] border-t border-th-border-light transition-colors ${aktiv ? 'bg-th-active' : 'hover:bg-th-hover'}`}>
                  {/* Ikonet er lige saa hoejt som tid + maerke i hoejre side og
                      staar midt i raekken, saa de to flugter som i designet */}
                  <span title={kildeNavn(item)}
                    className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 transition-colors ${ulaest ? 'bg-db-label text-white' : 'bg-th-inset text-th-text'}`}>
                    <Ikon size={20} />
                  </span>
                  <span className="flex-1 min-w-0 flex flex-col">
                    <span className={`truncate text-sm leading-5 text-th-text ${ulaest ? 'font-semibold' : ''}`}>{afsenderNavn(item)}</span>
                    <span className={`truncate text-sm leading-5 text-th-text-muted ${item.resolved ? 'line-through' : ''}`}>{item.subject || '(Ingen emne)'}</span>
                    {!selectedCompany && companies.length > 1 && companyByCvr[item.cvr] && (
                      <span className="truncate text-xs leading-4 text-th-text-faint mt-0.5">{companyByCvr[item.cvr].name}</span>
                    )}
                  </span>
                  <span className="shrink-0 h-11 flex flex-col items-end justify-between">
                    <span className="text-xs font-semibold leading-4 tracking-[0.24px] text-th-text-muted pt-0.5">{kortTid(item.received_at)}</span>
                    <ListeStatus item={item} />
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {/* Detaljevisning */}
        <section className={`flex-1 min-w-0 flex-col min-h-0 ${valgt ? 'flex' : 'hidden lg:flex'}`}>
          {!valgt ? (
            <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
              <Mail size={36} className="text-th-text-faint mb-3" />
              <p className="text-sm text-th-text-muted">Vælg en besked i listen</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 border-b border-th-border-light">
                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                  <button onClick={() => setExpanded(null)} title="Tilbage til listen" className={`${ikonKnap} lg:hidden`}>
                    <ArrowLeft size={16} />
                  </button>
                  {!valgt.resolved && inboxRecipients.length > 0 && (
                    <button onClick={e => aabnTildel(e, valgt)} className={knap}>
                      <UserPlus size={15} /> <span className="hidden sm:inline">Tildel</span>
                    </button>
                  )}
                  <button onClick={e => aabnVideresend(e, valgt)} className={knap}>
                    <Forward size={15} /> <span className="hidden sm:inline">Videresend</span>
                  </button>
                  <button onClick={e => valgt.read ? markUnread(e, valgt) : markRead(e, valgt)}
                    title={valgt.read ? 'Markér som ulæst' : 'Markér som læst'} className={ikonKnap}>
                    <Mail size={16} />
                  </button>
                  <button onClick={async e => { await deleteItem(e, valgt); }} title="Slet" className={`${ikonKnap} hover:text-red-600`}>
                    <Trash2 size={16} />
                  </button>
                </div>
                <button onClick={e => toggleResolved(e, valgt)}
                  className={valgt.resolved
                    ? knap
                    : 'shrink-0 inline-flex items-center gap-[7px] pl-[11px] pr-3 py-[7px] rounded-lg bg-db-done text-white text-sm font-medium leading-5 hover:opacity-90 transition-opacity'}>
                  <Check size={15} />
                  {valgt.resolved ? 'Genåbn' : <><span className="sm:hidden">Færdig</span><span className="hidden sm:inline">Færdigbehandlet</span></>}
                </button>
                {videresendPopup(valgt)}
                {tildelPopup(valgt)}
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                <div className="px-4 sm:px-7 py-6 flex flex-col gap-5">
                  <div className="flex flex-wrap gap-1.5">
                    <KildeTag item={valgt} />
                    <StatusTags item={valgt} visLaest />
                    {valgtSelskab && (
                      // Modtagerselskabet, saa man i "Alle" kan se, hvis post det er
                      <DbTag title={`Til ${valgtSelskab.name}, CVR ${formatCvr(valgt.cvr)}`} className="min-w-0 max-w-full">
                        <Building2 size={12} className="shrink-0" />
                        <span className="truncate">{valgtSelskab.name}</span>
                      </DbTag>
                    )}
                    {valgt.file_format && <DbTag>{valgt.file_format.replace(/^application\//, '').toUpperCase()}</DbTag>}
                    <MatchetMaerke item={valgt} />
                    <LeveringsMaerke item={valgt} />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <h2 className="font-heading text-[26px] font-semibold leading-8 tracking-[-0.26px] text-th-text break-words">{valgt.subject || '(Ingen emne)'}</h2>
                    <p className="text-sm leading-5 text-th-text-muted">
                      Fra {afsenderNavn(valgt)}
                      {valgtSelskab && <>{'  ·  '}Til {valgtSelskab.name}, CVR {formatCvr(valgt.cvr)}</>}
                      {'  ·  '}{formatDate(valgt.received_at)}
                    </p>
                  </div>

                  {valgt.pending_forward && !valgt.resolved && (
                    <div className="flex flex-wrap items-center gap-2 p-3 rounded-[10px] bg-amber-50 border border-amber-200">
                      <span className="text-sm text-amber-900 flex-1 min-w-0">Afventer godkendelse: {valgt.pending_rule_name}</span>
                      <button onClick={e => rejectForward(e, valgt)} className={knap}>Afvis</button>
                      <button onClick={e => approveForward(e, valgt)} className="inline-flex items-center gap-[7px] px-3 py-[7px] rounded-lg bg-db-done text-white text-sm font-medium">Godkend</button>
                    </div>
                  )}

                  <ExpandedMessage key={valgt.id} item={valgt} />

                  {(valgt.assigned_to_name || valgt.forwarded_to || (valgt.resolved && valgt.resolved_by)) && (
                    <>
                      <div className="h-px bg-th-border-light" />
                      <div className="flex flex-wrap items-center gap-2.5">
                        {valgt.assigned_to_name && (
                          <>
                            <span className="w-7 h-7 rounded-full bg-db-route text-white text-xs font-semibold flex items-center justify-center">
                              {valgt.assigned_to_name.split(/\s+/).map(d => d[0]).slice(0, 2).join('').toUpperCase()}
                            </span>
                            <span className="text-sm font-medium text-th-text">Tildelt {valgt.assigned_to_name}</span>
                          </>
                        )}
                        {valgt.forwarded_to && (
                          <DbTag variant="route">
                            Videresendt til {Array.isArray(valgt.forwarded_to) ? valgt.forwarded_to.join(', ') : valgt.forwarded_to}
                          </DbTag>
                        )}
                        {valgt.resolved && valgt.resolved_by && (
                          <span className="text-sm text-th-text-muted">
                            Færdigbehandlet af {valgt.resolved_by.startsWith('Regel:') ? 'regel' : valgt.resolved_by}
                            {valgt.resolved_at ? ` · ${formatDate(valgt.resolved_at)}` : ''}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
                <NoteThread
                  item={valgt}
                  currentEmail={user?.email}
                  draft={notePopup === valgt.id ? noteDraft : ''}
                  setDraft={v => { setNotePopup(valgt.id); setNoteDraft(v); }}
                  saving={savingNote}
                  onAdd={addNote}
                  onDelete={deleteNote}
                />
              </div>
            </>
          )}
        </section>

        {bekraeftVideresendelse}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Indbakke</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">Modtaget og videresendt digital post{unreadCount > 0 && <span className="ml-2 bg-brand-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount} ulæst</span>}</p>
        </div>
        {companies.length > 0 && (
          <button
            onClick={() => setShowUpload(true)}
            className="shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-btn hover:bg-btn-hover-strong text-btn-fg transition-colors"
          >
            <Upload size={15} />
            <span className="hidden sm:inline">Upload bilag</span>
          </button>
        )}
      </div>
      {showUpload && companies.length > 0 && (
        <UploadBilagDialog
          companies={companies}
          onClose={() => setShowUpload(false)}
          onUploaded={reloadInbox}
        />
      )}
      {companies.length > 0 && (
        <div className="mb-4 flex items-center gap-2 overflow-x-auto scrollbar-hide">
          <button
            onClick={() => selectCompany(null)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors shrink-0 ${!selectedCompany ? 'bg-th-hover text-th-text' : 'text-th-text-secondary hover:bg-th-hover hover:text-th-text'}`}
          >
            Alle
          </button>
          {companies.map(c => {
            const companyUnread = items.filter(x => x.cvr === c.cvr && !x.read && !x.resolved).length;
            const hasItems = items.some(x => x.cvr === c.cvr);
            return (
            <button
              key={c.id}
              onClick={() => selectCompany(c)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${selectedCompany?.id === c.id ? 'bg-th-hover text-th-text' : 'text-th-text-secondary hover:bg-th-hover hover:text-th-text'}`}
            >
              {c.name}
              {companyUnread > 0 && (
                <span className="bg-brand-500 text-white text-xs px-1.5 py-0.5 rounded-full leading-none">{companyUnread}</span>
              )}
              {hasItems && companyUnread === 0 && (
                <span className="w-1.5 h-1.5 rounded-full bg-th-text-faint inline-block" />
              )}
            </button>
            );
          })}
        </div>
      )}

      {/* Søgefeltet skal blive stående når en søgning ikke gav træffere —
          ellers kan den ikke ryddes igen */}
      {!loading && (filtered.length > 0 || search) && (
        <div className="mb-3 sm:mb-4 space-y-2 sm:space-y-0 sm:flex sm:items-center sm:gap-3 sm:flex-wrap">
          <div className="relative sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
            <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 transition-colors ${searching ? 'text-brand-400 animate-pulse' : 'text-th-text-muted'}`} />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Søg..."
              className="w-full pl-9 pr-8 py-1.5 rounded-lg text-sm bg-th-input border border-th-border text-th-text placeholder:text-th-text-muted focus:outline-none focus:border-brand-500 transition-colors"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-th-text-muted hover:text-th-text">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide">
            {[
              { key: 'all', label: 'Alle', count: openCount },
              { key: 'mine', label: 'Mine', count: mineCount },
              { key: 'unread', label: 'Ulæste', count: unreadCount },
              { key: 'pending', label: 'Afventer', count: pendingCount },
              { key: 'resolved', label: 'Løste', count: resolvedCount },
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-2.5 py-1 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap shrink-0 ${
                  filter === f.key
                    ? 'bg-th-hover text-th-text'
                    : 'text-th-text-secondary hover:bg-th-hover hover:text-th-text'
                }`}
              >
                {f.label}
                {f.count > 0 && <span className="ml-1 text-[10px] sm:text-xs text-th-text-muted">({f.count})</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 bg-th-input border-th-border text-center">
          <Inbox size={48} className="text-th-text-faint mx-auto mb-4" />
          <p className="text-th-text-secondary text-lg font-medium">Ingen post endnu</p>
          <p className="text-th-text-muted text-sm mt-1">Digital post der videresendes vises her</p>
        </Card>
      ) : (
        <div className="bg-th-input border border-th-border rounded-xl overflow-hidden divide-y divide-th-border max-w-full">
          {displayed.length === 0 ? (
            <div className="px-6 py-12 text-center text-th-text-muted">Ingen beskeder matcher filteret</div>
          ) : displayed.map((item, i) => (
            <div key={item.id || i} className={`overflow-hidden ${!item.read ? 'bg-brand-500/5' : ''} ${item.resolved ? 'opacity-60' : ''}`}>
              <button
                onClick={() => { const closing = expanded === item.id; setExpanded(closing ? null : item.id); if (closing && !item.read) markRead({ stopPropagation: () => {} }, item); }}
                className="w-full flex items-start gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-th-hover/40 transition-colors text-left"
              >
                <div className={`hidden sm:flex p-2 rounded-lg shrink-0 mt-0.5 ${item.resolved ? 'bg-emerald-100 dark:bg-emerald-500/20' : !item.read ? 'bg-brand-500/20' : 'bg-th-hover'}`}>
                  {item.resolved
                    ? <CheckCircle2 size={16} className="text-emerald-700 dark:text-emerald-400" />
                    : <Mail size={16} className={!item.read ? 'text-brand-400' : 'text-th-text-muted'} />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`truncate text-sm sm:text-base ${item.resolved ? 'text-th-text-muted line-through' : !item.read ? 'text-th-text font-semibold' : 'text-th-text-secondary font-medium'}`}>{item.subject || '(Ingen emne)'}</p>
                    {/* Badge inline on mobile */}
                    <span className="shrink-0 sm:hidden">
                      {item.matched_at ? (
                        <MatchetMaerke item={item} lille />
                      ) : leveringsstatus(item) ? (
                        <LeveringsMaerke item={item} lille />
                      ) : item.resolved ? (
                        <Badge className="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 border-emerald-400 dark:border-emerald-500/20 text-[10px]">Løst</Badge>
                      ) : item.pending_forward ? (
                        <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 text-[10px]">Afventer</Badge>
                      ) : !item.read ? (
                        <span className="w-2 h-2 rounded-full bg-brand-500 inline-block" />
                      ) : null}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 sm:gap-3 mt-0.5 flex-wrap">
                    {item.cvr && companyByCvr[item.cvr] && (
                      <span className="text-[10px] sm:text-xs font-medium text-th-text-secondary bg-th-hover border border-th-border-light px-1.5 sm:px-2 py-0.5 rounded">{companyByCvr[item.cvr].name}</span>
                    )}
                    <span className="hidden sm:inline text-xs text-th-text-muted">{item.from_address}</span>
                    {item.file_format && <span className="text-[10px] sm:text-xs bg-th-hover border border-th-border-light text-th-text-secondary px-1 sm:px-1.5 py-0.5 rounded">{item.file_format}</span>}
                    {item.forwarded_to && (
                      <span className="hidden sm:inline text-xs text-emerald-400">
                        → {Array.isArray(item.forwarded_to) ? item.forwarded_to.join(', ') : item.forwarded_to}
                      </span>
                    )}
                  </div>
                  {item.pending_forward && !item.resolved && (
                    <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                      <span className="text-[10px] sm:text-xs text-amber-600 dark:text-amber-400 font-medium truncate">
                        {item.pending_rule_name}
                      </span>
                      <button onClick={e => approveForward(e, item)} className="px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[10px] sm:text-xs font-medium transition-colors">Godkend</button>
                      <button onClick={e => rejectForward(e, item)} className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-500 text-white text-[10px] sm:text-xs font-medium transition-colors">Afvis</button>
                    </div>
                  )}
                  {item.resolved && item.resolved_by && (
                    <span className="hidden sm:inline text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                      {item.resolved_by.startsWith('Regel:')
                        ? `Løst af regel${item.resolved_at ? ` · ${formatDate(item.resolved_at)}` : ''}`
                        : `Løst af ${item.resolved_by}${item.resolved_at ? ` · ${formatDate(item.resolved_at)}` : ''}`
                      }
                    </span>
                  )}
                  {item.assigned_to_name && !item.resolved && (
                    <span className="hidden sm:inline text-xs text-brand-600 dark:text-brand-400 font-medium">
                      Tildelt {item.assigned_to_name}
                    </span>
                  )}
                </div>
                <div className="shrink-0 text-right hidden sm:block">
                  <p className="text-xs text-th-text-muted whitespace-nowrap">{formatDate(item.received_at)}</p>
                  <div className="flex items-center justify-end gap-2 mt-1">
                    <MatchetMaerke item={item} />
                    <LeveringsMaerke item={item} />
                    {item.resolved ? (
                      <Badge className="bg-emerald-100 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 border-emerald-400 dark:border-emerald-500/20">Løst</Badge>
                    ) : item.pending_forward ? (
                      <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20">Afventer</Badge>
                    ) : item.status === 'forwarded' ? (
                      <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Videresendt</Badge>
                    ) : item.status === 'error' ? (
                      <Badge className="bg-red-500/10 text-red-400 border-red-500/20">Fejl</Badge>
                    ) : item.read ? (
                      <Badge className="bg-th-hover text-th-text-secondary border-th-border-hover">Læst</Badge>
                    ) : (
                      <Badge className="bg-brand-500/10 text-brand-400 border-brand-500/20">Ulæst</Badge>
                    )}
                    <div className="relative">
                      <button onClick={e => aabnVideresend(e, item)} title="Videresend" className="p-1 text-th-text-faint hover:text-brand-400 transition-colors">
                        <Forward size={14} />
                      </button>
                      {videresendPopup(item)}
                    </div>
                    {/* Overdrag-knap */}
                    {!item.resolved && inboxRecipients.length > 0 && (
                      <div className="relative">
                        <button onClick={e => aabnTildel(e, item)} title="Overdrag til" className={`p-1 transition-colors ${item.assigned_to_email ? 'text-brand-400' : 'text-th-text-faint hover:text-brand-400'}`}>
                          <User size={14} />
                        </button>
                        {tildelPopup(item)}
                      </div>
                    )}
                    <button onClick={e => toggleResolved(e, item)} title={item.resolved ? 'Markér åben' : 'Markér løst'} className={`p-1 transition-colors ${item.resolved ? 'text-green-400 hover:text-th-text-faint' : 'text-th-text-faint hover:text-green-400'}`}>
                      <CheckCircle2 size={14} />
                    </button>
                    <button onClick={e => item.read ? markUnread(e, item) : markRead(e, item)} title={item.read ? 'Markér ulæst' : 'Markér læst'} className="p-1 text-th-text-faint hover:text-brand-400 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill={item.read ? 'none' : 'currentColor'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/></svg>
                    </button>
                    <button onClick={e => deleteItem(e, item)} title="Slet" className="p-1 text-th-text-faint hover:text-red-400 transition-colors">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    </button>
                    <span className="text-th-text-faint text-xs">{expanded === item.id ? '▲' : '▼'}</span>
                  </div>
                </div>
                {/* Mobile: just chevron */}
                <div className="shrink-0 sm:hidden flex items-center">
                  <span className="text-th-text-faint text-xs">{expanded === item.id ? '▲' : '▼'}</span>
                </div>
              </button>
              {expanded === item.id && (
                <>
                  <ExpandedMessage item={item} />
                  <NoteThread
                    item={item}
                    currentEmail={user?.email}
                    draft={notePopup === item.id ? noteDraft : ''}
                    setDraft={v => { setNotePopup(item.id); setNoteDraft(v); }}
                    saving={savingNote}
                    onAdd={addNote}
                    onDelete={deleteNote}
                  />
                  {/* Mobil: handlingsknapper (skjult på desktop hvor de er i rækken) */}
                  <div className="sm:hidden flex flex-wrap items-center gap-2 px-3 pb-3 pt-1 border-t border-th-border">
                    <button onClick={e => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const POPUP_W = 260, POPUP_MAX_H = Math.min(window.innerHeight - 32, 480);
                      let left = Math.min(rect.left + POPUP_W, window.innerWidth - 8);
                      left = Math.max(POPUP_W + 8, left);
                      let top = rect.bottom + 4;
                      if (top + POPUP_MAX_H > window.innerHeight - 8) top = Math.max(8, rect.top - POPUP_MAX_H - 4);
                      setForwardPopupPos({ top, left, maxHeight: POPUP_MAX_H });
                      setForwardPopup(forwardPopup === item.id ? null : item.id);
                    }} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-th-hover hover:bg-th-border text-th-text-secondary rounded-lg transition-colors">
                      <Forward size={14} /> Videresend
                    </button>
                    <button onClick={e => {
                      e.stopPropagation();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const POPUP_MAX_H = Math.min(window.innerHeight - 32, 400);
                      let top = rect.bottom + 4;
                      if (top + POPUP_MAX_H > window.innerHeight - 8) top = Math.max(8, rect.top - POPUP_MAX_H - 4);
                      setForwardPopupPos({ top, left: Math.min(rect.left + 200, window.innerWidth - 8), maxHeight: POPUP_MAX_H });
                      setAssignPopup(assignPopup === item.id ? null : item.id);
                      setAssignTarget(null); setAssignNote('');
                    }} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${item.assigned_to_email ? 'bg-brand-500/10 text-brand-400' : 'bg-th-hover hover:bg-th-border text-th-text-secondary'}`}>
                      <User size={14} /> {item.assigned_to_name ? item.assigned_to_name : 'Tildel'}
                    </button>
                    <button onClick={e => toggleResolved(e, item)} className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${item.resolved ? 'bg-green-500/10 text-green-400' : 'bg-th-hover hover:bg-th-border text-th-text-secondary'}`}>
                      <CheckCircle2 size={14} /> {item.resolved ? 'Åbn' : 'Løst'}
                    </button>
                    <button onClick={e => item.read ? markUnread(e, item) : markRead(e, item)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-th-hover hover:bg-th-border text-th-text-secondary rounded-lg transition-colors">
                      {item.read ? 'Markér ulæst' : 'Markér læst'}
                    </button>
                    <button onClick={e => deleteItem(e, item)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-th-hover hover:bg-red-500/10 text-th-text-secondary hover:text-red-400 rounded-lg transition-colors">
                      Slet
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {bekraeftVideresendelse}
    </div>
  );
}

// ─── Forward Rules View ───────────────────────────────────────────────────────

function RulesView() {
  const [rules, setRules] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const showForm = searchParams.get('ny') === '1' || !!searchParams.get('name');
  const [name, setName] = useState(searchParams.get('name') || '');
  const [forwardTo, setForwardTo] = useState(searchParams.get('forward_to') || '');
  const [fromEmail, setFromEmail] = useState(searchParams.get('from_email') || '');
  const [cvrFilter, setCvrFilter] = useState(searchParams.get('cvr_filter') || '');
  const [subjectMatch, setSubjectMatch] = useState(searchParams.get('subject_match') || '');
  const [senderFilter, setSenderFilter] = useState(searchParams.get('sender_filter') || '');
  const [autoApprove, setAutoApprove] = useState(true);
  const [editingId, setEditingId] = useState(null);
  // Selskabsnavne til reglernes grafik. Kun DigitalBox viser dem, saa hentpost
  // slipper for et opslag, den ikke bruger.
  const [selskaber, setSelskaber] = useState([]);
  useEffect(() => {
    if (TRE_KOLONNER) axios.get(`${API}/companies`).then(r => setSelskaber(r.data || [])).catch(() => {});
  }, []);

  const openForm = () => setSearchParams({ ny: '1' });
  const closeForm = () => {
    setName(''); setForwardTo(''); setFromEmail(''); setCvrFilter(''); setSubjectMatch(''); setSenderFilter(''); setAutoApprove(true); setEditingId(null);
    setSearchParams({}, { replace: true });
  };
  const editRule = (rule) => {
    setEditingId(rule.id);
    setName(rule.name);
    setForwardTo(Array.isArray(rule.forward_to) ? rule.forward_to.join(', ') : rule.forward_to);
    setFromEmail(rule.from_email || '');
    setCvrFilter(rule.cvr_filter || '');
    setSubjectMatch(rule.subject_match || '');
    setSenderFilter(rule.sender_filter || '');
    setAutoApprove(rule.auto_approve !== false);
    setSearchParams({ ny: '1' }, { replace: true });
  };

  const deepLinkId = searchParams.get('edit');

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/forward-rules`),
      axios.get(`${API}/contacts`),
    ]).then(([rulesRes, contactsRes]) => {
      setRules(rulesRes.data);
      setContacts(contactsRes.data);
      if (deepLinkId) {
        const rule = rulesRes.data.find(r => r.id === deepLinkId);
        if (rule) {
          setEditingId(rule.id);
          setName(rule.name);
          setForwardTo(Array.isArray(rule.forward_to) ? rule.forward_to.join(', ') : rule.forward_to);
          setFromEmail(rule.from_email || '');
          setCvrFilter(rule.cvr_filter || '');
          setSubjectMatch(rule.subject_match || '');
          setSenderFilter(rule.sender_filter || '');
          setAutoApprove(rule.auto_approve !== false);
          setSearchParams({ ny: '1' }, { replace: true });
        }
      }
    }).catch(() => toast.error('Kunne ikke hente regler'))
      .finally(() => setLoading(false));
  }, []);

  const addContactEmail = (email) => {
    const current = forwardTo.split(',').map(e => e.trim()).filter(Boolean);
    if (!current.includes(email)) {
      setForwardTo(current.length ? `${forwardTo}, ${email}` : email);
    }
  };

  const saveRule = async () => {
    if (!name || !forwardTo) { toast.error('Udfyld navn og modtager'); return; }
    const cleanFromEmail = fromEmail.trim();
    if (cleanFromEmail && !/^\S+@\S+\.\S+$/.test(cleanFromEmail)) { toast.error('Ugyldig afsender-email'); return; }
    const emails = forwardTo.split(',').map(e => e.trim()).filter(Boolean);
    const body = { name, forward_to: emails, from_email: cleanFromEmail || null, cvr_filter: cvrFilter || null, subject_match: subjectMatch || null, sender_filter: senderFilter || null, auto_approve: autoApprove };
    setSaving(true);
    try {
      if (editingId) {
        const r = await axios.put(`${API}/forward-rules/${editingId}`, body);
        setRules(prev => prev.map(rule => rule.id === editingId ? r.data : rule));
        toast.success('Regel opdateret');
      } else {
        const r = await axios.post(`${API}/forward-rules`, body);
        setRules(prev => [...prev, r.data]);
        toast.success('Regel oprettet');
      }
      closeForm();
    } catch {
      toast.error(editingId ? 'Kunne ikke opdatere regel' : 'Kunne ikke oprette regel');
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (id) => {
    try {
      await axios.delete(`${API}/forward-rules/${id}`);
      setRules(prev => prev.filter(r => r.id !== id));
      toast.success('Regel slettet');
    } catch {
      toast.error('Kunne ikke slette regel');
    }
  };

  const toggleRule = async (id) => {
    try {
      const r = await axios.put(`${API}/forward-rules/${id}/toggle`);
      setRules(prev => prev.map(rule => rule.id === id ? { ...rule, enabled: r.data.enabled } : rule));
    } catch {
      toast.error('Kunne ikke ændre regel');
    }
  };

  const runRules = async () => {
    setRunning(true);
    try {
      const r = await axios.post(`${API}/forward-rules/run`);
      if (r.data.matched > 0) {
        toast.success(r.data.message);
      } else {
        toast.info('Ingen beskeder matchede reglerne');
      }
    } catch {
      toast.error('Kunne ikke køre regler');
    } finally { setRunning(false); }
  };

  // Formularen er den samme i begge udseender; kun rammen om den skifter.
  const regelFormular = (
    <Card className="p-6 bg-th-input border-th-border mb-6">
      <h2 className="text-lg font-heading font-semibold text-th-text mb-4">{editingId ? 'Rediger regel' : 'Opret videresendelsesregel'}</h2>
      <div className="space-y-4">
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">Navn på regel</label>
          <Input value={name} onChange={e => setName(e.target.value)}
            placeholder="fx. Skat & moms"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
        </div>
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">Videresend til</label>
          <Input value={forwardTo} onChange={e => setForwardTo(e.target.value)}
            placeholder="din@email.dk, kollega@firma.dk"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
          {contacts.length > 0 && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-th-text-muted">Adressebog:</span>
              {contacts.map(c => (
                <button key={c.id} onClick={() => addContactEmail(c.email)}
                  className="text-xs px-2 py-1 rounded bg-th-hover border border-th-border text-th-text-secondary hover:text-th-text hover:border-brand-400 transition-colors">
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">Send fra <span className="text-th-text-faint">(valgfri afsender-email)</span></label>
          <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)}
            placeholder="fx. bogholderi@ditfirma.dk"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
          <p className="text-xs text-th-text-muted mt-1">Videresendte mails afsendes fra denne adresse — fx den email der er registreret hos modtageren (Pleo, e-conomic m.fl.). Tomt = standardafsenderen</p>
        </div>
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">Emne-match <span className="text-th-text-faint">(regex, tomt = alle)</span></label>
          <Input value={subjectMatch} onChange={e => setSubjectMatch(e.target.value)}
            placeholder="fx. skat|moms|frist"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted font-mono text-sm" />
          <p className="text-xs text-th-text-muted mt-1">Kun beskeder med emne der matcher dette mønster videresendes</p>
        </div>
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">Afsender-match <span className="text-th-text-faint">(regex, tomt = alle)</span></label>
          <Input value={senderFilter} onChange={e => setSenderFilter(e.target.value)}
            placeholder="fx. skat|gælds"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted font-mono text-sm" />
          <p className="text-xs text-th-text-muted mt-1">Kun beskeder fra afsendere der matcher dette mønster videresendes</p>
        </div>
        <div>
          <label className="text-sm text-th-text-secondary mb-1 block">CVR-filter <span className="text-th-text-faint">(tomt = alle)</span></label>
          <Input value={cvrFilter} onChange={e => setCvrFilter(e.target.value)}
            placeholder="12345678"
            className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setAutoApprove(!autoApprove)}
            className="flex items-center gap-2 text-sm text-th-text-secondary hover:text-th-text transition-colors">
            {autoApprove
              ? <ToggleRight size={24} className="text-brand-500" />
              : <ToggleLeft size={24} className="text-th-text-muted" />
            }
            {autoApprove ? 'Automatisk videresendelse' : 'Kræver manuel godkendelse'}
          </button>
        </div>
        <div className="flex gap-3">
          <Button onClick={saveRule} disabled={saving} className="bg-btn hover:bg-btn-hover text-btn-fg">
            {saving ? 'Gemmer...' : editingId ? 'Gem ændringer' : 'Opret regel'}
          </Button>
          <Button variant="outline" onClick={closeForm}>Annuller</Button>
        </div>
      </div>
    </Card>
  );

  // ── DigitalBox: hver regel som et lyst kort med sin egen lille flow-grafik
  if (TRE_KOLONNER) {
    const selskabsNavn = Object.fromEntries(selskaber.map(c => [c.cvr, c.name]));
    const regelGrafik = (rule) => {
      const modtagere = Array.isArray(rule.forward_to) ? rule.forward_to : String(rule.forward_to || '').split(',').map(e => e.trim()).filter(Boolean);
      return (
        <>
          <div className="flex flex-wrap items-center gap-2 bg-th-card border border-th-border-light rounded-xl px-4 py-3 text-sm text-th-text-muted">
            <span>Når</span>
            <DbTag variant="moerk">{rule.cvr_filter ? (selskabsNavn[rule.cvr_filter] || `CVR ${formatCvr(rule.cvr_filter)}`) : 'Al post'}</DbTag>
            {rule.sender_filter && <><span>fra</span><DbTag><span className="font-mono">{rule.sender_filter}</span></DbTag></>}
            {rule.subject_match && <><span>om</span><DbTag><span className="font-mono">{rule.subject_match}</span></DbTag></>}
            <span>lander</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 bg-db-label-soft rounded-xl px-4 py-3 text-sm text-db-ink">
            <Forward size={15} className="shrink-0" />
            <span>Send til</span>
            {modtagere.map(e => <DbTag key={e} variant="route">{e}</DbTag>)}
          </div>
        </>
      );
    };

    return (
      <div className="max-w-5xl mx-auto">
        <DbSideHoved titel="Regler for videresendelse"
          tekst="Når posten lander, sender DigitalBox den videre efter jeres regler — til kolleger, revisor eller jeres bogføringssystem.">
          {rules.length > 0 && (
            <button onClick={runRules} disabled={running} className={DB_KNAP}>
              <Play size={15} /> {running ? 'Kører…' : 'Kør regler nu'}
            </button>
          )}
          {!showForm && (
            <button onClick={openForm} className={DB_KNAP_PRIMAER}>
              <Plus size={15} /> Ny regel
            </button>
          )}
        </DbSideHoved>

        {showForm && regelFormular}

        {loading ? (
          <p className="py-12 text-center text-th-text-muted">Indlæser…</p>
        ) : rules.length === 0 && !showForm ? (
          <DbKort
            grafik={regelGrafik({ subject_match: 'skat|moms', forward_to: ['revisor@revisor.dk'] })}
            titel="Ingen regler endnu"
            tekst="En regel sender posten videre, så snart den lander — fx al post fra Skattestyrelsen til jeres revisor."
          >
            <div><button onClick={openForm} className={DB_KNAP_PRIMAER}><Plus size={15} /> Opret den første regel</button></div>
          </DbKort>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {rules.map(rule => (
              <DbKort key={rule.id} className={rule.enabled ? '' : 'opacity-70'}
                grafik={regelGrafik(rule)}
                titel={rule.name}
                tekst={
                  <div className="flex flex-col gap-3">
                    <p>
                      {rule.auto_approve === false ? 'Venter på, at nogen godkender, før den sendes.' : 'Sendes automatisk, så snart posten lander.'}
                      {rule.from_email && <> Afsendes fra {rule.from_email}.</>}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {rule.enabled ? <DbTag variant="faerdig">Aktiv</DbTag> : <DbTag>Slået fra</DbTag>}
                      {rule.auto_approve === false && <DbTag variant="advarsel">Manuel godkendelse</DbTag>}
                    </div>
                  </div>
                }
                handlinger={
                  <>
                    <DbSwitch til={!!rule.enabled} onSkift={() => toggleRule(rule.id)} titel={rule.enabled ? 'Slå reglen fra' : 'Slå reglen til'} />
                    <button onClick={() => editRule(rule)} title="Rediger" className={DB_IKONKNAP}><Pencil size={15} /></button>
                    <button onClick={() => deleteRule(rule.id)} title="Slet" className={`${DB_IKONKNAP} hover:text-red-600`}><Trash2 size={15} /></button>
                  </>
                }
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 sm:mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Videresendelse</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">Regler for automatisk videresendelse</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          {rules.length > 0 && (
            <Button onClick={runRules} disabled={running} variant="outline" size="sm"
              className="border-th-border text-th-text-secondary hover:text-th-text hover:border-brand-400">
              <Play size={16} className="mr-1 sm:mr-2" /> {running ? 'Kører...' : 'Kør regler'}
            </Button>
          )}
          <Button onClick={openForm} size="sm" className="bg-btn hover:bg-btn-hover text-btn-fg">
            <Plus size={16} className="mr-1 sm:mr-2" /> Ny regel
          </Button>
        </div>
      </div>

      {showForm && regelFormular}

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : rules.length === 0 && !showForm ? (
        <Card className="p-12 bg-th-input border-th-border text-center">
          <Settings size={48} className="text-th-text-faint mx-auto mb-4" />
          <p className="text-th-text-secondary text-lg font-medium">Ingen regler endnu</p>
          <p className="text-th-text-muted text-sm mt-1 mb-6">Opret en regel for at videresende digital post automatisk</p>
          <Button onClick={openForm} className="bg-btn hover:bg-btn-hover text-btn-fg">
            <Plus size={18} className="mr-2" /> Opret første regel
          </Button>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => (
            <Card key={rule.id} className="p-5 bg-th-input border-th-border">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-th-text font-medium">{rule.name}</p>
                    {rule.enabled
                      ? <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20">Aktiv</Badge>
                      : <Badge className="bg-th-hover text-th-text-secondary border-th-border-hover">Inaktiv</Badge>
                    }
                  </div>
                  <p className="text-sm text-th-text-secondary">
                    → {Array.isArray(rule.forward_to) ? rule.forward_to.join(', ') : rule.forward_to}
                  </p>
                  {rule.from_email && (
                    <p className="text-xs text-th-text-muted mt-0.5">Sendes fra: {rule.from_email}</p>
                  )}
                  {rule.subject_match && (
                    <p className="text-xs text-th-text-muted mt-0.5 font-mono">Emne: /{rule.subject_match}/</p>
                  )}
                  {rule.sender_filter && (
                    <p className="text-xs text-th-text-muted mt-0.5 font-mono">Afsender: /{rule.sender_filter}/</p>
                  )}
                  {rule.cvr_filter && (
                    <p className="text-xs text-th-text-muted mt-0.5">CVR: {rule.cvr_filter}</p>
                  )}
                  {rule.auto_approve === false && (
                    <Badge className="bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20 mt-1">Manuel godkendelse</Badge>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => editRule(rule)} title="Rediger"
                    className="text-th-text-muted hover:text-brand-400 transition-colors p-1">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => toggleRule(rule.id)}
                    className="text-th-text-secondary hover:text-brand-400 transition-colors p-1">
                    {rule.enabled
                      ? <ToggleRight size={24} className="text-brand-500" />
                      : <ToggleLeft size={24} />
                    }
                  </button>
                  <button onClick={() => deleteRule(rule.id)}
                    className="text-th-text-muted hover:text-red-400 transition-colors p-1">
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Contacts View (Adressebog) ──────────────────────────────────────────────

function ContactsView() {
  const [contacts, setContacts] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const editingId = searchParams.get('edit');
  const isNew = searchParams.get('ny') === '1';
  const showForm = !!editingId || isNew;
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchContacts = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/contacts`),
      axios.get(`${API}/forward-rules`),
    ])
      .then(([cr, rr]) => { setContacts(cr.data); setRules(rr.data); })
      .catch(() => toast.error('Kunne ikke hente kontakter'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchContacts(); }, []);

  useEffect(() => {
    if (editingId && contacts.length > 0) {
      const c = contacts.find(x => x.id === editingId);
      if (c) { setName(c.name); setEmail(c.email); setFromEmail(c.from_email || ''); }
    } else if (!showForm) {
      setName(''); setEmail(''); setFromEmail('');
    }
  }, [editingId, isNew, contacts]);

  const closeForm = () => setSearchParams({}, { replace: true });
  const openNew = () => setSearchParams({ ny: '1' });
  const startEdit = (c) => setSearchParams({ edit: c.id });

  const saveContact = async () => {
    if (!name || !email) { toast.error('Kaldenavn og email er påkrævet'); return; }
    const cleanFromEmail = fromEmail.trim();
    if (cleanFromEmail && !/^\S+@\S+\.\S+$/.test(cleanFromEmail)) { toast.error('Ugyldig afsender-email'); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/contacts/${editingId}`, { name, email, from_email: cleanFromEmail || null });
        toast.success('Kontakt opdateret');
      } else {
        await axios.post(`${API}/contacts`, { name, email, from_email: cleanFromEmail || null });
        toast.success('Kontakt tilføjet');
      }
      closeForm();
      fetchContacts();
    } catch {
      toast.error('Kunne ikke gemme kontakt');
    } finally {
      setSaving(false);
    }
  };

  const deleteContact = async (id) => {
    try {
      await axios.delete(`${API}/contacts/${id}`);
      setContacts(prev => prev.filter(c => c.id !== id));
      toast.success('Kontakt slettet');
    } catch {
      toast.error('Kunne ikke slette');
    }
  };

  // ── DigitalBox: kontakterne som hvide raekker i designets lyse kort
  if (TRE_KOLONNER) {
    const emailToRules = {};
    rules.forEach(r => (r.forward_to || []).forEach(e => { (emailToRules[e] = emailToRules[e] || []).push(r); }));
    const kendte = new Set(contacts.map(c => c.email));
    const kunIRegler = [...new Set(rules.flatMap(r => r.forward_to || []).filter(e => !kendte.has(e)))];
    const regelTags = (email) => (emailToRules[email] || []).length > 0 && (
      <div className="flex flex-wrap gap-1.5 mt-2">
        {emailToRules[email].map(r => (
          <a key={r.id} href={`/videresend?edit=${r.id}`} className="hover:opacity-80 transition-opacity">
            <DbTag variant="route">{r.name}</DbTag>
          </a>
        ))}
      </div>
    );
    const tom = contacts.length === 0 && kunIRegler.length === 0;

    return (
      <div className="max-w-5xl mx-auto">
        <DbSideHoved titel="Adressebog"
          tekst="Dem, I sender post videre til: revisor, bogholder og jeres bogføringssystem. Kontakterne bruges i reglerne.">
          {!showForm && <button onClick={openNew} className={DB_KNAP_PRIMAER}><Plus size={15} /> Tilføj kontakt</button>}
        </DbSideHoved>

        {showForm && (
          <DbKort className="mb-6" titel={editingId ? 'Rediger kontakt' : 'Ny kontakt'}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-th-text-muted mb-1 block">Kaldenavn</label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="fx Revisor" className="bg-th-card border-th-border-light text-th-text" />
              </div>
              <div>
                <label className="text-sm text-th-text-muted mb-1 block">E-mail</label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="email@firma.dk" className="bg-th-card border-th-border-light text-th-text" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm text-th-text-muted mb-1 block">Send fra <span className="text-th-text-faint">(valgfri)</span></label>
                <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="fx bogholderi@jeresfirma.dk" className="bg-th-card border-th-border-light text-th-text" />
                <p className="text-xs text-th-text-muted mt-1">Den adresse, modtagersystemet (Pleo, e-conomic m.fl.) kender jer på. Tomt = standardafsenderen.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={saveContact} disabled={saving} className={DB_KNAP_PRIMAER}>
                {saving ? 'Gemmer…' : editingId ? 'Gem ændringer' : 'Tilføj kontakt'}
              </button>
              <button onClick={closeForm} className={DB_KNAP}>Annuller</button>
            </div>
          </DbKort>
        )}

        {loading ? (
          <p className="py-12 text-center text-th-text-muted">Indlæser…</p>
        ) : tom && !showForm ? (
          <DbKort
            grafik={<DbChip ikon={BookUser} titel="revisor@revisor.dk" tekst="Revisor · bruges i 1 regel" />}
            titel="Ingen kontakter endnu"
            tekst="Tilføj dem, I plejer at sende post videre til, så kan de vælges med ét klik i reglerne og i indbakken.">
            <div><button onClick={openNew} className={DB_KNAP_PRIMAER}><Plus size={15} /> Tilføj den første kontakt</button></div>
          </DbKort>
        ) : (
          <div className="rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2">
            {contacts.map(c => (
              <div key={c.id} className="flex items-start gap-3 bg-th-card border border-th-border-light rounded-xl px-4 py-3">
                <DbAvatar tekst={c.name || c.email} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium leading-5 text-th-text truncate">{c.name}</p>
                  <p className="text-xs leading-4 text-th-text-muted truncate">
                    {c.email}{c.from_email ? ` · sendes fra ${c.from_email}` : ''}
                  </p>
                  {regelTags(c.email)}
                </div>
                <button onClick={() => startEdit(c)} title="Rediger" className={DB_IKONKNAP}><Pencil size={15} /></button>
                <button onClick={() => deleteContact(c.id)} title="Slet" className={`${DB_IKONKNAP} hover:text-red-600`}><Trash2 size={15} /></button>
              </div>
            ))}
            {kunIRegler.length > 0 && (
              <>
                <p className="px-2 pt-3 pb-1 text-xs font-semibold tracking-[0.72px] uppercase text-th-text-muted">Kun i regler — ikke gemt som kontakt</p>
                {kunIRegler.map(e => (
                  <div key={e} className="flex items-start gap-3 border border-dashed border-th-border rounded-xl px-4 py-3">
                    <DbAvatar tekst={e} farve="bg-slate-400" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm leading-5 text-th-text truncate">{e}</p>
                      {regelTags(e)}
                    </div>
                    <button onClick={() => { setEmail(e); setName(''); setSearchParams({ ny: '1' }); }} className={DB_KNAP}>
                      <Plus size={14} /> Gem
                    </button>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Adressebog</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">Kontakter til videresendelsesregler</p>
        </div>
        {!showForm && (
          <Button onClick={openNew} size="sm" className="bg-btn hover:bg-btn-hover text-btn-fg flex items-center gap-2 w-fit">
            <Plus size={16} /> Tilføj kontakt
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-6 bg-th-input border-th-border mb-6">
          <h2 className="text-lg font-heading font-semibold text-th-text mb-4">{editingId ? 'Rediger kontakt' : 'Ny kontakt'}</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-th-text-secondary mb-1 block">Kaldenavn *</label>
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Kaldenavn"
                  className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
              </div>
              <div>
                <label className="text-sm text-th-text-secondary mb-1 block">Email *</label>
                <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="email@firma.dk"
                  className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
              </div>
            </div>
            <div>
              <label className="text-sm text-th-text-secondary mb-1 block">Send fra <span className="text-th-text-faint">(valgfri afsender-email)</span></label>
              <Input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="fx. bogholderi@ditfirma.dk"
                className="bg-th-body border-th-border-light text-th-text placeholder:text-th-text-muted" />
              <p className="text-xs text-th-text-muted mt-1">Videresendelser til denne kontakt afsendes fra denne adresse — fx den email der er registreret hos modtageren (Pleo, e-conomic m.fl.). Tomt = standardafsenderen</p>
            </div>
            <div className="flex gap-3">
              <Button onClick={saveContact} disabled={saving} className="bg-btn hover:bg-btn-hover text-btn-fg">
                {saving ? 'Gemmer...' : editingId ? 'Gem ændringer' : 'Tilføj kontakt'}
              </Button>
              <Button variant="outline" onClick={closeForm}>Annuller</Button>
            </div>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : contacts.length === 0 && rules.every(r => !r.forward_to?.length) && !showForm ? (
        <Card className="p-12 bg-th-input border-th-border text-center">
          <div className="w-12 h-12 rounded-full bg-th-hover flex items-center justify-center mx-auto mb-4">
            <BookUser size={22} className="text-th-text-muted" />
          </div>
          <p className="text-th-text-secondary text-lg font-medium">Ingen kontakter endnu</p>
          <p className="text-th-text-muted text-sm mt-1 mb-6">Tilføj kontakter til brug i videresendelsesregler</p>
          <Button onClick={openNew} className="bg-btn hover:bg-btn-hover text-btn-fg">
            <Plus size={18} className="mr-2" /> Tilføj første kontakt
          </Button>
        </Card>
      ) : (() => {
        // Byg et map: email → hvilke regler der bruger den
        const emailToRules = {};
        rules.forEach(r => (r.forward_to || []).forEach(email => {
          if (!emailToRules[email]) emailToRules[email] = [];
          emailToRules[email].push(r);
        }));

        // Adresser fra regler der ikke har en kontakt
        const contactEmails = new Set(contacts.map(c => c.email));
        const ruleOnlyEmails = [...new Set(
          rules.flatMap(r => r.forward_to || []).filter(e => !contactEmails.has(e))
        )];

        return (
          <div className="space-y-3">
            {contacts.map(c => (
              <div key={c.id} className="bg-th-input border border-th-border rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="p-2 bg-th-hover rounded-full shrink-0">
                    <User size={18} className="text-th-text-secondary" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-th-text font-medium">{c.name}</p>
                    <span className="text-xs text-th-text-secondary">{c.email}</span>
                    {c.from_email && (
                      <p className="text-xs text-th-text-muted mt-0.5">Sendes fra: {c.from_email}</p>
                    )}
                    {emailToRules[c.email]?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {emailToRules[c.email].map(r => (
                          <a key={r.id} href={`/videresend?edit=${r.id}`}
                            className="inline-flex items-center gap-1 text-xs bg-brand-500/10 text-brand-700 dark:text-brand-400 border border-brand-300 dark:border-brand-500/20 rounded px-2 py-0.5 hover:bg-brand-500/20 transition-colors">
                            <ArrowRight size={10} /> {r.name}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => startEdit(c)} className="text-th-text-faint hover:text-brand-400 transition-colors">
                    <Pencil size={16} />
                  </button>
                  <button onClick={() => deleteContact(c.id)} className="text-th-text-faint hover:text-red-400 transition-colors">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {ruleOnlyEmails.length > 0 && (
              <>
                <p className="text-xs uppercase tracking-wider text-th-text-muted font-semibold pt-2">Adresser fra regler (ikke gemt som kontakt)</p>
                {ruleOnlyEmails.map(email => (
                  <div key={email} className="bg-th-input border border-th-border border-dashed rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="p-2 bg-th-hover rounded-full shrink-0">
                        <User size={18} className="text-th-text-muted" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-th-text-secondary text-sm">{email}</p>
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {emailToRules[email].map(r => (
                            <a key={r.id} href={`/videresend?edit=${r.id}`}
                              className="inline-flex items-center gap-1 text-xs bg-brand-500/10 text-brand-700 dark:text-brand-400 border border-brand-300 dark:border-brand-500/20 rounded px-2 py-0.5 hover:bg-brand-500/20 transition-colors">
                              <ArrowRight size={10} /> {r.name}
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => { setEmail(email); setName(''); setSearchParams({ ny: '1' }); }}
                      className="text-xs text-th-text-muted hover:text-brand-400 shrink-0">
                      + Gem som kontakt
                    </Button>
                  </div>
                ))}
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── Profile View ─────────────────────────────────────────────────────────────

// Kundens eget logo. Det saettes ind i toppen af de mails, vi sender paa
// hans vegne — foerst og fremmest indbydelsen til at underskrive.
function LogoKort({ user, fetchUser }) {
  const filFelt = useRef(null);
  const [sender, setSender] = useState(false);
  // Cache-bryder: browseren ville ellers blive ved med at vise det gamle logo,
  // fordi adressen er den samme efter en udskiftning.
  const [version, setVersion] = useState(() => user?.logo_at || '');
  const [visning, setVisning] = useState(null);

  const harLogo = Boolean(user?.logo_at);

  useEffect(() => {
    setVersion(user?.logo_at || '');
  }, [user?.logo_at]);

  // Billedet ligger bag login, saa <img src> kan ikke hente det selv — den
  // sender ingen Authorization-header. Derfor hentes det med axios og vises
  // som en blob-URL.
  useEffect(() => {
    let afbrudt = false;
    let url = null;
    if (!harLogo) { setVisning(null); return undefined; }
    axios.get(`${API}/profile/logo`, { responseType: 'blob' })
      .then((r) => {
        if (afbrudt) return;
        url = URL.createObjectURL(r.data);
        setVisning(url);
      })
      .catch(() => { if (!afbrudt) setVisning(null); });
    return () => {
      afbrudt = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [harLogo, version]);

  const vaelg = async (e) => {
    const fil = e.target.files?.[0];
    e.target.value = '';                 // saa samme fil kan vaelges igen
    if (!fil) return;
    if (fil.size > 2 * 1024 * 1024) { toast.error('Filen er større end 2 MB'); return; }
    setSender(true);
    try {
      const data = new FormData();
      data.append('fil', fil);
      await axios.post(`${API}/profile/logo`, data);
      toast.success('Logoet er gemt');
      await fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke gemme logoet');
    } finally {
      setSender(false);
    }
  };

  const fjern = async () => {
    if (!window.confirm('Fjern logoet? Mails sendes derefter kun med vores eget mærke.')) return;
    try {
      await axios.delete(`${API}/profile/logo`);
      toast.success('Logoet er fjernet');
      await fetchUser();
    } catch {
      toast.error('Kunne ikke fjerne logoet');
    }
  };

  return (
    <Card className="p-8 bg-th-sidebar border-th-border mb-6">
      <div className="flex items-center gap-4 mb-6">
        <Image size={32} className="text-brand-500" />
        <div>
          <h2 className="text-2xl font-heading font-bold text-th-text">Logo</h2>
          <p className="text-sm text-th-text-secondary">
            Vises øverst i de mails, vi sender på dine vegne
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-6">
        {/* Med et logo er fladen hvid, fordi mailen er det — saa ser man logoet,
            som modtageren ser det. Uden logo er der intet at vise det paa, og
            i DigitalBox er den tomme flade derfor stiplet i temaets farve. */}
        <div className={`shrink-0 w-full sm:w-52 h-28 rounded-lg flex flex-col items-center justify-center overflow-hidden ${
          TRE_KOLONNER && !visning ? 'border border-dashed border-th-border bg-th-card' : 'border border-th-border bg-white'
        }`}>
          {visning ? (
            <>
              <img src={visning} alt="Dit logo" className={`${TRE_KOLONNER ? 'max-h-16' : 'max-h-20'} max-w-[85%] object-contain`} />
              {TRE_KOLONNER && <span className="mt-1.5 text-[11px] text-slate-500">Sådan ser det ud i mailen</span>}
            </>
          ) : (
            <span className="text-sm text-th-text-muted">Intet logo</span>
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap gap-2 mb-3">
            <Button onClick={() => filFelt.current?.click()} disabled={sender}>
              <Upload size={16} className="mr-1.5" />
              {sender ? 'Gemmer…' : harLogo ? 'Skift logo' : 'Vælg logo'}
            </Button>
            {harLogo && (
              <Button
                variant="ghost"
                onClick={fjern}
                disabled={sender}
                className="text-red-600 dark:text-red-400 hover:bg-red-500/10"
              >
                <Trash2 size={15} className="mr-1.5" />
                Fjern
              </Button>
            )}
          </div>
          <input
            ref={filFelt}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/gif"
            onChange={vaelg}
            className="hidden"
          />
          <p className="text-sm text-th-text-secondary">
            PNG, JPG, WEBP eller GIF på højst 2 MB. Logoet skaleres til 96 px i højden,
            så et bredt logo med gennemsigtig baggrund virker bedst. SVG kan indeholde
            kode og accepteres derfor ikke.
          </p>
        </div>
      </div>
    </Card>
  );
}

// Defineret paa modulniveau og ikke inde i kortet: en komponent, der erklaeres
// under en gengivelse, faar ny identitet hver gang, og React river den gamle ned
// og bygger en ny. Talfeltet ville miste fokus efter hvert tastetryk.
function NotifKontakt({ til, onSkift, titel, beskrivelse }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <button type="button" onClick={onSkift} aria-pressed={til} aria-label={titel}
        className={`relative w-10 h-6 rounded-full shrink-0 mt-0.5 transition-colors ${til ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-600'}`}>
        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${til ? 'translate-x-4' : ''}`} />
      </button>
      <div>
        <span className="text-sm font-medium text-th-text">{titel}</span>
        <p className="text-xs text-th-text-muted mt-0.5">{beskrivelse}</p>
      </div>
    </label>
  );
}

function NotifDage({ vaerdi, onSkift, titel, beskrivelse, min, max }) {
  return (
    <div>
      <label className="text-sm font-medium text-th-text">{titel}</label>
      <div className="flex items-center gap-2 mt-1">
        <input type="number" min={min} max={max} value={vaerdi}
          onChange={(e) => onSkift(e.target.value === '' ? '' : Number(e.target.value))}
          className="w-20 h-9 px-3 rounded-lg bg-th-input border border-th-border text-th-text text-sm" />
        <span className="text-sm text-th-text-secondary">dage</span>
      </div>
      <p className="text-xs text-th-text-muted mt-1">{beskrivelse}</p>
    </div>
  );
}

// ─── Notifikationer ───────────────────────────────────────────────────────────
//
// Kontoens overordnede indstillinger — de gaelder hele virksomheden. Hvorvidt
// en BESTEMT modtager faar de daglige paamindelser styres stadig af "Primaer
// modtager" under Modtagere; her staar om kontoen sender dem overhovedet, og
// efter hvor mange dage. To steder, men to forskellige spoergsmaal.
function NotifikationsKort({ user, fetchUser }) {
  const standard = { daglig_mail: true, daglig_sms: true, ny_post: true, karens_dage: 3, eskalering_dage: 6 };
  const gemt = { ...standard, ...(user?.notifikationer || {}) };
  const [v, setV] = React.useState(gemt);
  const [gemmer, setGemmer] = React.useState(false);

  // Naar brugeren hentes igen efter en gemning, skal felterne foelge med.
  React.useEffect(() => { setV({ ...standard, ...(user?.notifikationer || {}) }); },
    [user?.notifikationer]); // eslint-disable-line react-hooks/exhaustive-deps

  const udfyldt = v.karens_dage !== '' && v.eskalering_dage !== '';
  const aendret = udfyldt && JSON.stringify(v) !== JSON.stringify(gemt);

  const gem = async () => {
    setGemmer(true);
    try {
      await axios.put(`${API}/profile/notifications`, v);
      toast.success('Notifikationer gemt');
      await fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme');
    } finally { setGemmer(false); }
  };

  return (
    <Card className="p-8 bg-th-sidebar border-th-border mb-6">
      <div className="flex items-center gap-4 mb-6">
        <Bell size={32} className="text-highlight-500" />
        <div>
          <h2 className="text-2xl font-heading font-bold text-th-text">Notifikationer</h2>
          <p className="text-sm text-th-text-secondary mt-0.5">Gælder hele virksomheden</p>
        </div>
      </div>

      <div className="space-y-5">
        <NotifKontakt til={v.ny_post} onSkift={() => setV(x => ({ ...x, ny_post: !x.ny_post }))}
          titel="Besked ved ny post"
          beskrivelse="Modtagere får mail og SMS, så snart der kommer post. Slås den fra, lander posten stadig i indbakken." />
        <NotifKontakt til={v.daglig_mail} onSkift={() => setV(x => ({ ...x, daglig_mail: !x.daglig_mail }))}
          titel="Daglig opsummering på mail"
          beskrivelse="Hverdage kl. 08:00 med ulæste beskeder og åbne sager." />
        <NotifKontakt til={v.daglig_sms} onSkift={() => setV(x => ({ ...x, daglig_sms: !x.daglig_sms }))}
          titel="Daglig opsummering på SMS"
          beskrivelse="Hverdage kl. 09:00, kun for ulæst post." />

        <div className="border-t border-th-border pt-5 grid gap-5 sm:grid-cols-2">
          <NotifDage vaerdi={v.karens_dage} onSkift={(n) => setV(x => ({ ...x, karens_dage: n }))}
            titel="Følg op på læste sager efter" min={1} max={60}
            beskrivelse="En læst men uløst sag kommer først med i opsummeringen, når den er ældre end dette." />
          <NotifDage vaerdi={v.eskalering_dage} onSkift={(n) => setV(x => ({ ...x, eskalering_dage: n }))}
            titel="Eskalér uløste sager efter" min={1} max={180}
            beskrivelse="Herefter får alle modtagere besked, ikke kun de primære." />
        </div>

        <div className="flex items-center gap-3 pt-1">
          <Button onClick={gem} disabled={!aendret || gemmer}
            className="bg-btn hover:bg-btn-hover text-btn-fg">
            {gemmer ? 'Gemmer...' : 'Gem indstillinger'}
          </Button>
          {aendret && <span className="text-xs text-th-text-muted">Ugemte ændringer</span>}
        </div>
      </div>
    </Card>
  );
}

function ProfileView({ user, fetchUser, permissions }) {
  const [editing, setEditing] = useState(false);
  const [contactName, setContactName] = useState(user?.contact_name || '');
  const [companyName, setCompanyName] = useState(user?.company_name || '');
  const [companyCvr, setCompanyCvr] = useState(user?.company_cvr || '');
  const [saving, setSaving] = useState(false);
  const tierInfo = getTierLabel(user?.subscription_tier);

  // Phone verify flow
  const [phoneStep, setPhoneStep] = useState(null); // null | 'input' | 'code'
  const [newPhone, setNewPhone] = useState('');
  const [phoneCode, setPhoneCode] = useState('');
  const [phoneSaving, setPhoneSaving] = useState(false);

  const sendPhoneSms = async () => {
    if (!newPhone.trim()) return;
    setPhoneSaving(true);
    try {
      await axios.post(`${API}/profile/phone/send-sms`, { phone: newPhone.trim() });
      setPhoneStep('code');
      setPhoneCode('');
      toast.success('SMS-kode sendt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende SMS');
    } finally { setPhoneSaving(false); }
  };

  const verifyPhoneCode = async () => {
    if (phoneCode.length !== 6) return;
    setPhoneSaving(true);
    try {
      await axios.post(`${API}/profile/phone/verify`, { phone: newPhone.trim(), code: phoneCode });
      toast.success('Mobilnummer opdateret');
      setPhoneStep(null);
      setNewPhone('');
      setPhoneCode('');
      fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert kode');
    } finally { setPhoneSaving(false); }
  };

  // Email verify flow
  const [emailStep, setEmailStep] = useState(null); // null | 'input' | 'code'
  const [newEmail, setNewEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  const sendEmailCode = async () => {
    if (!newEmail.trim()) return;
    setEmailSaving(true);
    try {
      await axios.post(`${API}/profile/email/send-code`, { email: newEmail.trim() });
      setEmailStep('code');
      setEmailCode('');
      toast.success('Bekræftelseskode sendt til ' + newEmail.trim());
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende kode');
    } finally { setEmailSaving(false); }
  };

  const verifyEmailCode = async () => {
    if (emailCode.length !== 6) return;
    setEmailSaving(true);
    try {
      await axios.post(`${API}/profile/email/verify`, { code: emailCode });
      toast.success('E-mailadresse opdateret');
      setEmailStep(null);
      setNewEmail('');
      setEmailCode('');
      fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert kode');
    } finally { setEmailSaving(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profile`, {
        contact_name: contactName,
        company_name: companyName,
        company_cvr: companyCvr,
      });
      await fetchUser();
      toast.success('Profil opdateret');
      setEditing(false);
    } catch {
      toast.error('Kunne ikke opdatere profil');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Min profil</h1>
        <p className="text-th-text-secondary">Administrer dine kontooplysninger</p>
      </div>

      {/* Profile Card */}
      <Card className="p-8 bg-th-sidebar border-th-border mb-6">
        <div className="flex items-center gap-4 mb-8">
          <div className="p-4 bg-brand-500/10 rounded-full">
            <User size={32} className="text-brand-500" />
          </div>
          <div>
            <h2 className="text-2xl font-heading font-bold text-th-text">{user?.email}</h2>
            <Badge className={`${tierInfo.color} text-white mt-2`}>{tierInfo.name}</Badge>
          </div>
        </div>

        {editing ? (
          <div className="space-y-5">
            {/* E-mail */}
            <div>
              <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-1">E-mail</label>
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-th-hover border border-th-border text-th-text-secondary text-sm">
                <Mail size={15} className="text-th-text-muted shrink-0" />
                <span className="flex-1">{user?.email}</span>
                {emailStep === null && (
                  <button onClick={() => { setEmailStep('input'); setNewEmail(''); }} className="text-xs text-brand-600 dark:text-brand-400 hover:underline shrink-0">Skift</button>
                )}
              </div>
              {emailStep === 'input' && (
                <div className="mt-2 space-y-2 p-3 rounded-lg bg-th-hover border border-th-border">
                  <Input value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="ny@email.dk"
                    className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                  <div className="flex gap-2">
                    <Button onClick={sendEmailCode} disabled={emailSaving || !newEmail.trim()} className="bg-btn hover:bg-btn-hover text-btn-fg text-xs h-8 px-3">
                      {emailSaving ? 'Sender...' : 'Send kode'}
                    </Button>
                    <Button variant="ghost" onClick={() => setEmailStep(null)} className="text-th-text-secondary text-xs h-8 px-3">Annuller</Button>
                  </div>
                </div>
              )}
              {emailStep === 'code' && (
                <div className="mt-2 space-y-2 p-3 rounded-lg bg-th-hover border border-th-border">
                  <p className="text-xs text-th-text-secondary">Kode sendt til {newEmail} — tjek din indbakke.</p>
                  <input type="text" inputMode="numeric" maxLength={6} value={emailCode}
                    onChange={e => setEmailCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456" autoFocus
                    className="w-full bg-th-input border border-th-border-light text-th-text placeholder-th-text-muted rounded-lg px-4 py-2 text-center text-xl font-mono tracking-widest focus:outline-none focus:border-brand-500" />
                  <div className="flex gap-2 items-center">
                    <Button onClick={verifyEmailCode} disabled={emailSaving || emailCode.length !== 6} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 px-3">
                      {emailSaving ? 'Bekræfter...' : 'Bekræft'}
                    </Button>
                    <button onClick={() => setEmailStep('input')} className="text-xs text-th-text-muted hover:text-th-text-secondary">Send ny kode</button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobilnummer */}
            <div>
              <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-1">Mobilnummer</label>
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-th-hover border border-th-border text-th-text-secondary text-sm">
                <Phone size={15} className="text-th-text-muted shrink-0" />
                <span className="flex-1">{user?.phone || <span className="italic text-th-text-muted">Ikke angivet</span>}</span>
                {phoneStep === null && (
                  <button onClick={() => { setPhoneStep('input'); setNewPhone(user?.phone || ''); }} className="text-xs text-brand-600 dark:text-brand-400 hover:underline shrink-0">Skift</button>
                )}
              </div>
              {phoneStep === 'input' && (
                <div className="mt-2 space-y-2 p-3 rounded-lg bg-th-hover border border-th-border">
                  <Input value={newPhone} onChange={e => setNewPhone(e.target.value)} placeholder="+45 12 34 56 78"
                    className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
                  <div className="flex gap-2">
                    <Button onClick={sendPhoneSms} disabled={phoneSaving || !newPhone.trim()} className="bg-btn hover:bg-btn-hover text-btn-fg text-xs h-8 px-3">
                      {phoneSaving ? 'Sender...' : 'Send SMS-kode'}
                    </Button>
                    <Button variant="ghost" onClick={() => setPhoneStep(null)} className="text-th-text-secondary text-xs h-8 px-3">Annuller</Button>
                  </div>
                </div>
              )}
              {phoneStep === 'code' && (
                <div className="mt-2 space-y-2 p-3 rounded-lg bg-th-hover border border-th-border">
                  <p className="text-xs text-th-text-secondary">SMS-kode sendt til {newPhone}.</p>
                  <input type="text" inputMode="numeric" maxLength={6} value={phoneCode}
                    onChange={e => setPhoneCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456" autoFocus
                    className="w-full bg-th-input border border-th-border-light text-th-text placeholder-th-text-muted rounded-lg px-4 py-2 text-center text-xl font-mono tracking-widest focus:outline-none focus:border-brand-500" />
                  <div className="flex gap-2 items-center">
                    <Button onClick={verifyPhoneCode} disabled={phoneSaving || phoneCode.length !== 6} className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs h-8 px-3">
                      {phoneSaving ? 'Bekræfter...' : 'Bekræft'}
                    </Button>
                    <button onClick={() => setPhoneStep('input')} className="text-xs text-th-text-muted hover:text-th-text-secondary">Send ny kode</button>
                  </div>
                </div>
              )}
            </div>

            {/* Kontaktperson */}
            <div>
              <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-1">Kontaktperson</label>
              <Input value={contactName} onChange={e => setContactName(e.target.value)}
                placeholder="Dit navn"
                className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-1">Firmanavn</label>
              <Input value={companyName} onChange={e => setCompanyName(e.target.value)}
                placeholder="Dit firma"
                className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-1">CVR-nummer</label>
              <Input value={companyCvr} onChange={e => setCompanyCvr(e.target.value)}
                placeholder="12345678"
                className="bg-th-input border-th-border-light text-th-text placeholder:text-th-text-muted" />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={save} disabled={saving} className="bg-btn hover:bg-btn-hover text-btn-fg">
                {saving ? 'Gemmer...' : 'Gem ændringer'}
              </Button>
              <Button onClick={() => { setEditing(false); setEmailStep(null); setPhoneStep(null); }} variant="ghost" className="text-th-text-secondary hover:text-th-text">
                Luk
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-start gap-4">
              <Mail size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Email</p>
                <p className="text-th-text-secondary">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <User size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Kontaktperson</p>
                <p className="text-th-text-secondary">{user?.contact_name || <span className="text-th-text-muted italic">Ikke angivet</span>}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Building size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Firmanavn</p>
                <p className="text-th-text-secondary">{user?.company_name || <span className="text-th-text-muted italic">Ikke angivet</span>}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Building size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">CVR-nummer</p>
                <p className="text-th-text-secondary">{user?.company_cvr || <span className="text-th-text-muted italic">Ikke angivet</span>}</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <Phone size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Mobilnummer</p>
                {user?.phone
                  ? (
                    <p className="text-th-text-secondary flex items-center gap-2 flex-wrap">
                      {user.phone}
                      {/* Tilstanden skal kunne ses, ogsaa paa planer hvor den
                          endnu ikke betyder noget — ellers opdager man det
                          foerst den dag SMS'erne udebliver. */}
                      {user.phone_verified
                        ? <span className="text-xs px-2 py-0.5 rounded border bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-300 dark:border-emerald-500/20">✓ Bekræftet</span>
                        : <span className="text-xs px-2 py-0.5 rounded border bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/20">Ikke bekræftet</span>}
                    </p>
                  )
                  : <p className="text-th-text-muted italic">Ikke angivet</p>}
              </div>
            </div>
            {permissions?.affiliated_with?.length > 0 && (
              <div className="flex items-start gap-4">
                <Building size={24} className="text-th-text-muted shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Tilknyttet</p>
                  {permissions.affiliated_with.map((a, i) => (
                    <div key={i} className="text-th-text-secondary">
                      <span className="font-medium text-th-text">{a.company_name || 'Ukendt firma'}</span>
                      {a.company_cvr && <span className="text-th-text-muted ml-2">CVR {a.company_cvr}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-start gap-4">
              <Calendar size={24} className="text-th-text-muted shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-1">Medlem siden</p>
                <p className="text-th-text-secondary">
                  {user?.created_at ? new Date(user.created_at).toLocaleDateString('da-DK', { year: 'numeric', month: 'long', day: 'numeric' }) : '—'}
                </p>
              </div>
            </div>
            <Button onClick={() => setEditing(true)} variant="ghost" className="text-th-text-secondary hover:text-th-text hover:bg-th-hover px-0">
              Rediger oplysninger →
            </Button>
          </div>
        )}
      </Card>

      <LogoKort user={user} fetchUser={fetchUser} />

      <NotifikationsKort user={user} fetchUser={fetchUser} />

      {/* Subscription Card */}
      <Card className="p-8 bg-th-sidebar border-th-border mb-6">
        <div className="flex items-center gap-4 mb-6">
          <CreditCard size={32} className="text-highlight-500" />
          <h2 className="text-2xl font-heading font-bold text-th-text">Abonnement</h2>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-2">Nuværende plan</p>
            <p className="text-xl font-bold text-th-text">{tierInfo.name}</p>
            {user?.subscription_tier === 'invited' ? (
              <p className="text-sm text-th-text-secondary mt-1">Du er inviteret som modtager — gratis adgang</p>
            ) : (
              <p className="text-sm text-th-text-secondary mt-1">Op til {tierInfo.limit} {tierInfo.limit === 1 ? 'selskab' : 'selskaber'}</p>
            )}
            {user?.subscription_tier === 'free' && (
              <ul className="mt-2 space-y-1">
                <li className="flex items-center gap-2 text-sm text-th-text-secondary">
                  <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
                  Egen e-mail — send posten til selskabets adresse
                </li>
                <li className="flex items-center gap-2 text-sm text-th-text-muted">
                  <Lock size={13} className="shrink-0" />
                  Digital Post og e-Boks kræver Solo
                </li>
              </ul>
            )}
            {user?.trial_expires_at && user?.subscription_tier !== 'free' && (
              <p className="text-sm text-amber-400 mt-1">Trial udløber: {formatDate(user.trial_expires_at)}</p>
            )}
          </div>
          <Badge className={`${tierInfo.color} text-white text-lg px-4 py-2`}>{tierInfo.name}</Badge>
        </div>

        {/* Alle planer ét sted — op og ned. Hvad der spærrer for en
            nedgradering afgøres af serveren, som ved hvad der er i brug. */}
        <PlanVaelger user={user} onSkiftet={fetchUser} />

        {user?.subscription_tier === 'pro' && (
          <p className="text-sm text-emerald-400 flex items-center gap-2">
            <CheckCircle2 size={16} /> Du er på vores højeste plan
          </p>
        )}
      </Card>

      {/* Cloud Storage er en Pro-funktion. Serveren afviser i forvejen kald fra
          lavere planer, men sektionen blev vist til alle — en gratisbruger fik
          tre "Forbind"-knapper, der kun kunne give en fejl. Planen er derfor
          betingelsen.
          Tidligere skjulte vi den ogsaa, hvis brugeren var modtager hos en
          anden ejer (affiliated_with). Det ramte forkert: man kan sagtens eje
          sin egen konto OG vaere modtager et andet sted, og cloud-koblingen
          hoerer til ens EGEN konto. Det skjulte kassen for de fleste rigtige
          brugere — kun fire konti opfyldte betingelsen. */}
      {user?.subscription_tier === 'pro' && (
        <CloudStorageSection />
      )}
    </div>
  );
}

// ─── Cloud Storage Section ────────────────────────────────────────────────────

const CLOUD_PROVIDERS = [
  // ikon: udbyderens eget logo (public/brand/cloud, fra Wikimedia Commons — /brand/ serveres ogsaa paa dev).
  // DigitalBox viser det; hentpost bruger stadig emojien.
  { id: 'onedrive',    label: 'OneDrive',     logo: '🔷', ikon: '/brand/cloud/onedrive.svg',    color: 'border-brand-500/30 bg-brand-500/5' },
  { id: 'googledrive', label: 'Google Drive', logo: '🟡', ikon: '/brand/cloud/googledrive.svg', color: 'border-yellow-500/30 bg-yellow-500/5' },
  { id: 'dropbox',     label: 'Dropbox',      logo: '🔵', ikon: '/brand/cloud/dropbox.svg',     color: 'border-sky-500/30 bg-sky-500/5' },
];

// Planvalget hentes fra serveren: den ved hvad kontoen faktisk bruger, og
// dermed hvad der spaerrer for en nedgradering. UI'et gentager kun beskeden.
function PlanVaelger({ user, onSkiftet }) {
  const [planer, setPlaner] = useState([]);
  const [henter, setHenter] = useState(true);
  const [skifter, setSkifter] = useState(null);

  const hent = () => {
    setHenter(true);
    axios.get(`${API}/subscription/plans`)
      .then(r => setPlaner(r.data?.planer || []))
      .catch(() => setPlaner([]))
      .finally(() => setHenter(false));
  };
  useEffect(hent, [user?.subscription_tier]);

  const skift = async (plan) => {
    if (plan.retning === 'ned' && !window.confirm(
      `Nedgrader til ${plan.name}?\n\nDu mister adgangen til det, planen ikke rummer.`
    )) return;
    setSkifter(plan.tier);
    try {
      await axios.post(`${API}/subscription/change`, { tier: plan.tier });
      toast.success(plan.retning === 'ned' ? `Nedgraderet til ${plan.name}` : `Opgraderet til ${plan.name}`);
      onSkiftet();
      hent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke skifte plan', { duration: 9000 });
    } finally { setSkifter(null); }
  };

  if (henter) return <p className="text-sm text-th-text-muted">Henter planer...</p>;

  return (
    <div>
      <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold mb-4">Alle abonnementer</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-stretch">
        {planer.map(p => (
          <div key={p.tier} className={`rounded-xl border p-5 flex flex-col gap-3 ${
            p.current ? 'border-emerald-500/40 bg-emerald-500/5' : 'border-th-border bg-th-hover'
          }`}>
            <div className="flex items-center justify-between gap-2">
              <span className="font-heading font-bold text-th-text">{p.name}</span>
              <div className="text-right shrink-0">
                <span className="text-xl font-bold text-th-text">{p.price}</span>
                <span className="text-th-text-secondary text-xs"> kr/md</span>
              </div>
            </div>
            <ul className="space-y-1 flex-1">
              {p.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-th-text-secondary">
                  <CheckCircle2 size={13} className="text-emerald-500 shrink-0 mt-1" />
                  {f}
                </li>
              ))}
            </ul>

            {p.current ? (
              <p className="text-sm text-emerald-500 flex items-center gap-2 font-medium">
                <CheckCircle2 size={15} /> Din nuværende plan
              </p>
            ) : p.blokeringer?.length ? (
              <div className="space-y-2">
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  Du kan ikke nedgradere, da disse services er aktive:
                </p>
                <ul className="space-y-1">
                  {p.blokeringer.map((b, i) => (
                    <li key={i} className="text-xs text-th-text-muted flex items-start gap-1.5">
                      <Lock size={11} className="shrink-0 mt-0.5" /> {b}
                    </li>
                  ))}
                </ul>
                <a href="mailto:support@hentpost.dk?subject=Nedgradering%20af%20abonnement"
                   className="text-xs text-blue-600 dark:text-blue-400 hover:underline inline-block">
                  Kontakt support, hvis du ønsker at nedgradere
                </a>
              </div>
            ) : (
              <Button
                onClick={() => skift(p)}
                disabled={skifter === p.tier}
                className={p.retning === 'ned'
                  ? 'w-full bg-th-hover hover:bg-th-border text-th-text-secondary border border-th-border'
                  : 'w-full bg-btn hover:bg-btn-hover text-btn-fg'}
              >
                {skifter === p.tier
                  ? 'Skifter...'
                  : p.retning === 'ned' ? `Nedgrader til ${p.name}` : `Opgrader til ${p.name}`}
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CloudStorageSection() {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [folderPath, setFolderPath] = useState('');
  const [saving, setSaving] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    axios.get(`${API}/integrations/cloud`)
      .then(r => setIntegrations(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    if (searchParams.get('cloud') === 'connected') {
      toast.success('Cloud storage forbundet!');
      window.history.replaceState({}, '', '/profil');
    }
  }, []);

  const connect = async (provider) => {
    try {
      const r = await axios.get(`${API}/integrations/${provider}/connect`);
      window.location.href = r.data.url;
    } catch {
      toast.error('Kunne ikke starte forbindelsen');
    }
  };

  const disconnect = async (id) => {
    if (!window.confirm('Afkobl denne integration?')) return;
    try {
      await axios.delete(`${API}/integrations/cloud/${id}`);
      setIntegrations(prev => prev.filter(i => i.id !== id));
      toast.success('Integration afkoblet');
    } catch {
      toast.error('Kunne ikke afkoble');
    }
  };

  const saveFolder = async (id) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/integrations/cloud/${id}`, { folder_path: folderPath });
      setIntegrations(prev => prev.map(i => i.id === id ? { ...i, folder_path: folderPath } : i));
      setEditingId(null);
      toast.success('Mappesti gemt');
    } catch {
      toast.error('Kunne ikke gemme');
    } finally { setSaving(false); }
  };

  const toggleEnabled = async (integration) => {
    try {
      await axios.patch(`${API}/integrations/cloud/${integration.id}`, { enabled: !integration.enabled });
      setIntegrations(prev => prev.map(i => i.id === integration.id ? { ...i, enabled: !i.enabled } : i));
    } catch {
      toast.error('Kunne ikke opdatere');
    }
  };

  return (
    <Card className="p-8 bg-th-sidebar border-th-border">
      <div className="flex items-center gap-4 mb-6">
        {TRE_KOLONNER
          ? <Cloud size={28} className="text-db-label shrink-0" />
          : <div className="w-8 h-8 text-2xl leading-none">☁</div>}
        <div>
          <h2 className="text-2xl font-heading font-bold text-th-text">Cloud Storage</h2>
          <p className="text-sm text-th-text-secondary mt-0.5">Gem indkommende dokumenter automatisk i din cloud-løsning</p>
        </div>
      </div>

      {loading ? (
        <p className="text-th-text-muted text-sm">Indlæser...</p>
      ) : (
        <div className="space-y-4">
          {CLOUD_PROVIDERS.map(provider => {
            const connected = integrations.filter(i => i.provider === provider.id);
            return (
              <div key={provider.id} className={`rounded-xl border p-5 ${TRE_KOLONNER ? 'bg-th-card border-th-border-light' : provider.color}`}>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    {TRE_KOLONNER ? (
                      // Hvid flade under logoet ogsaa i moerkt tema: logoerne er
                      // tegnet til lys baggrund, og Dropbox' blaa forsvinder paa moerk
                      <span className="w-10 h-10 rounded-lg bg-white border border-th-border-light flex items-center justify-center shrink-0">
                        <img src={provider.ikon} alt="" className="w-6 h-6 object-contain" />
                      </span>
                    ) : (
                      <span className="text-2xl">{provider.logo}</span>
                    )}
                    <div>
                      <p className="font-semibold text-th-text">{provider.label}</p>
                      {connected.length === 0 && (
                        <p className="text-xs text-th-text-muted mt-0.5">Ikke forbundet</p>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => connect(provider.id)}
                    className="bg-btn hover:bg-btn-hover text-btn-fg text-sm shrink-0">
                    + Forbind {provider.label}
                  </Button>
                </div>

                {connected.map(integration => (
                  <div key={integration.id} className="mt-4 pt-4 border-t border-th-border space-y-3">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${integration.enabled ? 'bg-emerald-500' : 'bg-th-text-muted'}`} />
                        <span className="text-sm text-th-text">{integration.account_email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleEnabled(integration)}
                          className="text-xs text-th-text-muted hover:text-th-text transition-colors border border-th-border rounded px-2 py-1">
                          {integration.enabled ? 'Deaktivér' : 'Aktivér'}
                        </button>
                        <button onClick={() => disconnect(integration.id)}
                          className="text-xs text-red-500 hover:text-red-400 transition-colors border border-red-500/30 rounded px-2 py-1">
                          Afkobl
                        </button>
                      </div>
                    </div>

                    {editingId === integration.id ? (
                      <div className="flex gap-2 items-center">
                        <input
                          value={folderPath}
                          onChange={e => setFolderPath(e.target.value)}
                          placeholder={`${BRAND.cloudFolder}/Sundahl ApS`}
                          className="flex-1 bg-th-body border border-th-border-light text-th-text placeholder:text-th-text-muted rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand-500"
                        />
                        <Button onClick={() => saveFolder(integration.id)} disabled={saving}
                          className="bg-btn hover:bg-btn-hover text-btn-fg text-sm">
                          {saving ? '...' : 'Gem'}
                        </Button>
                        <Button variant="ghost" onClick={() => setEditingId(null)}
                          className="text-th-text-muted text-sm">Annuller</Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-th-text-muted">Mappe:</span>
                        <span className="text-th-text font-mono text-xs bg-th-hover px-2 py-0.5 rounded">
                          {integration.folder_path || BRAND.cloudFolder}
                        </span>
                        <button onClick={() => { setEditingId(integration.id); setFolderPath(integration.folder_path || BRAND.cloudFolder); }}
                          className="text-th-text-muted hover:text-brand-400 transition-colors">
                          <Pencil size={13} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

// ─── API ──────────────────────────────────────────────────────────────────────

// Kodeblok med kopiknap. Bruges baade til enkeltlinjer og hele eksempler.
function Kodeblok({ children, kopi }) {
  const tekst = typeof children === 'string' ? children : String(children ?? '');
  return (
    <div className="relative group">
      <pre className="bg-th-hover border border-th-border rounded-lg p-4 pr-12 overflow-x-auto text-xs leading-relaxed text-th-text font-mono whitespace-pre">
        {tekst}
      </pre>
      {kopi !== false && (
        <button
          type="button"
          onClick={() => kopierTekst(tekst, 'Kopieret')}
          className="absolute top-2.5 right-2.5 p-1.5 rounded-md text-th-text-muted hover:text-th-text hover:bg-th-border transition-colors"
          title="Kopiér"
          aria-label="Kopiér kodeeksempel"
        >
          <Copy size={14} />
        </button>
      )}
    </div>
  );
}

// navigator.clipboard findes kun i sikker kontekst. Uden fallback ville
// kopiknappen vaere doed paa http under lokal udvikling.
async function kopierTekst(tekst, besked = 'Kopieret') {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(tekst);
    } else {
      const felt = document.createElement('textarea');
      felt.value = tekst;
      felt.setAttribute('readonly', '');
      felt.style.position = 'fixed';
      felt.style.opacity = '0';
      document.body.appendChild(felt);
      felt.select();
      document.execCommand('copy');
      document.body.removeChild(felt);
    }
    toast.success(besked);
  } catch {
    toast.error('Kunne ikke kopiere — markér teksten manuelt');
  }
}

function Endepunkt({ metode, sti, beskriv, children }) {
  const farve = metode === 'POST'
    ? 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-800 dark:text-emerald-400 border-emerald-400 dark:border-emerald-500/20'
    : 'bg-sky-100 dark:bg-sky-500/10 text-sky-800 dark:text-sky-400 border-sky-400 dark:border-sky-500/20';
  return (
    <div className="border border-th-border rounded-lg p-4">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Badge className={`${farve} font-mono text-[11px]`}>{metode}</Badge>
        <code className="text-sm text-th-text font-mono break-all">{sti}</code>
      </div>
      <p className="text-sm text-th-text-secondary mb-3">{beskriv}</p>
      {children}
    </div>
  );
}

// Her står kun dokumentationen. Selve nøglerne hører til selskaberne og styres
// under Indstillinger — én pr. selskab, så et system aldrig kan aflevere et
// dokument i et andet selskabs navn.
function ApiView({ user }) {
  const navigate = useNavigate();
  // Der er ingen vej til en rigtig nøgle herfra: klarteksten vises kun i det
  // øjeblik den laves. Eksemplerne bruger derfor en pladsholder.
  const eksempelNoegle = 'hp_DIN_NOEGLE';

  return (
    <div className="max-w-4xl mx-auto">
      {TRE_KOLONNER && (
        <button onClick={() => navigate('/dokumenter')}
          className="text-sm text-th-text-muted hover:text-th-text-secondary mb-4 inline-flex items-center gap-1">
          ← Tilbage til dokumenter
        </button>
      )}
      <div className="mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">API</h1>
        <p className="text-th-text-secondary">
          Lad et andet system aflevere dokumenter til underskrift — uden at nogen skal logge ind.
        </p>
      </div>


      <Card className="p-6 sm:p-8 bg-th-sidebar border-th-border mb-6">
        <h2 className="text-lg font-heading font-bold text-th-text flex items-center gap-2 mb-2">
          <Key size={18} className="text-brand-500" />
          Hvor finder jeg nøglen?
        </h2>
        <p className="text-sm text-th-text-secondary mb-4">
          Hvert selskab har sin egen nøgle. Den laves under{' '}
          <strong className="text-th-text">Indstillinger</strong>, hvor du vælger selskabet —
          samme sted som visningsnavn og logo. Dokumenter afleveret med nøglen sendes i det
          selskabs navn, så et system aldrig kan komme til at sende i et andet selskabs.
        </p>
        <Button onClick={() => navigate('/dokumenter/indstillinger')}>
          Gå til indstillinger
          <ArrowRight size={15} className="ml-1.5" />
        </Button>
      </Card>

      {/* ── Dokumentation ──────────────────────────────────────────────────── */}
      <Card className="p-6 sm:p-8 bg-th-sidebar border-th-border">
        <h2 className="text-lg font-heading font-bold text-th-text flex items-center gap-2 mb-6">
          <BookOpen size={18} className="text-brand-500" />
          Dokumentation
        </h2>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-2">Adgang</h3>
          <p className="text-sm text-th-text-secondary mb-3">
            Send nøglen med i hoveddet <code className="font-mono text-th-text">X-Api-Key</code> på hvert kald.
            Kaldene sker som din konto <strong className="text-th-text">{user?.email}</strong> — dokumenterne
            lander præcis hvor de ville, hvis du selv havde uploadet dem.
          </p>
          <Kodeblok>{`X-Api-Key: ${eksempelNoegle}`}</Kodeblok>
        </section>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-2">Hvad en nøgle må</h3>
          <p className="text-sm text-th-text-secondary mb-3">
            Nøglen åbner kun de tre kald der udgør en aflevering, og kun for det selskab den
            hører til. Alt andet kræver stadig login. Slipper nøglen ud, kan den sende et dokument
            til underskrift i det ene selskabs navn — ikke læse din indbakke, flytte selskaber,
            slette noget eller lave flere nøgler.
          </p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="border border-th-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-400 font-semibold mb-2">Kan</p>
              <ul className="text-sm text-th-text-secondary space-y-1">
                <li>Uploade et dokument</li>
                <li>Tilføje underskrivere</li>
                <li>Sende til underskrift</li>
              </ul>
            </div>
            <div className="border border-th-border rounded-lg p-4">
              <p className="text-xs uppercase tracking-wider text-red-700 dark:text-red-400 font-semibold mb-2">Kan ikke</p>
              <ul className="text-sm text-th-text-secondary space-y-1">
                <li>Læse indbakke eller dokumenter</li>
                <li>Slette eller annullere noget</li>
                <li>Røre selskaber og modtagere</li>
                <li>Oprette eller forlænge nøgler</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-3">Endepunkter</h3>
          <div className="space-y-3">
            <Endepunkt
              metode="POST"
              sti={`${BASE_URL}/api/documents`}
              beskriv="Upload dokumentet. Sendes som multipart/form-data. Svarer med dokumentets id."
            >
              <Kodeblok kopi={false}>{`fil           PDF'en (påkrævet, max 50 MB)
title         Titel, max 200 tegn (påkrævet)
purpose       "sign" (standard) eller "archive"
cvr           8 cifre, valgfrit
kraev_mitid   "true" hvis der skal underskrives med MitID
retur_url     Landingsside efter underskrift, fuld https-adresse, valgfrit
retur_url_signed  Vi POSTer det underskrevne dokument hertil, valgfrit`}</Kodeblok>
            </Endepunkt>

            <Endepunkt
              metode="POST"
              sti={`${BASE_URL}/api/documents/{id}/signers`}
              beskriv="Tilføj én underskriver. Kald én gang pr. person. Både e-mail og mobilnummer er påkrævet — underskriveren bekræfter begge dele."
            >
              <Kodeblok kopi={false}>{`{
  "name":  "Jens Jensen",        // påkrævet, max 120 tegn
  "email": "jens@eksempel.dk",   // påkrævet
  "phone": "20 31 20 00",        // påkrævet, dansk nummer
  "cvr":   "12345678"            // valgfrit, slås op automatisk
}`}</Kodeblok>
            </Endepunkt>

            <Endepunkt
              metode="POST"
              sti={`${BASE_URL}/api/documents/{id}/send`}
              beskriv="Sender underskriftslink på mail til alle der endnu ikke har svaret. Tom krop. Svarer med hvor mange mails der gik af sted."
            />

            <Endepunkt
              metode="GET"
              sti={`${BASE_URL}/api/documents/{id}/signed-file`}
              beskriv="Hent det underskrevne dokument med dokumentnøgle og underskriftsside. Svarer 409, hvis ikke alle har skrevet under endnu."
            />

            <Endepunkt
              metode="POST"
              sti={`${BASE_URL}/api/documents/{id}/cancel`}
              beskriv="Annullér dokumentet — alle udestående underskriftslinks bliver ugyldige. Tom krop. Brug den til at rydde op, hvis dit eget flow fejler efter uploaden."
            />
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-2">Komplet eksempel</h3>
          <p className="text-sm text-th-text-secondary mb-3">
            Fra PDF til udsendt underskriftslink i tre kald.
          </p>
          <Kodeblok>{`# 1 — upload dokumentet
DOC=$(curl -s -X POST ${BASE_URL}/api/documents \\
  -H "X-Api-Key: ${eksempelNoegle}" \\
  -F "title=Samarbejdsaftale 2026" \\
  -F "purpose=sign" \\
  -F "retur_url=https://ditfirma.dk/tak" \\
  -F "fil=@aftale.pdf" \\
  | python3 -c "import sys,json; print(json.load(sys.stdin)['id'])")

# 2 — tilføj en underskriver (gentag pr. person)
curl -s -X POST ${BASE_URL}/api/documents/$DOC/signers \\
  -H "X-Api-Key: ${eksempelNoegle}" \\
  -H "Content-Type: application/json" \\
  -d '{"name":"Jens Jensen","email":"jens@eksempel.dk","phone":"20312000"}'

# 3 — send til underskrift
curl -s -X POST ${BASE_URL}/api/documents/$DOC/send \\
  -H "X-Api-Key: ${eksempelNoegle}"`}</Kodeblok>
        </section>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-2">Retur-kald når alle har underskrevet</h3>
          <p className="text-sm text-th-text-secondary mb-3">
            Har dokumentet et <code className="font-mono text-th-text">retur_url_signed</code>, sender vi
            det underskrevne dokument til den adresse, så snart sidste underskrift er faldet.
            Du behøver ikke spørge efter det.
          </p>
          <p className="text-sm text-th-text-secondary mb-3">
            Sender kaldet ingen adresse med, bruger vi selskabets faste webhook fra{' '}
            <strong className="text-th-text">Indstillinger</strong>. Så lander også de kontrakter,
            der bliver underskrevet gennem brugerfladen, i dit system — uden at nogen skal huske
            at sætte et felt. Signaturen laves da med selskabets egen hemmelighed, som du finder
            samme sted; de dokumenter, der selv har sendt en adresse med, signeres fortsat med
            den fælles nøgle.
          </p>
          <Kodeblok kopi={false}>{`POST <retur_url_signed>
X-Signature: base64(HMAC-SHA256(rå krop, delt hemmelighed))
Content-Type: application/json

{
  "document_id":  "aedcb4c2-...",
  "signed_at":    "2026-08-30T14:22:00Z",
  "pdf_base64":   "<den stemplede PDF>",
  "pdf_filename": "Aftale-underskrevet.pdf",
  "signers": [{"name": "...", "email": "...", "signed_at": "..."}]
}`}</Kodeblok>
          <p className="text-sm text-th-text-secondary mt-3">
            Er PDF'en større end 8 MB, sendes <code className="font-mono text-th-text">pdf_url</code> i
            stedet for <code className="font-mono text-th-text">pdf_base64</code>. Adressen kræver din
            API-nøgle. Signaturen er HMAC-SHA256 over den <strong>rå</strong> krop — genserialisér den
            ikke før kontrollen, da én ændret feltrækkefølge giver en anden signatur.
          </p>
          <p className="text-sm text-th-text-secondary mt-3">
            Svarer dit endepunkt ikke 2xx, prøver vi igen efter 1, 5 og 15 minutter, derefter 1 og 4 timer
            — seks forsøg i alt. Dit endepunkt bør derfor tåle at få samme dokument mere end én gang.
          </p>
        </section>

        <section className="mb-8">
          <h3 className="text-sm font-semibold text-th-text mb-2">Fejl</h3>
          <div className="border border-th-border rounded-lg divide-y divide-th-border">
            {[
              ['401', 'Nøglen mangler, er forkert eller er tilbagekaldt'],
              ['400', 'Noget i kaldet er ugyldigt — teksten i "detail" siger hvad'],
              ['404', 'Dokumentet findes ikke, eller det tilhører en anden konto'],
              ['409', 'Dokumentet er allerede underskrevet eller annulleret'],
              ['413', 'Filen er større end 50 MB'],
            ].map(([kode, tekst]) => (
              <div key={kode} className="flex gap-4 px-4 py-2.5 text-sm">
                <code className="font-mono text-th-text w-10 shrink-0">{kode}</code>
                <span className="text-th-text-secondary">{tekst}</span>
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3 className="text-sm font-semibold text-th-text mb-2">Opbevar nøglen forsvarligt</h3>
          <ul className="text-sm text-th-text-secondary space-y-1.5 list-disc pl-5">
            <li>Læg den i en miljøvariabel eller et hemmelighedslager — aldrig i kildekode eller et versionsstyret repo.</li>
            <li>Nøglen hører til ét selskab. Skal et system aflevere for to selskaber, bruger det to nøgler.</li>
            <li>Vi gemmer kun et hash af nøglen, så vi kan ikke vise den igen. Er den væk, så lav en ny under Indstillinger.</li>
            <li>Er en nøgle sluppet ud, så tilbagekald den under Indstillinger. Det virker med det samme.</li>
          </ul>
        </section>
      </Card>
    </div>
  );
}

// ─── Admin View ───────────────────────────────────────────────────────────────

function AdminView() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const [search, setSearch] = useState(() => new URLSearchParams(location.search).get('q') || '');

  const deleteUser = async (u) => {
    if (!window.confirm(`Slet bruger ${u.email}? Dette sletter også alle selskaber og modtagere tilknyttet brugeren.`)) return;
    try {
      await axios.delete(`${API}/admin/users/${u.id}`);
      setUsers(prev => prev.filter(x => x.id !== u.id));
      toast.success('Bruger slettet');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fejl'); }
  };

  // Navngivet, saa den kan kaldes igen efter en aendring — fx naar et selskab
  // har skiftet ejer og skal staa under en anden bruger.
  const hentBrugere = React.useCallback(() => {
    axios.get(`${API}/admin/users`)
      .then(r => setUsers(r.data))
      .catch(() => toast.error('Ikke adgang'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { hentBrugere(); }, [hentBrugere]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    if ((u.email || '').toLowerCase().includes(q)) return true;
    if ((u.contact_name || '').toLowerCase().includes(q)) return true;
    if ((u.company_name || '').toLowerCase().includes(q)) return true;
    const matchSelskab = c =>
      (c.name || '').toLowerCase().includes(q) || (c.cvr || '').includes(q);
    return (u.companies || []).some(matchSelskab)
      || (u.shared_companies || []).some(matchSelskab);
  });

  const totalCompanies = users.reduce((sum, u) => sum + (u.companies_count || 0), 0);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Abonnenter</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">{users.length} brugere · {totalCompanies} selskaber</p>
        </div>
        <input
          type="text"
          placeholder="Søg email, navn, CVR eller selskab..."
          value={search}
          onChange={e => { setSearch(e.target.value); navigate(`/admin?q=${encodeURIComponent(e.target.value)}`, { replace: true }); }}
          className="bg-th-hover border border-th-border-light text-th-text-secondary placeholder-th-text-muted rounded-lg px-4 py-2 text-sm w-full sm:w-72 focus:outline-none focus:border-th-border-light"
        />
      </div>

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : (
        <div className="space-y-3">
          {filtered.map(u => (
            <AdminUserRow
              key={u.id}
              u={u}
              onDelete={deleteUser}
              onTierChange={(id, tier) =>
                setUsers(prev => prev.map(x => x.id === id ? { ...x, subscription_tier: tier } : x))
              }
              onCompanyDelete={(uid, cid) =>
                setUsers(prev => prev.map(x => x.id === uid
                  ? { ...x, companies: (x.companies || []).filter(c => c.id !== cid), companies_count: Math.max(0, (x.companies_count || 0) - 1) }
                  : x
                ))
              }
              onFlyttet={hentBrugere}
              brugere={users}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AdminUserRow({ u, onDelete, onTierChange, onCompanyDelete, onFlyttet, brugere }) {
  const [savingTier, setSavingTier] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const handleTierChange = async (e) => {
    const newTier = e.target.value;
    setSavingTier(true);
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, { subscription_tier: newTier });
      onTierChange(u.id, newTier);
      toast.success('Plan opdateret');
    } catch {
      toast.error('Kunne ikke opdatere plan');
    } finally {
      setSavingTier(false);
    }
  };

  const deleteCompany = async (c) => {
    if (!window.confirm(`Slet selskab ${c.name}?`)) return;
    try {
      await axios.delete(`${API}/companies/${c.id}`);
      onCompanyDelete(u.id, c.id);
      toast.success('Selskab slettet');
    } catch {
      toast.error('Kunne ikke slette selskab');
    }
  };

  const tierInfo = getTierLabel(u.subscription_tier);
  const companies = u.companies || [];
  const delte = u.shared_companies || [];

  return (
    <Card className="bg-th-input border-th-border overflow-hidden">
      <div className="p-5">
        <div className="flex items-center justify-between gap-4">
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-3 min-w-0 flex-1 text-left hover:opacity-80 transition-opacity"
            title={expanded ? 'Skjul selskaber' : 'Vis selskaber'}
          >
            {expanded ? <ChevronDown size={18} className="text-th-text-muted shrink-0" /> : <ChevronRight size={18} className="text-th-text-muted shrink-0" />}
            <div className="min-w-0">
              <p className="text-th-text font-medium truncate">{u.email}</p>
              <p className="text-sm text-th-text-secondary truncate">{u.contact_name || '—'}</p>
              <p className="text-xs text-th-text-muted mt-0.5">Oprettet: {formatDate(u.created_at)}</p>
            </div>
          </button>
          <div className="shrink-0 flex items-center gap-2 flex-wrap justify-end">
            <Badge className="bg-brand-500/10 text-brand-400 border-brand-500/20 gap-1">
              <Building size={12} /> {u.companies_count || 0}
            </Badge>
            {(u.ekstra_selskaber || 0) > 0 && (
              <Badge
                className="bg-amber-500/10 text-amber-400 border-amber-500/20 gap-1"
                title={`${u.ekstra_selskaber} selskab(er) ud over de 10 der er med i Pro — faktureres manuelt`}
              >
                +{u.ekstra_selskaber} = {u.ekstra_maanedspris},-/md.
              </Badge>
            )}
            {(u.shared_companies_count || 0) > 0 && (
              <Badge className="bg-sky-500/10 text-sky-400 border-sky-500/20 gap-1" title="Selskaber brugeren ser gennem andre">
                <Share2 size={12} /> {u.shared_companies_count}
              </Badge>
            )}
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 gap-1">
              <Users size={12} /> {u.recipients_count || 0}
            </Badge>
            {u.is_admin && <Badge className="bg-highlight-500/10 text-highlight-400 border-highlight-500/20">Admin</Badge>}
            <select
              value={u.subscription_tier || 'solo'}
              onChange={handleTierChange}
              disabled={savingTier}
              className={`text-xs font-medium text-white rounded px-2 py-1 border-0 focus:outline-none cursor-pointer ${tierInfo.color}`}
            >
              <option value="free">Gratis</option>
              <option value="solo">Solo</option>
              <option value="holding">Holding</option>
              <option value="pro">Pro</option>
            </select>
            <button onClick={() => onDelete(u)} className="ml-1 text-th-text-faint hover:text-red-400 transition-colors" title="Slet bruger">
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="border-t border-th-border bg-th-body/30 px-5 py-4 space-y-5">
          {companies.length === 0 && delte.length === 0 ? (
            <p className="text-sm text-th-text-muted text-center py-3">Ingen selskaber</p>
          ) : (
            <>
              {companies.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-th-text-muted font-semibold mb-2">Egne selskaber ({companies.length})</p>
                  {companies.map(c => (
                    <AdminCompanyRow
                      key={c.id}
                      c={c}
                      onDelete={() => deleteCompany(c)}
                      onKeyUpdate={(newKey) => {
                        c.dp_api_key = newKey || undefined;
                      }}
                      ejerEmail={u.email}
                      onFlyttet={onFlyttet}
                      brugere={brugere}
                    />
                  ))}
                </div>
              )}
              {delte.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs uppercase tracking-wider text-th-text-muted font-semibold mb-2">
                    Adgang gennem andre ({delte.length})
                  </p>
                  {delte.map(c => (
                    <div key={c.id} className="bg-th-hover/40 border border-th-border rounded-lg px-3 py-2 flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Share2 size={14} className="text-sky-400 shrink-0" />
                        <span className="text-sm font-medium text-th-text-secondary truncate">{c.name}</span>
                        <span className="font-mono text-th-text-muted text-xs">{c.cvr}</span>
                      </div>
                      <div className="shrink-0 flex items-center gap-2">
                        {c.role === 'admin' && (
                          <Badge className="bg-indigo-500/10 text-indigo-400 border-indigo-500/20 text-[10px] px-1.5 py-0">Admin</Badge>
                        )}
                        {c.via_all && (
                          <Badge
                            className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-[10px] px-1.5 py-0"
                            title="Adgang til alle ejerens selskaber — også dem der oprettes fremover"
                          >Alle</Badge>
                        )}
                        <span className="text-xs text-th-text-muted truncate max-w-[14rem]" title={c.owner_email}>
                          via {c.owner_name || c.owner_email}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ─── Admin Inbox ─────────────────────────────────────────────────────────────

// Vaelg ny ejer ved at soege. En prompt kraevede, at man kendte adressen
// udenad — her skrives et navn, og listen viser hvem det er, sammen med hvad
// de allerede ejer, saa to personer med samme fornavn kan skelnes.
function FlytEjerDialog({ selskab, ejerEmail, brugere, onLuk, onFlyttet }) {
  const [soeg, setSoeg] = React.useState('');
  const [sender, setSender] = React.useState(false);

  const q = soeg.trim().toLowerCase();
  // Der soeges ogsaa i selskabsnavn og CVR: kender man kun det selskab,
  // personen i forvejen har, kan man finde hende paa det.
  const traf = !q ? [] : brugere.filter(u => {
    const felter = [u.email, u.contact_name, u.company_name,
                    ...(u.companies || []).flatMap(c => [c.name, c.cvr])];
    return felter.some(v => (v || '').toLowerCase().includes(q));
  }).slice(0, 8);

  const flyt = async (modtager) => {
    if (!window.confirm(
      `Flyt ${selskab.name} (CVR ${selskab.cvr}) til ${modtager.email}?\n\n` +
      'Selskabets post og videresendelsesregler flytter med.'
    )) return;
    setSender(true);
    try {
      const r = await axios.post(`${API}/admin/companies/${selskab.id}/owner`, { email: modtager.email });
      toast.success(
        `Flyttet til ${r.data.til_email} — ${r.data.flyttet_post} beskeder og ${r.data.flyttede_regler} regler fulgte med`,
        { duration: 8000 });
      onFlyttet && onFlyttet();
      onLuk();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke flytte selskabet');
    } finally { setSender(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 pt-24">
      <div className="absolute inset-0 bg-black/50" onClick={onLuk} />
      <div className="relative z-10 w-full max-w-lg bg-th-sidebar border border-th-border rounded-xl shadow-2xl p-6">
        <h3 className="text-lg font-semibold text-th-text">Flyt {selskab.name}</h3>
        <p className="text-sm text-th-text-secondary mt-1">
          CVR {selskab.cvr} · ligger nu hos {ejerEmail || 'ukendt'}
        </p>
        <p className="text-xs text-th-text-muted mt-2">
          Selskabet flytter med sin post og sine videresendelsesregler.
        </p>

        <input
          autoFocus value={soeg} onChange={e => setSoeg(e.target.value)}
          placeholder="Søg på navn, e-mail, selskab eller CVR…"
          className="mt-4 w-full h-11 px-3 rounded-lg bg-th-input border border-th-border text-th-text placeholder:text-th-text-muted"
        />

        <div className="mt-3 max-h-72 overflow-y-auto space-y-1">
          {!q ? (
            <p className="text-sm text-th-text-muted py-3 text-center">Skriv for at søge efter den nye ejer</p>
          ) : traf.length === 0 ? (
            <p className="text-sm text-th-text-muted py-3 text-center">Ingen brugere matcher “{soeg}”</p>
          ) : traf.map(u => {
            const erNuvaerende = (u.email || '').toLowerCase() === (ejerEmail || '').toLowerCase();
            return (
              <button key={u.id} disabled={sender || erNuvaerende} onClick={() => flyt(u)}
                className="w-full text-left px-3 py-2 rounded-lg border border-th-border hover:border-brand-400 hover:bg-th-hover transition-colors disabled:opacity-40 disabled:hover:border-th-border">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm text-th-text truncate">{u.contact_name || u.email}</p>
                    <p className="text-xs text-th-text-muted truncate">{u.email}</p>
                  </div>
                  <span className="text-xs text-th-text-muted shrink-0">
                    {erNuvaerende ? 'nuværende ejer' : `${(u.companies || []).length} selskaber`}
                  </span>
                </div>
                {(u.companies || []).length > 0 && !erNuvaerende && (
                  <p className="text-[11px] text-th-text-faint truncate mt-0.5">
                    {(u.companies || []).slice(0, 3).map(c => c.name).join(' · ')}
                  </p>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onLuk} className="text-sm text-th-text-muted hover:text-th-text-secondary">Annuller</button>
        </div>
      </div>
    </div>
  );
}

function AdminCompanyRow({ c, onDelete, onKeyUpdate, ejerEmail, onFlyttet, brugere }) {
  const { user } = useAuth();
  // Kun brandets egen adminkonto. Backenden haandhaever det samme — det her
  // skjuler blot en knap, der alligevel ville blive afvist.
  const maaFlytte = user?.is_admin
    && (user?.email || '').toLowerCase() === (BRAND.adminEmail || '').toLowerCase();

  const [flytAaben, setFlytAaben] = useState(false);

  const [editing, setEditing] = useState(false);
  const [keyInput, setKeyInput] = useState(c.dp_api_key || '');
  const [saving, setSaving] = useState(false);
  const [hasKey, setHasKey] = useState(!!c.dp_api_key);

  const saveKey = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/companies/${c.id}/dp-api-key`, { dp_api_key: keyInput });
      toast.success(keyInput ? 'API-key gemt' : 'API-key fjernet');
      setHasKey(!!keyInput);
      onKeyUpdate(keyInput);
      setEditing(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-th-hover/40 border border-th-border rounded-lg">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <Building size={14} className="text-th-text-muted shrink-0" />
          <span className="text-sm font-medium text-th-text-secondary truncate">{c.name}</span>
          <span className="font-mono text-th-text-muted text-xs">{c.cvr}</span>
          {hasKey ? (
            <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px] px-1.5 py-0">DP Key</Badge>
          ) : (
            <Badge className="bg-slate-500/10 text-slate-400 border-slate-500/20 text-[10px] px-1.5 py-0">Ingen DP Key</Badge>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {c.created_at && <span className="text-xs text-th-text-faint">{formatDate(c.created_at)}</span>}
          <button
            onClick={() => { setEditing(v => !v); setKeyInput(c.dp_api_key || ''); }}
            className="text-th-text-faint hover:text-brand-400 transition-colors"
            title="Digital Post API-key"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          </button>
          {maaFlytte && (
            <button onClick={() => setFlytAaben(true)}
              className="text-[11px] px-2 py-0.5 rounded border border-th-border text-th-text-muted hover:border-brand-400 hover:text-brand-400 transition-colors"
              title="Flyt selskabet til en anden ejer">
              Flyt ejer
            </button>
          )}
          <button onClick={onDelete} className="text-th-text-faint hover:text-red-400 transition-colors" title="Slet selskab">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {flytAaben && (
        <FlytEjerDialog selskab={c} ejerEmail={ejerEmail} brugere={brugere || []}
          onLuk={() => setFlytAaben(false)} onFlyttet={onFlyttet} />
      )}
      {editing && (
        <div className="border-t border-th-border px-3 py-3 space-y-2">
          <p className="text-xs text-th-text-muted">Base64-encoded key fra Digital Post (systemID:token). Bruges til digital kvittering overfor Digital Post.</p>
          <div className="flex gap-2">
            <input
              type="text"
              value={keyInput}
              onChange={e => setKeyInput(e.target.value)}
              placeholder="NmY5NjZi..."
              className="flex-1 bg-th-hover border border-th-border-light text-th-text-secondary placeholder-th-text-muted rounded-lg px-3 py-1.5 text-xs font-mono focus:outline-none focus:border-brand-400"
            />
            <Button onClick={saveKey} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white text-xs shrink-0 h-auto px-3 py-1.5">
              {saving ? 'Gemmer...' : 'Gem'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Admin: alle selskaber og Digital Post-opsaetningen ─────────────────────
// Arbejdslisten for support: hvem venter paa en DP-noegle, og hvad skal der
// bruges for at saette dem op. Raekkefoelgen kommer fra serveren.

const ADMIN_STATUS = {
  afvist: { tekst: 'Adgang afvist', variant: 'fejl' },
  venter: { tekst: 'Venter på opsætning', variant: 'advarsel' },
  mangler_noegle: { tekst: 'Mangler DP-nøgle', variant: 'lys' },
  opsat: { tekst: 'Opsat', variant: 'faerdig' },
};

// Etiket og vaerdi, der kan kopieres med ét klik
function AdminFelt({ label, vaerdi, kode, kopier = true }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-th-border-light last:border-0">
      <span className="text-xs text-th-text-muted shrink-0 w-36 pt-0.5">{label}</span>
      <span className="flex-1 min-w-0 flex items-start justify-end gap-2 text-right">
        <span className={`text-sm text-th-text break-all ${kode ? 'font-mono text-[13px]' : ''}`}>{vaerdi || '—'}</span>
        {kopier && vaerdi && <span className="pt-0.5"><CopyButton value={String(vaerdi)} /></span>}
      </span>
    </div>
  );
}

function AdminSelskabRaekke({ c, aaben, onSkift, onGemt }) {
  const navigate = useNavigate();
  const [noegle, setNoegle] = useState('');
  const [gemmer, setGemmer] = useState(false);
  const status = ADMIN_STATUS[c.status] || ADMIN_STATUS.mangler_noegle;
  const dato = c.dp_tilmeldt_at || c.created_at;

  const gemNoegle = async () => {
    setGemmer(true);
    try {
      await axios.patch(`${API}/admin/companies/${c.id}/dp-api-key`, { dp_api_key: noegle.trim() });
      toast.success(noegle.trim() ? `DP-nøgle gemt for ${c.name}` : 'DP-nøgle fjernet');
      setNoegle('');
      onGemt();
    } catch (e) {
      // Serveren forklarer, hvad der er galt med noeglen — vis det
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme', { duration: 8000 });
    } finally { setGemmer(false); }
  };

  return (
    <div className="bg-th-card border border-th-border-light rounded-xl">
      <button onClick={onSkift} aria-expanded={aaben} className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <Building2 size={18} className="shrink-0 text-th-text" />
        <span className="flex-1 min-w-0 flex items-baseline gap-2">
          <span className="text-sm font-medium text-th-text truncate">{c.name}</span>
          <span className="text-xs text-th-text-muted shrink-0">CVR {formatCvr(c.cvr)}</span>
        </span>
        <span className="hidden md:block text-xs text-th-text-muted shrink-0">
          {c.dp_tilmeldt_at ? 'Tilmeldt ' : 'Oprettet '}{formatDate(dato)}
        </span>
        <DbTag variant={status.variant}>{status.tekst}</DbTag>
        <ChevronDown size={16} className={`shrink-0 text-th-text-muted transition-transform ${aaben ? 'rotate-180' : ''}`} />
      </button>
      {aaben && (
        <div className="border-t border-th-border-light px-4 py-4 grid lg:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-th-text-muted mb-1">Til Digital Post Admin</p>
            <AdminFelt label="Selskab" vaerdi={c.name} />
            <AdminFelt label="CVR" vaerdi={c.cvr} kode />
            <AdminFelt label="Endpoint URL" vaerdi={c.endpoint_url} kode />
            <AdminFelt label="Bilagsadresse" vaerdi={c.bilagsadresse} kode />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-th-text-muted mb-1">Kunden</p>
            <AdminFelt label="Ejer" vaerdi={c.ejer?.navn} kopier={false} />
            <AdminFelt label="E-mail" vaerdi={c.ejer?.email} />
            <AdminFelt label="Telefon" vaerdi={c.ejer?.telefon} />
            <AdminFelt label="Digital Post-guide" kopier={false}
              vaerdi={c.dp_tilmeldt_at ? `${formatDate(c.dp_tilmeldt_at)}${c.dp_tilmeldt_af ? ` · ${c.dp_tilmeldt_af}` : ''}` : 'Ikke gennemført'} />
            <AdminFelt label="Seneste post" kopier={false}
              vaerdi={[c.sidst_memo && `Digital Post ${formatDate(c.sidst_memo)}`, c.sidst_eboks && `e-Boks ${formatDate(c.sidst_eboks)}`].filter(Boolean).join(' · ') || 'Ingen endnu'} />
          </div>

          <div className="lg:col-span-2 rounded-lg bg-th-inset p-4 flex flex-col gap-3">
            {c.dp_adgang_fejl && (
              <p className="text-sm text-red-600">Digital Post afviste adgangen{c.dp_adgang_fejl.besked ? `: ${c.dp_adgang_fejl.besked}` : ''}.</p>
            )}
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Lock size={14} className="text-th-text-muted" />
              <span className="font-medium text-th-text">DP-nøgle</span>
              {c.har_noegle
                ? <DbTag variant="faerdig">Sat · slutter på {c.noegle_slutter}</DbTag>
                : <DbTag variant="lys">Ingen</DbTag>}
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={noegle} onChange={e => setNoegle(e.target.value)} spellCheck={false} autoComplete="off"
                placeholder={c.har_noegle ? 'Indsæt en ny nøgle for at erstatte den' : 'Indsæt API-nøglen fra Digital Post Admin — “Basic ” foran fjernes selv'}
                className="flex-1 min-w-0 h-9 rounded-lg border border-th-border bg-th-card px-3 text-sm font-mono text-th-text placeholder:font-sans placeholder:text-th-text-muted focus:outline-none focus:border-db-route" />
              <button onClick={gemNoegle} disabled={gemmer || !noegle.trim()} className={DB_KNAP_PRIMAER}>
                {gemmer ? 'Gemmer…' : 'Gem nøgle'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {c.dp_klar_link && (
                <a href={c.dp_klar_link} target="_blank" rel="noopener noreferrer" className={DB_KNAP}>
                  <Send size={14} /> Giv kunden besked
                </a>
              )}
              {c.kunde_besked_at && <DbTag variant="faerdig">Kunden fik besked {formatDate(c.kunde_besked_at)}</DbTag>}
              <button onClick={() => navigate(`/admin?q=${c.cvr || ''}`)} className={DB_KNAP}>
                <Users size={14} /> Se abonnenten
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Mange DP-noegler paa én gang: indsaet eller vaelg JSON'en fra opsaetningen,
// kontrollér, og gem. Kontrollen koerer paa serveren — den samme, som koerer
// ved gem — saa det, man ser, er det, der sker.
const DP_IMPORT_STATUS = {
  ny: { tekst: 'Ny nøgle', variant: 'faerdig' },
  erstattes: { tekst: 'Erstatter eksisterende', variant: 'advarsel' },
  uaendret: { tekst: 'Uændret', variant: 'lys' },
  fejl: { tekst: 'Fejl', variant: 'fejl' },
};

function DpNoegleImport({ onGemt }) {
  const [aaben, setAaben] = useState(false);
  const [tekst, setTekst] = useState('');
  const [resultat, setResultat] = useState(null);
  const [travl, setTravl] = useState(false);
  const filRef = useRef(null);

  const raekker = () => {
    const data = JSON.parse(tekst);
    const liste = Array.isArray(data) ? data : (data?.raekker || data?.selskaber);
    if (!Array.isArray(liste) || liste.length === 0) throw new Error('Forventede en liste med selskaber');
    return liste.map(x => ({
      selskab: x.selskab ?? x.name ?? null, cvr: x.cvr != null ? String(x.cvr) : null,
      inboxId: x.inboxId ?? x.inbox_id ?? null, endpoint: x.endpoint ?? null,
      dpSystemId: x.dpSystemId ?? null, dpApiKey: x.dpApiKey ?? x.dp_api_key ?? '',
    }));
  };

  const send = async (dryRun) => {
    let liste;
    try { liste = raekker(); } catch (e) {
      toast.error(e instanceof SyntaxError ? 'Det er ikke gyldig JSON' : e.message);
      return;
    }
    setTravl(true);
    try {
      const r = await axios.post(`${API}/admin/companies/dp-api-keys`, { raekker: liste, dry_run: dryRun });
      if (dryRun) { setResultat(r.data.raekker); return; }
      const gemt = r.data.raekker.filter(x => x.gemt).length;
      toast.success(`${gemt} DP-nøgle${gemt === 1 ? '' : 'r'} gemt`);
      // Feltet indeholder noegler — det skal ikke blive staaende i browseren
      setTekst(''); setResultat(null); setAaben(false);
      onGemt();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke kontrolleres', { duration: 8000 });
    } finally { setTravl(false); }
  };

  const laesFil = (e) => {
    const fil = e.target.files?.[0];
    if (!fil) return;
    fil.text().then(t => { setTekst(t); setResultat(null); });
    e.target.value = '';
  };

  const kanGemmes = resultat ? resultat.filter(x => x.status === 'ny' || x.status === 'erstattes').length : 0;

  return (
    <div className="rounded-xl bg-th-card border border-th-border-light">
      <button onClick={() => setAaben(v => !v)} aria-expanded={aaben}
        className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <Upload size={18} className="shrink-0 text-th-text" />
        <span className="flex-1 text-sm font-medium text-th-text">Indlæs DP-nøgler fra JSON <span className="font-normal text-th-text-muted">· mange selskaber på én gang</span></span>
        <ChevronDown size={16} className={`shrink-0 text-th-text-muted transition-transform ${aaben ? 'rotate-180' : ''}`} />
      </button>
      {aaben && (
        <div className="border-t border-th-border-light px-4 py-4 flex flex-col gap-3">
          <p className="text-xs text-th-text-muted">
            En liste med <code>inboxId</code> (eller <code>cvr</code>) og <code>dpApiKey</code> pr. selskab. <code>dpSystemId</code> kontrolleres mod nøglen, hvis det står der. “Basic ” foran fjernes.
          </p>
          <textarea value={tekst} onChange={e => { setTekst(e.target.value); setResultat(null); }}
            rows={8} spellCheck={false} autoComplete="off"
            placeholder={'[\n  { "cvr": "12345678", "inboxId": "…", "dpSystemId": "…", "dpApiKey": "Basic …" }\n]'}
            className="w-full rounded-lg border border-th-border bg-th-card px-3 py-2 text-xs font-mono text-th-text placeholder:text-th-text-muted focus:outline-none focus:border-db-route" />
          <div className="flex flex-wrap items-center gap-2">
            <input ref={filRef} type="file" accept=".json,application/json" onChange={laesFil} className="hidden" />
            <button onClick={() => filRef.current?.click()} className={DB_KNAP}><Upload size={14} /> Vælg fil</button>
            <button onClick={() => send(true)} disabled={travl || !tekst.trim()} className={DB_KNAP}>
              {travl && !resultat ? 'Kontrollerer…' : 'Kontrollér'}
            </button>
            {resultat && (
              <button onClick={() => send(false)} disabled={travl || kanGemmes === 0} className={DB_KNAP_PRIMAER}>
                {travl ? 'Gemmer…' : `Gem ${kanGemmes} nøgle${kanGemmes === 1 ? '' : 'r'}`}
              </button>
            )}
          </div>
          {resultat && (
            <div className="rounded-lg border border-th-border-light divide-y divide-th-border-light">
              {resultat.map(x => {
                const st = DP_IMPORT_STATUS[x.status] || DP_IMPORT_STATUS.fejl;
                return (
                  <div key={x.raekke} className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm">
                    <span className="w-6 text-xs text-th-text-muted">{x.raekke}</span>
                    <span className="flex-1 min-w-0 truncate text-th-text">{x.selskab || '—'} <span className="text-xs text-th-text-muted">{x.cvr ? `CVR ${formatCvr(x.cvr)}` : ''}</span></span>
                    {x.noegle_slutter && <span className="text-xs text-th-text-muted font-mono">…{x.noegle_slutter}</span>}
                    <DbTag variant={st.variant}>{st.tekst}</DbTag>
                    {x.besked && <span className="basis-full pl-9 text-xs text-red-600">{x.besked}</span>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AdminSelskaberView() {
  const [data, setData] = useState(null);
  // Alle er standard: serveren har allerede lagt dem, der mangler noget, oeverst
  const [filter, setFilter] = useState('alle');
  const [soeg, setSoeg] = useState('');
  // ?selskab=<id> — linket fra onboardingopgaven i Slack. Kunden foldes ud og
  // rulles frem, saa medarbejderen lander direkte paa den, der skal saettes op.
  const [searchParams] = useSearchParams();
  const linketSelskab = searchParams.get('selskab');
  const [aaben, setAaben] = useState(linketSelskab);
  useEffect(() => {
    if (!data || !linketSelskab) return;
    document.getElementById(`selskab-${linketSelskab}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [data, linketSelskab]);
  const [visVaerdier, setVisVaerdier] = useState(false);

  const hent = React.useCallback(() => {
    axios.get(`${API}/admin/selskaber-opsaetning`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Ikke adgang'));
  }, []);
  useEffect(() => { hent(); }, [hent]);

  if (!data) return <div className="text-th-text-muted py-12">Indlæser…</div>;

  const alle = data.selskaber || [];
  const kraeverHandling = c => c.status !== 'opsat' && !c.sletning_bekraeftet;
  const tal = {
    handling: alle.filter(kraeverHandling).length,
    venter: alle.filter(c => c.status === 'venter' || c.status === 'afvist').length,
    opsat: alle.filter(c => c.status === 'opsat').length,
    mangler: alle.filter(c => c.status === 'mangler_noegle').length,
    alle: alle.length,
  };
  const q = soeg.trim().toLowerCase();
  const vist = alle
    .filter(c => filter === 'alle' ? true
      : filter === 'handling' ? kraeverHandling(c)
      : filter === 'venter' ? (c.status === 'venter' || c.status === 'afvist')
      : c.status === 'opsat')
    .filter(c => !q || (c.name || '').toLowerCase().includes(q) || (c.cvr || '').includes(q)
      || (c.ejer?.email || '').toLowerCase().includes(q));
  const o = data.opsaetning || {};

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <DbSideHoved className="" titel="Alle selskaber"
        tekst={`${tal.alle} selskaber · ${tal.venter} venter på opsætning · ${tal.mangler} mangler DP-nøgle`} />

      {/* De faste vaerdier — ens for alle selskaber, saa de staar én gang */}
      <div className="rounded-xl bg-th-card border border-th-border-light">
        <button onClick={() => setVisVaerdier(v => !v)} aria-expanded={visVaerdier}
          className="w-full flex items-center gap-3 px-4 py-3 text-left">
          <Landmark size={18} className="shrink-0 text-th-text" />
          <span className="flex-1 text-sm font-medium text-th-text">Værdier til Digital Post Admin <span className="font-normal text-th-text-muted">· de samme for alle selskaber</span></span>
          <ChevronDown size={16} className={`shrink-0 text-th-text-muted transition-transform ${visVaerdier ? 'rotate-180' : ''}`} />
        </button>
        {visVaerdier && (
          <div className="border-t border-th-border-light px-4 py-3 grid lg:grid-cols-2 gap-x-8">
            <div>
              <AdminFelt label="Log ind som" vaerdi={o.operatoer} kopier={false} />
              <AdminFelt label="Systemnavn" vaerdi={o.systemnavn} />
              <AdminFelt label="Opsæt som" vaerdi="Modtagersystem" kopier={false} />
              <AdminFelt label="MeMo-version" vaerdi={o.memo_version} kopier={false} />
              <AdminFelt label="Leveringstype" vaerdi={o.leveringstype} kopier={false} />
            </div>
            <div>
              <AdminFelt label="IP-adresse" vaerdi={o.ip_omraade} kode />
              <AdminFelt label="Systemfuldmagt (CVR)" vaerdi={o.operatoer_cvr} kode />
              <AdminFelt label="Kontaktnavn" vaerdi={o.kontakt_navn} />
              <AdminFelt label="Kontakt-e-mail" vaerdi={o.kontakt_email} />
              <AdminFelt label="Kontakttelefon" vaerdi={o.kontakt_telefon} />
            </div>
            <p className="lg:col-span-2 text-xs text-th-text-muted py-2">
              Rettighedsportalen → Virksomhedens adgange til andre → selskabet → Gruppe API.
              Digital Post Admin → skift til selskabet → Systemtilslutning → + Opret system → Opsæt fra bunden.
              Endpoint URL står på hvert selskab. Aktivér systemet nu, uden deaktiveringstidspunkt — og kopiér API-nøglen, den vises kun én gang.
            </p>
          </div>
        )}
      </div>

      <DpNoegleImport onGemt={hent} />

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex flex-wrap gap-1.5">
          {[['alle', 'Alle'], ['handling', 'Kræver handling'], ['venter', 'Venter'], ['opsat', 'Opsat']].map(([id, navn]) => (
            <button key={id} onClick={() => setFilter(id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filter === id ? 'bg-db-ink text-white' : 'text-th-text-muted hover:text-th-text hover:bg-th-hover'}`}>
              {navn} <span className="opacity-70">{tal[id]}</span>
            </button>
          ))}
        </div>
        <div className="relative sm:w-72">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted" />
          <input value={soeg} onChange={e => setSoeg(e.target.value)} placeholder="Søg navn, CVR eller e-mail"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-th-border bg-th-card text-sm text-th-text placeholder:text-th-text-muted focus:outline-none focus:border-db-route" />
        </div>
      </div>

      {vist.length === 0 ? (
        <p className="text-sm text-th-text-muted py-8 text-center">
          {filter === 'handling' && !q ? 'Alle selskaber har en DP-nøgle. Intet at gøre.' : 'Ingen selskaber matcher.'}
        </p>
      ) : (
        <div className="rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2">
          {vist.map(c => (
            <div key={c.id} id={`selskab-${c.id}`} className="scroll-mt-6">
              <AdminSelskabRaekke c={c} aaben={aaben === c.id}
                onSkift={() => setAaben(aaben === c.id ? null : c.id)} onGemt={hent} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Admin: onboarding af nye kunder ────────────────────────────────────────
// De nyeste kunder og hvor langt de er naaet — fra oprettelse til den foerste
// post. Samme data som "Alle selskaber", men set som et forloeb pr. kunde.

const ONBOARDING_DAGE = 60;

// Trinene i raekkefoelge. Hvert trin afgoeres af kendsgerninger fra serveren,
// ikke af et flag nogen skal huske at saette. Slack-trinnet gaelder kun kunder
// oprettet, efter integrationen kom til — aeldre kunder kan aldrig naa det og
// ville ellers aldrig blive faerdige.
const ONBOARDING_TRIN = [
  { navn: 'Slack-opgave', relevant: c => !!c.slack_status, gjort: c => c.slack_status === 'oprettet',
    fejl: c => c.slack_status === 'fejl' || c.slack_status === 'opgivet' },
  // Kunden tager e-Boks lige efter Digital Post-guiden, mens DP-noeglen og
  // beskeden er vores del bagefter — derfor staar e-Boks foer noeglen
  { navn: 'Digital Post-guide', gjort: c => !!c.dp_tilmeldt_at },
  { navn: 'e-Boks', gjort: c => !!(c.eboks_tilmeldt_at || c.sidst_eboks) },
  { navn: 'DP-nøgle', gjort: c => c.har_noegle, fejl: c => !!c.dp_adgang_fejl },
  { navn: 'Kunden har fået besked', gjort: c => !!(c.kunde_besked_at || c.sidst_memo) },
];

function AdminOnboardingView() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState('igang');

  useEffect(() => {
    axios.get(`${API}/admin/selskaber-opsaetning`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Ikke adgang'));
  }, []);

  if (!data) return <div className="text-th-text-muted py-12">Indlæser…</div>;

  const graense = Date.now() - ONBOARDING_DAGE * 24 * 3600 * 1000;
  const nye = (data.selskaber || [])
    .filter(c => !c.sletning_bekraeftet && c.created_at && new Date(c.created_at).getTime() >= graense)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const trinFor = c => ONBOARDING_TRIN.filter(t => !t.relevant || t.relevant(c));
  const faerdig = c => trinFor(c).every(t => t.gjort(c));
  const tal = { igang: nye.filter(c => !faerdig(c)).length, faerdige: nye.filter(faerdig).length, alle: nye.length };
  const vist = nye.filter(c => filter === 'alle' ? true : filter === 'faerdige' ? faerdig(c) : !faerdig(c));

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <DbSideHoved className="" titel="Onboarding"
        tekst={`Nye kunder de seneste ${ONBOARDING_DAGE} dage — fra oprettelse til den første post.`} />

      <div className="flex flex-wrap gap-1.5">
        {[['igang', 'I gang'], ['faerdige', 'Færdige'], ['alle', 'Alle']].map(([id, navn]) => (
          <button key={id} onClick={() => setFilter(id)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filter === id ? 'bg-db-ink text-white' : 'text-th-text-muted hover:text-th-text hover:bg-th-hover'}`}>
            {navn} <span className="opacity-70">{tal[id]}</span>
          </button>
        ))}
      </div>

      {vist.length === 0 ? (
        <p className="text-sm text-th-text-muted py-8 text-center">
          {filter === 'igang' ? 'Ingen nye kunder venter. Alt er sat op.' : 'Ingen kunder her.'}
        </p>
      ) : (
        <div className="rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2">
          {vist.map(c => {
            const trin = trinFor(c);
            const gjort = trin.filter(t => t.gjort(c)).length;
            const status = ADMIN_STATUS[c.status] || ADMIN_STATUS.mangler_noegle;
            return (
              <div key={c.id} className="bg-th-card border border-th-border-light rounded-xl px-4 py-3 flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <Building2 size={18} className="shrink-0 text-th-text" />
                  <span className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="text-sm font-medium text-th-text truncate">{c.name}</span>
                    <span className="text-xs text-th-text-muted shrink-0">CVR {formatCvr(c.cvr)}</span>
                  </span>
                  <span className="hidden md:block text-xs text-th-text-muted shrink-0">Oprettet {formatDate(c.created_at)}</span>
                  {faerdig(c)
                    ? <DbTag variant="faerdig">Færdig</DbTag>
                    : <DbTag variant={status.variant}>{gjort} af {trin.length}</DbTag>}
                  <button onClick={() => navigate(`/admin/selskaber?selskab=${c.id}`)} className={`${DB_KNAP} shrink-0`}>
                    Sæt op <ArrowRight size={14} />
                  </button>
                </div>
                {/* Trinene som en linje af maerker: groen = gjort, roed = fejl */}
                <div className="flex flex-wrap gap-1.5 pl-[30px]">
                  {trin.map(t => (
                    <DbTag key={t.navn} variant={t.gjort(c) ? 'faerdig' : t.fejl?.(c) ? 'fejl' : 'lys'}>
                      {t.gjort(c) && <Check size={12} />}{t.navn}
                    </DbTag>
                  ))}
                </div>
                <p className="text-xs text-th-text-muted pl-[30px]">
                  {c.ejer?.navn || '—'}{c.ejer?.email ? ` · ${c.ejer.email}` : ''}{c.ejer?.telefon ? ` · ${c.ejer.telefon}` : ''}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AdminInboxView() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/inbox`)
      .then(r => setItems(r.data))
      .catch(() => toast.error('Ikke adgang'))
      .finally(() => setLoading(false));
  }, []);

  const deleteItem = async (item) => {
    if (!window.confirm('Slet denne besked?')) return;
    try {
      await axios.delete(`${API}/admin/inbox/${item.id}`);
      setItems(prev => prev.filter(x => x.id !== item.id));
      toast.success('Besked slettet');
    } catch (e) { toast.error('Fejl ved sletning'); }
  };

  const sourceLabel = { eboks: 'e-Boks', memo: 'Digital Post', unknown: '?' };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Alt indbakke</h1>
        <p className="text-sm sm:text-base text-th-text-secondary">{items.length} beskeder modtaget</p>
      </div>

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : items.length === 0 ? (
        <Card className="p-12 bg-th-input border-th-border text-center">
          <Inbox size={48} className="text-th-text-faint mx-auto mb-4" />
          <p className="text-th-text-secondary text-lg font-medium">Ingen beskeder endnu</p>
        </Card>
      ) : (
        <div className="bg-th-input border border-th-border rounded-xl overflow-hidden divide-y divide-th-border max-w-full">
          {items.map((item, i) => (
            <div key={item.id || i}>
              <button
                onClick={() => setExpanded(expanded === i ? null : i)}
                className="w-full flex items-start gap-2 sm:gap-4 px-3 sm:px-6 py-3 sm:py-4 hover:bg-th-hover/40 transition-colors text-left"
              >
                <div className="p-2 bg-brand-500/10 rounded-lg shrink-0 mt-0.5">
                  <Mail size={16} className="text-brand-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-th-text font-medium truncate">{item.subject || '(Ingen titel)'}</p>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {item.company_name && <span className="text-xs font-medium text-th-text-secondary bg-th-hover border border-th-border-light px-1.5 py-0.5 rounded">{item.company_name}</span>}
                    {item.from_address && <span className="text-xs text-th-text-muted">{item.from_address}</span>}
                    {item.user_email && <span className="text-xs text-th-text-faint">{item.user_email}</span>}
                    {item.file_format && <span className="text-xs bg-th-hover border border-th-border-light text-th-text-secondary px-1.5 py-0.5 rounded">{item.file_format}</span>}
                    {item.source && <span className="text-xs text-th-text-faint">{sourceLabel[item.source] || item.source}</span>}
                  </div>
                </div>
                <div className="shrink-0 text-right flex flex-col items-end gap-1">
                  <p className="text-xs text-th-text-muted whitespace-nowrap">{formatDate(item.received_at)}</p>
                  <div className="flex items-center gap-2">
                    <p className="text-xs text-th-text-faint">{expanded === i ? '▲' : '▼'}</p>
                    <button onClick={e => { e.stopPropagation(); deleteItem(item); }} className="text-th-text-faint hover:text-red-400 transition-colors" title="Slet">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                    </button>
                  </div>
                </div>
              </button>
              {expanded === i && (
                <ExpandedMessage item={item} adminMode={true} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── My Companies ────────────────────────────────────────────────────────────

function ApproveTransferDialog({ request, onDone, onCancel }) {
  const [step, setStep] = useState('confirm'); // confirm → code
  const [code, setCode] = useState('');
  const [emailHint, setEmailHint] = useState('');
  const [loading, setLoading] = useState(false);

  const sendEmailCode = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/companies/transfer-request/${request.id}/send-email-code`);
      setEmailHint(r.data.email_hint || '');
      setStep('code');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fejl ved afsendelse af e-mail');
    } finally { setLoading(false); }
  };

  const confirm = async () => {
    if (!code.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/companies/transfer-request/${request.id}/confirm`, { code });
      toast.success(`${request.company_name} er overdraget`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert kode');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-th-sidebar border border-th-border rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl">
        {step === 'confirm' && (
          <>
            <h3 className="text-th-text font-heading font-bold text-lg">Godkend overdragelse</h3>
            <p className="text-th-text-secondary text-sm">
              <strong className="text-th-text">{request.requester_name}</strong> ({request.requester_email}) anmoder om at overtage
              <strong className="text-th-text"> {request.company_name}</strong> (CVR {request.cvr}).
            </p>
            <p className="text-th-text-secondary text-sm">
              For at bekræfte sendes en e-mail-kode til din registrerede e-mailadresse.
            </p>
            <div className="flex gap-3 pt-2">
              <Button onClick={sendEmailCode} disabled={loading} className="bg-btn hover:bg-btn-hover text-btn-fg">
                {loading ? 'Sender e-mail...' : 'Send e-mail-kode'}
              </Button>
              <Button variant="ghost" onClick={onCancel} className="text-th-text-secondary">Annuller</Button>
            </div>
          </>
        )}
        {step === 'code' && (
          <>
            <h3 className="text-th-text font-heading font-bold text-lg">Indtast e-mail-kode</h3>
            <p className="text-th-text-secondary text-sm">
              En 6-cifret kode er sendt til{emailHint ? ` ${emailHint}` : ' din e-mailadresse'}.
            </p>
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              placeholder="123456"
              autoFocus
              className="w-full bg-th-hover border border-th-border-light text-th-text placeholder-th-text-muted rounded-lg px-4 py-3 text-center text-2xl font-mono tracking-widest focus:outline-none focus:border-brand-500"
            />
            <div className="flex gap-3">
              <Button onClick={confirm} disabled={loading || code.length !== 6} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                {loading ? 'Bekræfter...' : 'Bekræft overdragelse'}
              </Button>
              <button onClick={() => { setStep('confirm'); setCode(''); }} className="text-sm text-th-text-muted hover:text-th-text-secondary">
                Send ny kode
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TransferRequestDialog({ cvr, companyName, onDone, onCancel }) {
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/companies/transfer-request`, { cvr });
      toast.success('Anmodning sendt — ejer skal godkende overtagelsen');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fejl ved anmodning');
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-th-sidebar border border-th-border rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl">
        <h3 className="text-th-text font-heading font-bold text-lg">Anmod om ejerskab</h3>
        <p className="text-th-text-secondary text-sm">
          Du er ved at anmode om at overtage ejerskabet af <strong className="text-th-text">{companyName}</strong> (CVR {cvr}).
        </p>
        <p className="text-th-text-secondary text-sm">
          Den nuværende ejer modtager en e-mail og skal godkende overtagelsen, før selskabet overføres til din konto.
        </p>
        <div className="flex gap-3 pt-2">
          <Button
            onClick={submit}
            disabled={loading}
            className="bg-btn hover:bg-btn-hover text-btn-fg"
          >
            {loading ? 'Sender...' : 'Send anmodning'}
          </Button>
          <Button variant="ghost" onClick={onCancel} className="text-th-text-secondary">
            Annuller
          </Button>
        </div>
      </div>
    </div>
  );
}

// DigitalBox: "Tilføj selskab" som tre trin, der folder sig ud ét ad gangen.
// Man starter med navnet — ingen kan sit CVR-nummer udenad — og soeger i
// hentcvr gennem vores egen proxy (et direkte kald ville blive stoppet af
// CORS paa DigitalBox, se RegisterPage). Naar et trin er udfyldt, foldes det
// sammen til én linje, og det naeste kommer frem.
// Guidens tilstand gemmes i fanens sessionStorage, saa F5 lander samme sted.
// Kun i fanen: en halvfaerdig tilfoejelse skal ikke dukke op i en ny fane i
// morgen. Adgangen kan fejle (privat vindue), og saa starter guiden forfra.
const TILFOEJ_NOEGLE = 'db-tilfoej-selskab';
function hentTilfoejTilstand() {
  try { return JSON.parse(sessionStorage.getItem(TILFOEJ_NOEGLE)) || {}; } catch { return {}; }
}

function DbTilfoejSelskab({ onDone: onDoneUdefra, onTilslut: onTilslutUdefra }) {
  const [gemt] = useState(hentTilfoejTilstand);
  const [soeg, setSoeg] = useState(gemt.soeg || '');
  const [resultater, setResultater] = useState([]);
  const [soeger, setSoeger] = useState(false);
  // Den soegning, resultaterne svarer til. "Ingen fundet" vises kun, naar det
  // er svaret paa det, der staar i feltet nu — ikke paa et halvt tastet CVR.
  const [soegtFor, setSoegtFor] = useState(null);
  const [valgt, setValgt] = useState(gemt.valgt || null);
  const [gemmer, setGemmer] = useState(false);
  const [oprettet, setOprettet] = useState(gemt.oprettet || null);
  const [overtag, setOvertag] = useState(null);
  const trin = oprettet ? 3 : valgt ? 2 : 1;

  useEffect(() => {
    try { sessionStorage.setItem(TILFOEJ_NOEGLE, JSON.stringify({ soeg, valgt, oprettet })); } catch { /* uden lager starter F5 forfra */ }
  }, [soeg, valgt, oprettet]);
  const glem = () => { try { sessionStorage.removeItem(TILFOEJ_NOEGLE); } catch { /* intet at glemme */ } };
  const onDone = () => { glem(); onDoneUdefra(); };
  const onTilslut = (...a) => { glem(); onTilslutUdefra(...a); };

  const seneste = useRef(0);
  useEffect(() => {
    if (valgt) return undefined;
    const q = soeg.trim();
    if (q.length < 2) { setResultater([]); setSoegtFor(null); return undefined; }
    // Et fuldt CVR-nummer soeges med det samme; tekst venter, til man holder pause
    const t = setTimeout(async () => {
      const nr = ++seneste.current;
      setSoeger(true);
      try {
        const r = await axios.get(`${API}/public/search-company`, { params: { q } });
        // Et langsomt svar paa en aeldre soegning maa ikke overskrive et nyere
        if (nr !== seneste.current) return;
        const liste = Array.isArray(r.data) ? r.data : (r.data?.results ?? r.data?.companies ?? []);
        // Aktive selskaber foerst: et ophoert selskab faar ikke post
        setResultater([...liste].sort((a, b) => (a.status === 'Aktiv' ? 0 : 1) - (b.status === 'Aktiv' ? 0 : 1)));
        setSoegtFor(q);
      } catch {
        if (nr === seneste.current) { setResultater([]); setSoegtFor(q); }
      } finally { if (nr === seneste.current) setSoeger(false); }
    }, /^\d{8}$/.test(q.replace(/\s/g, '')) ? 0 : 300);
    return () => clearTimeout(t);
  }, [soeg, valgt]);

  const tilfoej = async () => {
    setGemmer(true);
    try {
      const r = await axios.post(`${API}/companies`, { cvr: valgt.cvr, name: valgt.name });
      toast.success(`${valgt.name} er tilføjet`);
      setOprettet(r.data?.id ? r.data : { ...valgt, id: r.data?.id });
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === 'upgrade_required') {
        toast.error('Du skal opgradere dit abonnement for at oprette selskaber.', { duration: 5000 });
      } else if (e.response?.status === 409) {
        setOvertag({ cvr: valgt.cvr, name: valgt.name });
      } else {
        toast.error(detail || 'Selskabet kunne ikke tilføjes');
      }
    } finally { setGemmer(false); }
  };

  const adresse = (c) => [c.address, [c.zipcode, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const nummer = (nr) => {
    const faerdig = nr < trin;
    return (
      <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 text-xs font-semibold ${
        faerdig || nr === trin ? 'bg-db-label text-white' : 'border-2 border-th-border text-th-text-muted'
      }`}>{faerdig ? <Check size={14} /> : nr}</span>
    );
  };
  const hoved = (nr, titel, tekst) => (
    <div className="flex items-start gap-3">
      {nummer(nr)}
      <div className="min-w-0">
        <p className="text-base font-semibold leading-7 text-th-text">{titel}</p>
        {tekst && <p className="text-sm text-th-text-muted">{tekst}</p>}
      </div>
    </div>
  );

  return (
    <div className="rounded-[20px] bg-th-inset p-5 sm:p-7 flex flex-col gap-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-heading text-xl sm:text-2xl font-semibold tracking-[-0.4px] text-th-text">Tilføj selskab</h2>
        {!oprettet && <button onClick={onDone} className={DB_KNAP}>Annuller</button>}
      </div>
      <DbTrinLinje antal={3} aktiv={trin} className="" />

      {/* 1 · Find selskabet */}
      <div className="rounded-xl bg-th-card border border-th-border-light p-4 sm:p-5 flex flex-col gap-4">
        {valgt ? (
          <div className="flex items-center gap-3">
            {nummer(1)}
            <Building2 size={16} className="shrink-0 text-th-text" />
            <span className="flex-1 min-w-0 truncate text-sm"><span className="font-medium text-th-text">{valgt.name}</span> <span className="text-th-text-muted">· CVR {formatCvr(valgt.cvr)}</span></span>
            {!oprettet && <button onClick={() => { setValgt(null); }} className={DB_KNAP}>Skift</button>}
          </div>
        ) : (
          <>
            {hoved(1, 'Find selskabet', 'Skriv firmanavnet — eller CVR-nummeret, hvis du har det ved hånden.')}
            <div className="relative">
              <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${soeger ? 'text-db-route animate-pulse' : 'text-th-text-muted'}`} />
              <input autoFocus value={soeg} onChange={e => setSoeg(e.target.value)}
                placeholder="fx Nordlys ApS eller 12345678"
                className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-th-card border border-th-border text-sm text-th-text placeholder:text-th-text-muted focus:outline-none focus:border-db-route" />
            </div>
            {/* De forrige resultater bliver staaende, nedtonet, til de nye er
                der — ellers blinker listen ved hvert tastetryk */}
            {resultater.length > 0 && (
              <div className={`flex flex-col divide-y divide-th-border-light rounded-lg border border-th-border-light overflow-hidden transition-opacity duration-150 ${
                soeger || soegtFor !== soeg.trim() ? 'opacity-60' : ''}`}>
                {resultater.slice(0, 8).map(c => (
                  <button key={c.cvr} onClick={() => setValgt(c)}
                    className="flex items-center gap-3 px-3 py-2.5 text-left bg-th-card hover:bg-th-hover transition-colors">
                    <Building2 size={16} className="shrink-0 text-th-text-muted" />
                    <span className="flex-1 min-w-0">
                      <span className="block text-sm font-medium text-th-text truncate">{c.name}</span>
                      <span className="block text-xs text-th-text-muted truncate">CVR {formatCvr(c.cvr)}{adresse(c) ? ` · ${adresse(c)}` : ''}</span>
                    </span>
                    {c.status && c.status !== 'Aktiv' && <DbTag variant="advarsel">{c.status}</DbTag>}
                  </button>
                ))}
              </div>
            )}
            {!soeger && soegtFor === soeg.trim() && soegtFor && resultater.length === 0 && (
              <p className="text-sm text-th-text-muted">Ingen selskaber fundet. Prøv med en del af navnet eller CVR-nummeret.</p>
            )}
          </>
        )}
      </div>

      {/* 2 · Bekræft og tilføj — kommer frem, naar et selskab er valgt */}
      {valgt && (
        <div className="rounded-xl bg-th-card border border-th-border-light p-4 sm:p-5 flex flex-col gap-4">
          {oprettet ? (
            <div className="flex items-center gap-3">
              {nummer(2)}
              <span className="flex-1 text-sm text-th-text"><span className="font-medium">Tilføjet</span> <span className="text-th-text-muted">· bilag kan nu sendes til {inboxEmail(valgt.cvr)}</span></span>
            </div>
          ) : (
            <>
              {hoved(2, 'Bekræft selskabet', 'Det er dette selskab, der får sin egen indbakke og bilagsadresse.')}
              <div className="rounded-lg bg-th-inset p-4 text-sm flex flex-col gap-1">
                <p className="font-heading text-lg font-semibold text-th-text">{valgt.name}</p>
                <p className="text-th-text-muted">CVR {formatCvr(valgt.cvr)}{valgt.category ? ` · ${valgt.category}` : ''}</p>
                {adresse(valgt) && <p className="text-th-text-muted">{adresse(valgt)}</p>}
                <p className="mt-2 text-th-text-muted">Bilagsadresse: <span className="font-medium text-th-text break-all">{inboxEmail(valgt.cvr)}</span></p>
              </div>
              <div className="flex gap-2">
                <button onClick={tilfoej} disabled={gemmer} className={DB_KNAP_PRIMAER}>
                  <Plus size={15} /> {gemmer ? 'Tilføjer…' : 'Tilføj selskab'}
                </button>
                <button onClick={() => setValgt(null)} className={DB_KNAP}>Tilbage</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* 3 · Tilslut posten — kommer frem, naar selskabet er tilfoejet. Digital
          Post foerst: den kraever en tegningsberettiget i Rettighedsportalen og
          er den, der tager laengst tid. Guiden tilbyder e-Boks bagefter. */}
      {oprettet && (
        <div className="rounded-xl bg-th-card border border-th-border-light p-4 sm:p-5 flex flex-col gap-4">
          {hoved(3, 'Tilslut Digital Post og e-Boks', 'Vi tager Digital Post først og fortsætter derefter med e-Boks. Begge kan også tilsluttes senere fra listen.')}
          <div className="flex flex-col gap-2">
            {[['digitalpost', 'Først'], ['eboks', 'Bagefter']].map(([id, hvornaar]) => {
              const Ikon = id === 'eboks' ? Mail : Landmark;
              return (
                <div key={id} className="flex items-center gap-3 rounded-lg bg-th-inset px-3 py-2.5 text-sm">
                  <Ikon size={16} className="text-th-text-muted shrink-0" />
                  <span className="flex-1 font-medium text-th-text">{KANALER[id].navn}</span>
                  <span className="text-th-text-muted">{hvornaar}</span>
                </div>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => onTilslut(oprettet, 'digitalpost')} className={DB_KNAP_PRIMAER}>
              <ArrowRight size={15} /> Start guiden til {KANALER.digitalpost.navn}
            </button>
            <button onClick={onDone} className={DB_KNAP}>Senere</button>
          </div>
        </div>
      )}

      {overtag && (
        <TransferRequestDialog
          cvr={overtag.cvr}
          companyName={overtag.name}
          onDone={() => { setOvertag(null); onDone(); }}
          onCancel={() => setOvertag(null)}
        />
      )}
    </div>
  );
}

function AddCompanyForm({ user, onDone }) {
  const [cvr, setCvr] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [lookingUp, setLookingUp] = useState(false);
  const [transferTarget, setTransferTarget] = useState(null);

  const lookupCvr = async (val) => {
    setCvr(val);
    if (val.replace(/\s/g, '').length !== 8) { setName(''); return; }
    setLookingUp(true);
    try {
      const r = await fetch(`/api/public/search-company?q=${encodeURIComponent(val.trim())}`);
      const data = await r.json();
      if (Array.isArray(data) && data.length > 0) setName(data[0].name || '');
    } catch { } finally { setLookingUp(false); }
  };

  const submit = async () => {
    const cleanCvr = cvr.replace(/\s/g, '');
    if (cleanCvr.length !== 8 || !name) return;
    setLoading(true);
    try {
      await axios.post(`${API}/companies`, { cvr: cleanCvr, name });
      toast.success(`${name} tilføjet`);
      onDone();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (detail === 'upgrade_required') {
        toast.error('Du skal opgradere dit abonnement for at oprette selskaber.', { duration: 5000 });
        onDone();
      } else if (status === 409) {
        setTransferTarget({ cvr: cleanCvr, name });
      } else {
        toast.error(detail || 'Fejl ved oprettelse');
      }
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-th-input border border-th-border rounded-xl p-6 space-y-4">
      <h3 className="text-th-text font-semibold text-lg">Tilføj selskab</h3>
      <div>
        <label className="text-xs text-th-text-secondary uppercase tracking-wider font-semibold block mb-1">CVR-nummer</label>
        <input
          type="text"
          value={cvr}
          onChange={e => lookupCvr(e.target.value)}
          placeholder="12345678"
          maxLength={8}
          className="w-full bg-th-hover border border-th-border-light text-th-text placeholder-th-text-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-brand-500"
        />
      </div>
      <div>
        <label className="text-xs text-th-text-secondary uppercase tracking-wider font-semibold block mb-1">Firmanavn</label>
        <input
          type="text"
          value={lookingUp ? 'Søger...' : name}
          onChange={e => setName(e.target.value)}
          placeholder="Hentes automatisk fra CVR"
          className="w-full bg-th-hover border border-th-border-light text-th-text placeholder-th-text-muted rounded-lg px-4 py-2 text-sm focus:outline-none focus:border-brand-500"
        />
      </div>
      <div className="flex gap-3">
        <Button onClick={submit} disabled={loading || !cvr || !name} className="bg-btn hover:bg-btn-hover text-btn-fg">
          {loading ? 'Gemmer...' : 'Tilføj'}
        </Button>
        <Button variant="ghost" onClick={onDone} className="text-th-text-secondary">Annuller</Button>
      </div>
      {transferTarget && (
        <TransferRequestDialog
          cvr={transferTarget.cvr}
          companyName={transferTarget.name}
          onDone={() => { setTransferTarget(null); onDone(); }}
          onCancel={() => setTransferTarget(null)}
        />
      )}
    </div>
  );
}

// Guiden aabnes for et selskab der allerede findes: selskabet og posttypen
// kommer med i adressen, saa de to foerste trin springes over.
// Digital Post og e-Boks er integrationer vi opsaetter og driver. Gratis-planen
// modtager post paa selskabets e-mailadresse og skal opgradere for kanalerne.
const KAN_FORBINDE_KANALER = t => t !== 'free' && t !== 'invited';

function guideLink(c, flow) {
  const p = new URLSearchParams({
    selskab: c.id, cvr: c.cvr || '', navn: c.name || '', flow,
  });
  return `/opret?${p.toString()}`;
}

// En toast forsvinder af sig selv og efterlader ingen vej videre. Naar en
// gratisbruger trykker paa en laast kanal, er der noget at TAGE STILLING til,
// og det hoerer til i en dialog man selv lukker.
function OpgraderDialog({ kanal, onLuk, onSeAbonnementer }) {
  const navn = kanal === 'eboks' ? 'e-Boks' : 'Digital Post';
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onLuk}>
      <div
        className="bg-th-sidebar border border-th-border rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-th-hover flex items-center justify-center shrink-0">
            <Lock size={18} className="text-th-text-secondary" />
          </div>
          <h3 className="text-th-text font-heading font-bold text-lg">{navn} kræver Solo</h3>
        </div>
        <p className="text-th-text-secondary text-sm">
          På <strong className="text-th-text">Gratis</strong> modtager du post ved at sende den til
          selskabets e-mailadresse. Det koster ikke noget og virker med det samme.
        </p>
        <p className="text-th-text-secondary text-sm">
          <strong className="text-th-text">{navn}</strong> er en integration, vi opsætter og driver, så
          posten kommer ind af sig selv. Den følger med fra <strong className="text-th-text">Solo</strong>.
        </p>
        <div className="flex gap-3 pt-2">
          <Button onClick={onSeAbonnementer} className="bg-btn hover:bg-btn-hover text-btn-fg">
            Se abonnementer
          </Button>
          <Button variant="ghost" onClick={onLuk} className="text-th-text-secondary">
            Ikke nu
          </Button>
        </div>
      </div>
    </div>
  );
}

// Sletning kan ikke fortrydes og river posthistorikken med sig. Derfor to
// bevidste bekraeftelser her, og et link i brugerens egen indbakke bagefter —
// saa en fejlklik alene ikke kan slette et selskab.
function SletSelskabDialog({ selskab, onLuk, onSendt }) {
  const [trin, setTrin] = useState(1);
  const [navn, setNavn] = useState('');
  const [sender, setSender] = useState(false);
  const passer = navn.trim().toLowerCase() === (selskab.name || '').trim().toLowerCase();

  const send = async () => {
    setSender(true);
    try {
      const r = await axios.post(`${API}/companies/${selskab.id}/request-delete`);
      onSendt(r.data?.sendt_til);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende bekræftelse');
    } finally { setSender(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onLuk}>
      <div className="bg-th-sidebar border border-th-border rounded-xl p-6 max-w-md w-full space-y-4 shadow-2xl"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center shrink-0">
            <Trash2 size={18} className="text-red-500" />
          </div>
          <h3 className="text-th-text font-heading font-bold text-lg">Slet {selskab.name}?</h3>
        </div>

        {trin === 1 ? (
          <>
            <p className="text-th-text-secondary text-sm">
              Sletningen kan <strong className="text-th-text">ikke fortrydes</strong>. Al modtaget post og
              alle dokumenter på selskabet forsvinder med.
            </p>
            <p className="text-th-text-secondary text-sm">
              Integrationen til <strong className="text-th-text">Digital Post og e-Boks skal fjernes</strong>,
              før sletningen træder helt i kraft. Det gør vores support, og du får besked, når det er sket.
              Indtil da kan der stadig komme post ind på selskabet.
            </p>
            <div className="flex gap-3 pt-2">
              <Button onClick={() => setTrin(2)} className="bg-red-600 hover:bg-red-700 text-white">
                Jeg forstår — fortsæt
              </Button>
              <Button variant="ghost" onClick={onLuk} className="text-th-text-secondary">Annuller</Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-th-text-secondary text-sm">
              Skriv selskabets navn for at bekræfte: <strong className="text-th-text">{selskab.name}</strong>
            </p>
            <Input
              autoFocus
              value={navn}
              onChange={e => setNavn(e.target.value)}
              placeholder={selskab.name}
              className="bg-th-input border-th-border-light text-th-text"
            />
            <p className="text-xs text-th-text-muted">
              Derefter sender vi et bekræftelseslink til din e-mail. Selskabet slettes først, når du har klikket.
            </p>
            <div className="flex gap-3 pt-2">
              <Button onClick={send} disabled={!passer || sender} className="bg-red-600 hover:bg-red-700 text-white">
                {sender ? 'Sender...' : 'Send bekræftelse på mail'}
              </Button>
              <Button variant="ghost" onClick={onLuk} className="text-th-text-secondary">Annuller</Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// Hentekontoen kundens ERP skal sættes op med. Den vises kun for selskaber,
// der faktisk har fået en konto — resten har ingen SFTP endnu, og et tomt
// felt med en vært i ville bare give supportopkald.
// Hentekontoen har tre tilstande, og de skal se forskellige ud:
//
//   ingen adgang      — et tilbud, ikke en fejl. Langtfra alle skal have det.
//   netop oprettet    — adgangskoden staar her, én gang. Alt andet viger.
//   oprettet tidligere— oplysningerne uden koden, og en vej til en ny.
//
// Koden kan ikke hentes frem igen. Det staar direkte i fladen, for det er den
// slags, folk kun opdager for sent.
function SftpAdgang({ company, onAendret }) {
  const [travl, setTravl] = useState(false);
  // Klarteksten kommer kun med i svaret paa oprettelsen. Den lever her, indtil
  // brugeren skjuler den eller forlader siden — der er ingen vej til den bagefter.
  const [friskKode, setFriskKode] = useState(null);
  const sftp = company.sftp || {};
  const harAdgang = !!sftp.brugernavn;

  const kopier = async (vaerdi, hvad) => {
    if (!vaerdi) return;
    try {
      await navigator.clipboard.writeText(vaerdi);
      toast.success(`${hvad} kopieret`);
    } catch {
      // Udklipsholderen kræver en sikker kontekst og kan afvises af browseren.
      toast.error('Kunne ikke kopiere — marker teksten og kopier manuelt');
    }
  };

  // Hele opsaetningen i ét stykke. Det er saadan den skal ind i et ERP-system,
  // og det sparer fire klik og en tastefejl.
  const kopierAlt = () => kopier(
    [`Vært: ${sftp.vaert}`, `Port: ${sftp.port}`, `Protokol: ${sftp.protokol || 'SFTP'}`,
     `Brugernavn: ${sftp.brugernavn}`, `Adgangskode: ${friskKode}`].join('\n'),
    'Alle oplysninger');

  const opret = async () => {
    setTravl(true);
    try {
      const r = await axios.post(`${API}/companies/${company.id}/sftp`);
      setFriskKode(r.data.adgangskode);
      toast.success('SFTP-adgang oprettet');
      onAendret?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke oprette adgangen');
    } finally { setTravl(false); }
  };

  const nulstil = async () => {
    if (!window.confirm(
      `Lav en ny adgangskode til ${company.name}?\n\nDen nuværende holder op med at virke med det samme, så opsætningen i kundens system skal rettes bagefter.`
    )) return;
    setTravl(true);
    try {
      const r = await axios.post(`${API}/companies/${company.id}/sftp/nulstil`);
      setFriskKode(r.data.adgangskode);
      toast.success('Ny adgangskode oprettet');
      onAendret?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke nulstille adgangskoden');
    } finally { setTravl(false); }
  };

  const fjern = async () => {
    if (!window.confirm(
      `Fjern SFTP-adgangen for ${company.name}?\n\nKontoen lukkes, og kunden kan ikke længere hente filer. Det kan ikke fortrydes — men du kan oprette adgangen igen bagefter med en ny adgangskode.`
    )) return;
    setTravl(true);
    try {
      await axios.delete(`${API}/companies/${company.id}/sftp`);
      setFriskKode(null);
      toast.success('SFTP-adgangen er fjernet');
      onAendret?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke fjerne adgangen');
    } finally { setTravl(false); }
  };

  const felt = (navn, vaerdi) => (
    <div className="flex items-center gap-2 min-w-0">
      <span className="text-xs text-th-text-muted w-24 shrink-0">{navn}</span>
      <span className="text-xs font-mono text-th-text-secondary truncate select-all">{vaerdi}</span>
      <button onClick={() => kopier(vaerdi, navn)}
        className="text-th-text-muted hover:text-th-text transition-colors shrink-0"
        title={`Kopier ${navn.toLowerCase()}`}>
        <Copy size={13} />
      </button>
    </div>
  );

  // ── Ingen adgang endnu ────────────────────────────────────────────────────
  if (!harAdgang) {
    if (!company.sftp_muligt) return null;
    return (
      <div className="border-t border-th-border pt-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold flex items-center gap-1.5 mb-1">
              <Server size={13} /> Hent filer via SFTP
            </p>
            <p className="text-xs text-th-text-muted max-w-lg">
              Giv selskabet en konto, så deres eget system kan hente de færdige
              OIOUBL-fakturaer automatisk. Det er ikke noget alle skal have — opret
              den, når kunden beder om den.
            </p>
          </div>
          <Button onClick={opret} disabled={travl} className="shrink-0">
            <Plus size={15} className="mr-1.5" />
            {travl ? 'Opretter…' : 'Opret SFTP-adgang'}
          </Button>
        </div>
      </div>
    );
  }

  // ── Adgang findes ─────────────────────────────────────────────────────────
  return (
    <div className="border-t border-th-border pt-4 space-y-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold flex items-center gap-1.5">
          <Server size={13} /> Hent filer via SFTP
        </p>
        <div className="flex items-center gap-3 shrink-0">
          <button onClick={nulstil} disabled={travl}
            className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-th-text transition-colors disabled:opacity-50"
            title="Lav en ny adgangskode — den gamle holder op med at virke">
            <RefreshCw size={13} /> {travl ? 'Arbejder…' : 'Ny adgangskode'}
          </button>
          <button onClick={fjern} disabled={travl}
            className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-red-500 transition-colors disabled:opacity-50">
            <Trash2 size={13} /> Fjern adgang
          </button>
        </div>
      </div>

      {felt('Vært', sftp.vaert)}
      {felt('Port', String(sftp.port ?? ''))}
      {/* Protokollen staar med, fordi den kan vaere mere end SFTP: kan kundens
          ERP kun FTP, er den slaaet til paa serveren, og saa skal feltet i
          ERP'et sættes derefter. */}
      {felt('Protokol', sftp.protokol || 'SFTP')}
      {felt('Brugernavn', sftp.brugernavn)}

      {/* De to fejl alle laver, og de er ikke sjusk:
          1) programmet gaetter paa FTP, naar man kun skriver vaertsnavnet, og
          2) FileZilla NULSTILLER portfeltet, naar man skifter protokol — saa
             de 2255, man lige har tastet, bliver til 22 uden varsel.
          Derfor staar raekkefoelgen her, ikke bare vaerdierne. */}
      {!(sftp.protokol || 'SFTP').split(',').map(p => p.trim().toUpperCase()).includes('FTP') && (
        <div className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-1.5">
          <p className="text-xs font-semibold text-th-text">Sådan forbinder du</p>
          <p className="text-xs text-th-text-secondary">
            Vælg <strong className="text-th-text">SFTP</strong> i dit program — <strong className="text-th-text">ikke FTP</strong>.
            FTP er slået fra på serveren og vil altid fejle, uanset port.
          </p>
          <p className="text-xs text-th-text-secondary">
            Vælg protokollen <strong className="text-th-text">først</strong>, og skriv <strong className="text-th-text">derefter</strong> porten{' '}
            <strong className="text-th-text">{sftp.port}</strong>. FileZilla nulstiller portfeltet til 22, når man skifter
            protokol bagefter — det er den hyppigste årsag til, at det ikke virker.
          </p>
          <p className="text-xs text-th-text-muted">
            Hurtigt i FileZilla: skriv <code className="bg-th-hover px-1 py-0.5 rounded">sftp://{sftp.vaert}</code> i
            værtsfeltet og <code className="bg-th-hover px-1 py-0.5 rounded">{sftp.port}</code> i portfeltet.
          </p>
        </div>
      )}

      {friskKode ? (
        <div className="border-2 border-brand-500 rounded-lg p-4 mt-3 bg-brand-500/5">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle size={16} className="text-brand-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-th-text">Skriv adgangskoden ned nu</p>
              <p className="text-xs text-th-text-secondary">
                Det er eneste gang, den vises. Lukker du siden uden at gemme den,
                kan den ikke findes frem igen — så må du lave en ny.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-th-hover border border-th-border rounded-lg px-3 py-2.5 text-xs font-mono text-th-text break-all">
              {friskKode}
            </code>
            <Button onClick={() => kopier(friskKode, 'Adgangskoden')} className="shrink-0">
              <Copy size={15} className="mr-1.5" /> Kopiér
            </Button>
          </div>
          <div className="flex items-center gap-4 mt-3">
            <button onClick={kopierAlt}
              className="text-xs text-brand-500 hover:underline">
              Kopiér alle oplysninger
            </button>
            <button onClick={() => setFriskKode(null)}
              className="text-xs text-th-text-muted hover:text-th-text transition-colors">
              Jeg har gemt den — skjul
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-th-text-muted w-24 shrink-0">Adgangskode</span>
          <span className="text-xs text-th-text-muted italic">
            vist én gang ved oprettelsen — lav en ny, hvis den er væk
          </span>
        </div>
      )}
    </div>
  );
}

function MyCompaniesView({ user }) {
  const navigate = useNavigate();
  const maaForbinde = KAN_FORBINDE_KANALER(user?.subscription_tier);
  const [opgraderKanal, setOpgraderKanal] = useState(null);
  const aabnGuide = (c, flow) => {
    if (!maaForbinde) {
      setOpgraderKanal(flow);
      return;
    }
    navigate(guideLink(c, flow));
  };
  const [companies, setCompanies] = useState([]);
  const [affiliated, setAffiliated] = useState([]);
  const [transfers, setTransfers] = useState({ incoming: [], outgoing: [] });
  const [loading, setLoading] = useState(true);
  // DigitalBox: at "Tilføj selskab" er aaben, staar i URL'en (?tilfoej=1), saa
  // F5 ikke lukker den. Hvor langt man er naaet, husker guiden selv.
  const [soegeParams, setSoegeParams] = useSearchParams();
  const [showAdd, setShowAddTilstand] = useState(() => TRE_KOLONNER && soegeParams.get('tilfoej') === '1');
  const setShowAdd = (aaben) => {
    // Et nyt klik paa "Tilføj selskab" starter forfra. Kun en genindlaesning
    // (?tilfoej=1 ved start) genopretter, hvor man var naaet til.
    if (aaben) { try { sessionStorage.removeItem(TILFOEJ_NOEGLE); } catch { /* intet at rydde */ } }
    setShowAddTilstand(aaben);
    if (!TRE_KOLONNER) return;
    const ny = new URLSearchParams(soegeParams);
    if (aaben) ny.set('tilfoej', '1'); else ny.delete('tilfoej');
    setSoegeParams(ny, { replace: true });
  };
  const [respondingId, setRespondingId] = useState(null);
  const [transferTarget, setTransferTarget] = useState(null);
  const [approveRequest, setApproveRequest] = useState(null);

  const fetchAll = async () => {
    try {
      const [cr, all, tr] = await Promise.all([
        axios.get(`${API}/companies/own`),
        axios.get(`${API}/companies`),
        axios.get(`${API}/companies/transfer-requests`),
      ]);
      setCompanies(cr.data);
      // Tilknyttede = selskaber fra andre ejere
      const ownIds = new Set(cr.data.map(c => c.id));
      setAffiliated((all.data || []).filter(c => !ownIds.has(c.id)));
      setTransfers(tr.data);
    } catch {
      toast.error('Kunne ikke hente selskaber');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const [sletSelskab, setSletSelskab] = useState(null);
  // DigitalBox: hvilket selskab der er foldet ud
  const [aabentSelskab, setAabentSelskab] = useState(null);
  const tilfoejRef = useRef(null);
  // "Tilføj selskab" aabner oeverst paa siden. Har brugeren rullet ned i en
  // lang liste, skal knappen tage hende op til boksen igen.
  useEffect(() => {
    if (TRE_KOLONNER && showAdd) tilfoejRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showAdd]);
  // Uden et eneste selskab er der kun én ting at goere — typisk en ny bruger,
  // der lige er logget ind med Google eller Microsoft. Guiden aabner af sig selv.
  useEffect(() => {
    if (TRE_KOLONNER && !loading && !showAdd && companies.length === 0 && user?.subscription_tier !== 'invited') setShowAdd(true);
  }, [loading, companies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const reject = async (requestId) => {
    setRespondingId(requestId);
    try {
      await axios.post(`${API}/companies/transfer-request/${requestId}/reject`);
      toast.success('Anmodning afvist');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fejl');
    } finally { setRespondingId(null); }
  };

  const pendingIncoming = transfers.incoming?.filter(r => r.status === 'pending') || [];
  const pendingOutgoing = transfers.outgoing?.filter(r => r.status === 'pending') || [];

  const isInvited = user?.subscription_tier === 'invited';

  // DigitalBox: hvert selskab som et lyst kort med Digital Post og e-Boks som
  // to kanaler, der enten er tilsluttet eller har en knap til guiden.
  const dbKanal = (c, flow, Ikon, navn, tekst, tilsluttet) => (
    <div className="flex items-center gap-3 bg-th-card border border-th-border-light rounded-xl px-4 py-3">
      <Ikon size={18} className="shrink-0 text-th-text" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium leading-5 text-th-text">{navn}</p>
        <p className="text-xs leading-4 text-th-text-muted truncate">{tekst}</p>
      </div>
      {flow === 'digitalpost' && c.dp_adgang_fejl ? (
        <span title={c.dp_adgang_fejl.besked || ''}><DbTag variant="fejl">Adgang afvist</DbTag></span>
      ) : tilsluttet ? (
        <DbTag variant="faerdig" prik={flow === 'eboks' ? KILDE_PRIK.eboks : undefined}>Tilsluttet</DbTag>
      ) : !c.deletion_confirmed_at && (
        <button onClick={() => aabnGuide(c, flow)} className={DB_KNAP}
          title={maaForbinde ? `Trin for trin: tilslut ${navn}` : 'Kræver Solo — på Gratis modtager du post på selskabets e-mailadresse'}>
          {maaForbinde ? <ArrowRight size={14} /> : <Lock size={14} />} Tilslut
        </button>
      )}
    </div>
  );
  const dbSelskabRaekke = (c) => {
    const aaben = aabentSelskab === c.id;
    // e-Boks faar samme roede prik som kildemaerket i indbakken
    const kanalMaerke = (navn, tilsluttet, fejl, prik) => (
      <DbTag variant={fejl ? 'fejl' : tilsluttet ? 'faerdig' : 'lys'} prik={tilsluttet && !fejl ? prik : undefined} title={fejl ? 'Adgangen blev afvist' : tilsluttet ? `${navn} er tilsluttet` : `${navn} er ikke tilsluttet`}>
        {navn}
      </DbTag>
    );
    return (
      <div key={c.id} className="bg-th-card border border-th-border-light rounded-xl">
        <button onClick={() => setAabentSelskab(aaben ? null : c.id)} aria-expanded={aaben}
          className="w-full flex items-center gap-3 px-4 py-3 text-left">
          <Building2 size={18} className="shrink-0 text-th-text" />
          <span className="flex-1 min-w-0 flex items-baseline gap-2">
            <span className="text-sm font-medium text-th-text truncate">{c.name}</span>
            <span className="text-xs text-th-text-muted shrink-0">CVR {formatCvr(c.cvr)}</span>
          </span>
          <span className="hidden sm:flex items-center gap-1.5 shrink-0">
            {c.deletion_confirmed_at
              ? <DbTag variant="advarsel">Afventer sletning</DbTag>
              : <>{kanalMaerke('Digital Post', c.setup_digitalpost, !!c.dp_adgang_fejl)}{kanalMaerke('e-Boks', c.setup_eboks, false, KILDE_PRIK.eboks)}</>}
            {c.spejl && (
              <DbTag variant="lys" title={c.sidst_spejlet_at
                ? `Sidst afstemt ${new Date(c.sidst_spejlet_at).toLocaleString('da-DK')}`
                : 'Afventer første afstemning'}>
                {BRAND.partner_navn}
              </DbTag>
            )}
          </span>
          <ChevronDown size={16} className={`shrink-0 text-th-text-muted transition-transform ${aaben ? 'rotate-180' : ''}`} />
        </button>
        {aaben && (
          <div className="border-t border-th-border-light px-4 py-4 flex flex-col gap-4">
            <div className="grid sm:grid-cols-2 gap-2">
              {dbKanal(c, 'digitalpost', Landmark, 'Digital Post', 'Myndigheder · MeMo-format', c.setup_digitalpost)}
              {dbKanal(c, 'eboks', Mail, 'e-Boks', 'Banker, løn, forsikring', c.setup_eboks)}
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-th-text-muted">
              {c.cvr && <span>Bilagsadresse: <span className="select-all font-medium text-th-text break-all">{inboxEmail(c.cvr)}</span></span>}
              {c.deletion_confirmed_at ? (
                <span className="text-amber-700">Support fjerner integrationen til Digital Post og e-Boks.</span>
              ) : (
                <button onClick={() => setSletSelskab(c)} title="Kræver bekræftelse på mail" className={`${DB_KNAP} hover:text-red-600`}>
                  <Trash2 size={14} /> Slet selskab
                </button>
              )}
            </div>
            <SftpAdgang company={c} onAendret={fetchAll} />
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`${TRE_KOLONNER ? 'max-w-5xl' : 'max-w-3xl'} mx-auto space-y-8`}>
      {TRE_KOLONNER ? (
        <DbSideHoved className="" titel="Selskaber"
          tekst="Hvert selskab får sin egen indbakke. Tilslut Digital Post og e-Boks, så lander al posten ét sted.">
          {!showAdd && !isInvited && (
            <button onClick={() => setShowAdd(true)} className={DB_KNAP_PRIMAER}><PlusCircle size={15} /> Tilføj selskab</button>
          )}
        </DbSideHoved>
      ) : (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Mine selskaber</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">Oversigt over selskaber tilknyttet din konto.</p>
        </div>
        {!showAdd && !isInvited && (
          <Button onClick={() => setShowAdd(true)} className="bg-btn hover:bg-btn-hover text-btn-fg w-fit flex items-center gap-2">
            <PlusCircle size={16} /> Tilføj selskab
          </Button>
        )}
      </div>
      )}


      {isInvited && (
        <div className="bg-highlight-500/10 border border-highlight-500/30 rounded-xl p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-start gap-4">
            <CreditCard size={28} className="text-highlight-400 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-th-text">Vil du oprette dit eget selskab?</p>
              <p className="text-sm text-th-text-secondary mt-1">Du er inviteret som modtager. Opgrader til Solo eller højere for at tilføje og modtage post for egne selskaber.</p>
            </div>
          </div>
          <Button onClick={() => navigate('/profil')} className="bg-highlight-600 hover:bg-highlight-500 text-white shrink-0">
            Se abonnementer
          </Button>
        </div>
      )}

      {showAdd && !isInvited && !TRE_KOLONNER && (
        <AddCompanyForm user={user} onDone={() => { setShowAdd(false); fetchAll(); }} />
      )}

      {/* Indkommende overtagelsesanmodninger */}
      {pendingIncoming.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wider text-th-text-secondary font-semibold mb-3">Anmodninger om overtagelse</h2>
          <div className="space-y-3">
            {pendingIncoming.map(r => (
              <div key={r.id} className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <p className="text-th-text font-medium">{r.company_name} <span className="text-th-text-muted font-mono text-xs">(CVR {r.cvr})</span></p>
                  <p className="text-sm text-th-text-secondary mt-0.5">{r.requester_name} ({r.requester_email}) ønsker at overtage dette selskab</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    onClick={() => setApproveRequest(r)}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-sm"
                  >Godkend</Button>
                  <Button
                    onClick={() => reject(r.id)}
                    disabled={respondingId === r.id}
                    variant="ghost"
                    className="text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-sm"
                  >Afvis</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Udgående anmodninger */}
      {pendingOutgoing.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wider text-th-text-secondary font-semibold mb-3">Dine afventende anmodninger</h2>
          <div className="space-y-2">
            {pendingOutgoing.map(r => (
              <div key={r.id} className="bg-th-input border border-th-border rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-th-text font-medium">{r.company_name} <span className="text-th-text-muted font-mono text-xs">(CVR {r.cvr})</span></p>
                  <p className="text-xs text-th-text-muted mt-0.5">Afventer godkendelse fra nuværende ejer</p>
                </div>
                <Badge className="bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/30 shrink-0">Afventer</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* DigitalBox: "Tilføj selskab" står øverst, over listen */}
      {TRE_KOLONNER && showAdd && !isInvited && (
        <div ref={tilfoejRef} className="scroll-mt-6">
          <DbTilfoejSelskab
            onDone={() => { setShowAdd(false); fetchAll(); }}
            onTilslut={(c, flow) => { fetchAll(); aabnGuide(c, flow); }} />
        </div>
      )}

      {/* Selskabsliste */}
      {/* Egne selskaber — skjult for invited */}
      {!isInvited && <div>
        {!(TRE_KOLONNER && showAdd && !loading && companies.length === 0) && (
          <h2 className="text-sm uppercase tracking-wider text-th-text-secondary font-semibold mb-3">Mine selskaber</h2>
        )}
        {loading ? (
          <div className="text-th-text-muted text-center py-12">Indlæser...</div>
        ) : companies.length === 0 && TRE_KOLONNER && showAdd ? (
          // Guiden staar lige under og siger det samme — ingen grund til begge
          null
        ) : companies.length === 0 ? (
          <div className="bg-th-input border border-th-border rounded-xl p-8 text-center space-y-3">
            <div className="w-12 h-12 rounded-full bg-th-hover flex items-center justify-center mx-auto">
              <Building size={22} className="text-th-text-muted" />
            </div>
            <p className="text-th-text-secondary font-medium">Ingen egne selskaber endnu</p>
            <p className="text-th-text-muted text-sm">Klik "Tilføj selskab" for at komme i gang.</p>
          </div>
        ) : (
          <div className={TRE_KOLONNER ? 'rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2' : 'space-y-3'}>
            {companies.map(c => TRE_KOLONNER ? dbSelskabRaekke(c) : (
              <div key={c.id} className="bg-th-input border border-th-border rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-th-hover flex items-center justify-center shrink-0">
                      <Building size={18} className="text-th-text-secondary" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-th-text font-semibold">{c.name}</p>
                      <p className="text-xs text-th-text-muted font-mono mt-0.5">CVR: {c.cvr}</p>
                      {c.cvr && (
                        <p className="text-xs text-th-text-faint font-mono mt-0.5 break-all">
                          E-mail: <span className="text-th-text-secondary select-all">{inboxEmail(c.cvr)}</span>
                        </p>
                      )}
                      <SpejlMaerke selskab={c} />
                    </div>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-2">
                    <div className="flex items-center gap-3">
                      <span className="inline-flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-600 dark:bg-green-400 inline-block"></span>Aktiv
                      </span>
                    </div>
                    {c.deletion_confirmed_at ? (
                      <span className="text-xs text-amber-600 dark:text-amber-400 text-right max-w-[15rem]">
                        Afventer sletning — support fjerner integrationen til Digital Post og e-Boks
                      </span>
                    ) : (
                    <div className="flex items-center gap-2 flex-wrap justify-end">
                      {!c.setup_digitalpost && (
                      <button
                        onClick={() => aabnGuide(c, 'digitalpost')}
                        className="inline-flex items-center justify-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors border border-blue-400 dark:border-blue-500/50 rounded-lg px-3 py-1.5 font-medium whitespace-nowrap"
                        title={maaForbinde ? 'Vejledning til at forbinde Digital Post for dette selskab' : 'Kræver Solo — på Gratis modtager du post på selskabets e-mailadresse'}
                      >
                        {maaForbinde ? <BookOpen size={13} /> : <Lock size={13} />} Opsæt Digital Post
                      </button>
                      )}
                      {!c.setup_eboks && (
                      <button
                        onClick={() => aabnGuide(c, 'eboks')}
                        className="inline-flex items-center justify-center gap-1.5 text-xs text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors border border-orange-400 dark:border-orange-500/50 rounded-lg px-3 py-1.5 font-medium whitespace-nowrap"
                        title={maaForbinde ? 'Vejledning til at forbinde e-Boks Erhverv for dette selskab' : 'Kræver Solo — på Gratis modtager du post på selskabets e-mailadresse'}
                      >
                        {maaForbinde ? <BookOpen size={13} /> : <Lock size={13} />} Opsæt e-Boks
                      </button>
                      )}
                      <button
                        onClick={() => setSletSelskab(c)}
                        className="inline-flex items-center justify-center gap-1.5 text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors border border-red-400 dark:border-red-500/50 rounded-lg px-3 py-1.5 font-medium whitespace-nowrap"
                        title="Slet selskabet — kræver bekræftelse på mail"
                      >
                        <Trash2 size={13} /> Slet selskab
                      </button>
                    </div>
                    )}
                  </div>
                </div>
                <SftpAdgang company={c} onAendret={fetchAll} />
              </div>
            ))}
          </div>
        )}
      </div>}

      {/* Tilknyttede selskaber (via modtager-relation) */}
      {!loading && affiliated.length > 0 && (
        <div>
          <h2 className="text-sm uppercase tracking-wider text-th-text-secondary font-semibold mb-3">Tilknyttede selskaber</h2>
          {TRE_KOLONNER ? (
            // Samme raekker som Mine selskaber, men uden fold-ud: kanaler og
            // sletning er ejerens. Til hoejre staar, hvem der ejer selskabet.
            <div className="rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2">
              {affiliated.map(c => (
                <div key={c.id} className="bg-th-card border border-th-border-light rounded-xl flex items-center gap-3 px-4 py-3">
                  <Building2 size={18} className="shrink-0 text-th-text" />
                  <span className="flex-1 min-w-0 flex items-baseline gap-2">
                    <span className="text-sm font-medium text-th-text truncate">{c.name}</span>
                    <span className="text-xs text-th-text-muted shrink-0">CVR {formatCvr(c.cvr)}</span>
                  </span>
                  <span className="hidden md:block text-xs text-th-text-muted truncate max-w-[16rem]">
                    {c.owner_email || c.owner_name ? <>Ejes af <span className="text-th-text">{c.owner_email || c.owner_name}</span></> : 'Ejet af anden bruger'}
                  </span>
                  <button
                    onClick={() => {
                      if (isInvited) { navigate('/profil'); toast.info('Opgrader dit abonnement for at anmode om ejerskab.'); }
                      else setTransferTarget({ cvr: c.cvr, name: c.name });
                    }}
                    // Lav som kanalmaerkerne, saa raekken er lige saa hoej som Mine selskabers
                    className="shrink-0 px-[9px] py-[5px] rounded-[5px] border border-th-border text-xs font-semibold leading-4 text-th-text hover:bg-th-hover transition-colors">
                    Anmod ejerskab
                  </button>
                </div>
              ))}
            </div>
          ) : (
          <div className="space-y-3">
            {affiliated.map(c => (
              <div key={c.id} className="bg-th-input border border-th-border rounded-xl p-5 flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-lg bg-th-hover flex items-center justify-center shrink-0">
                    <Building size={18} className="text-th-text-muted" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-th-text font-semibold">{c.name}</p>
                    <p className="text-xs text-th-text-muted font-mono mt-0.5">CVR: {c.cvr}</p>
                    <p className="text-xs text-th-text-faint mt-0.5">
                      {c.owner_email || c.owner_name
                        ? <>Ejes af <span className="text-th-text-secondary break-all">{c.owner_email || c.owner_name}</span></>
                        : 'Ejet af anden bruger'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (isInvited) { navigate('/profil'); toast.info('Opgrader dit abonnement for at anmode om ejerskab.'); }
                    else setTransferTarget({ cvr: c.cvr, name: c.name });
                  }}
                  className="shrink-0 text-xs text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 transition-colors border border-brand-400 dark:border-brand-500/50 rounded-lg px-3 py-1.5 font-medium"
                >
                  Anmod ejerskab
                </button>
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {sletSelskab && (
        <SletSelskabDialog
          selskab={sletSelskab}
          onLuk={() => setSletSelskab(null)}
          onSendt={(til) => {
            setSletSelskab(null);
            toast.success(`Bekræftelseslink sendt til ${til || 'din e-mail'}`, { duration: 8000 });
            fetchAll();
          }}
        />
      )}

      {opgraderKanal && (
        <OpgraderDialog
          kanal={opgraderKanal}
          onLuk={() => setOpgraderKanal(null)}
          onSeAbonnementer={() => { setOpgraderKanal(null); navigate('/profil'); }}
        />
      )}

      {transferTarget && (
        <TransferRequestDialog
          cvr={transferTarget.cvr}
          companyName={transferTarget.name}
          onDone={() => { setTransferTarget(null); fetchAll(); }}
          onCancel={() => setTransferTarget(null)}
        />
      )}

      {approveRequest && (
        <ApproveTransferDialog
          request={approveRequest}
          onDone={() => { setApproveRequest(null); fetchAll(); }}
          onCancel={() => setApproveRequest(null)}
        />
      )}
    </div>
  );
}

// ─── Recipients ──────────────────────────────────────────────────────────────

function InviteRecipientForm({ onDone, onCancel }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const send = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/invite`, { email });
      toast.success(`Invitation sendt til ${email}`);
      // Nye invitationer giver ingen selskabsadgang. Åbn rettighederne med
      // det samme, så det ikke bliver glemt.
      onDone(r.data?.invite_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende invitation');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-th-input border border-th-border rounded-xl overflow-hidden">
      <div className="px-6 pt-6 pb-4">
        <h2 className="text-xl font-semibold text-th-text mb-1">Send invitation</h2>
        <p className="text-sm text-th-text-secondary mb-5">Modtageren får en email med et link til at oprette sin konto (navn, telefon, SMS-kode, email-kode og adgangskode).</p>
        <Input autoFocus value={email} onChange={e => setEmail(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && email.includes('@') && send()}
          placeholder="modtager@firma.dk" type="email"
          className="bg-th-hover border-th-border-light text-th-text placeholder:text-th-text-muted text-lg py-6" />
      </div>
      <div className="px-6 py-4 border-t border-th-border flex items-center justify-between">
        <button onClick={onCancel} className="text-sm text-th-text-muted hover:text-th-text-secondary transition-colors">Annuller</button>
        <Button onClick={send} disabled={loading || !email.includes('@')} className={TRE_KOLONNER ? 'bg-db-ink hover:opacity-90 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}>
          {loading ? 'Sender...' : 'Send invitation'}
        </Button>
      </div>
    </div>
  );
}

// Bruges både til aktiverede modtagere og til invitationer der endnu ikke er
// accepteret. For en invitation gemmes valget på selve invitationen og
// kopieres over på modtageren først når hun aktiverer.
function RecipientPermissions({ recipient, companies, onSaved, isInvite = false }) {
  const isSelf = !!recipient.is_self;
  const [role, setRole] = useState(recipient.role || 'user');
  const [isPrimary, setIsPrimary] = useState(recipient.is_primary !== false);
  // Kanalvalg pr. modtager. !== false, saa en modtager oprettet foer felterne
  // fandtes staar som tilvalgt — det er den adfaerd hun har i dag.
  const [notifyEmail, setNotifyEmail] = useState(recipient.notify_email !== false);
  const [notifySms, setNotifySms] = useState(recipient.notify_sms !== false);
  const [saving, setSaving] = useState(false);

  // excluded = selskaber der er fravalgt.
  // Backend: null = alle tilladt, [] = ingen tilladt, [ids] = kun disse.
  const initExcluded = () => {
    const allowed = recipient.allowed_companies;
    if (allowed === null || allowed === undefined) return []; // alle valgt
    return companies.filter(c => !allowed.includes(c.id)).map(c => c.id);
  };
  const [excluded, setExcluded] = useState(initExcluded);

  const allSelected = excluded.length === 0;
  const noneSelected = excluded.length === companies.length;

  const toggleCompany = (id) => {
    setExcluded(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (allSelected) {
      setExcluded(companies.map(c => c.id));
    } else {
      setExcluded([]);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // null = alle, [] = ingen, [ids] = specifikke
      const allowed = allSelected
        ? null
        : companies.filter(c => !excluded.includes(c.id)).map(c => c.id);
      if (isInvite) {
        // is_primary sættes først ved aktivering
        await axios.patch(`${API}/invites/${recipient.id}`, {
          role,
          allowed_companies: allowed,
        });
      } else {
        await axios.put(`${API}/recipients/${recipient.id}/permissions`, {
          role,
          allowed_companies: allowed,
          is_primary: isPrimary,
          notify_email: notifyEmail,
          notify_sms: notifySms,
        });
      }
      toast.success('Rettigheder opdateret');
      onSaved();
    } catch {
      toast.error('Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  const roleLabel = { admin: 'Admin', user: 'Bruger' };
  const roleDesc = {
    admin: 'Adgang til alle menupunkter undtagen at slette ejeren',
    user: 'Kun adgang til Indbakke, Videresend og Adressebog',
  };

  return (
    <div className="mt-3 border-t border-th-border pt-4 space-y-4">
      {isSelf && (
        <p className="text-xs text-th-text-muted">Du modtager altid notifikationer for alle dine selskaber. Du kan slå daglige påmindelser til/fra herunder.</p>
      )}
      {!isSelf && (
        <div>
          <p className="text-sm font-medium text-th-text mb-2">Rolle</p>
          <div className="flex gap-2">
            {['admin', 'user'].map(r => (
              <button
                key={r}
                onClick={() => setRole(r)}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  role === r
                    ? 'bg-brand-500 text-white border-brand-500'
                    : 'bg-th-hover text-th-text-secondary border-th-border hover:border-brand-400'
                }`}
              >
                {roleLabel[r]}
              </button>
            ))}
          </div>
          <p className="text-xs text-th-text-muted mt-1">{roleDesc[role]}</p>
        </div>
      )}

      {isInvite && (
        <p className="text-xs text-th-text-muted">
          Valget gemmes på invitationen og træder i kraft i det øjeblik hun aktiverer sin konto.
        </p>
      )}

      {!isInvite && (
      <div>
        <p className="text-sm font-medium text-th-text mb-2">Notifikationer</p>
        <label className="flex items-center gap-3 cursor-pointer">
          <button
            onClick={() => setIsPrimary(!isPrimary)}
            className={`relative w-10 h-6 rounded-full transition-colors ${isPrimary ? 'bg-brand-500' : 'bg-slate-300 dark:bg-slate-600'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform shadow ${isPrimary ? 'translate-x-4' : ''}`} />
          </button>
          <div>
            <span className="text-sm text-th-text font-medium">Primær modtager</span>
            <p className="text-xs text-th-text-muted mt-0.5">
              {isPrimary
                ? 'Modtager daglige påmindelser om uløste sager'
                : 'Modtager kun besked når sager eskaleres'}
            </p>
          </div>
        </label>

        {/* Mailserveren har afvist adressen permanent. Uden det her ville
            systemet vide besked uden at fortaelle nogen — og modtageren ville
            blive ved med at staa som aktiv, mens intet naaede frem. */}
        {recipient.email_bounce_at && (
          <div className="mt-4 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/30 dark:bg-red-500/10 px-3 py-2">
            <p className="text-sm font-medium text-red-800 dark:text-red-300">Mails til denne adresse afvises</p>
            <p className="text-xs text-red-700 dark:text-red-400 mt-0.5">
              Mailserveren svarede {recipient.email_bounce_status} den {formatDate(recipient.email_bounce_at)}.
              Adressen findes formentlig ikke — ret den, eller fjern modtageren.
            </p>
          </div>
        )}

        {/* Kanalvalg. Primær afgør HVOR MEGET modtageren får; her vælges
            HVORDAN det kommer frem. Slås begge fra, får hun ingenting. */}
        <div className="mt-4 space-y-3">
          <NotifKontakt til={notifyEmail} onSkift={() => setNotifyEmail(v => !v)}
            titel="E-mail"
            beskrivelse="Besked ved ny post og den daglige opsummering kl. 08:00." />
          <NotifKontakt til={notifySms} onSkift={() => setNotifySms(v => !v)}
            titel="SMS"
            beskrivelse={recipient.phone
              ? 'Den daglige opsummering kl. 09:00 om ulæst post.'
              : 'Kræver et mobilnummer på modtageren — der er ingen registreret.'} />
          {!notifyEmail && !notifySms && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Begge kanaler er slået fra — modtageren får ingen notifikationer.
            </p>
          )}
        </div>
      </div>
      )}

      {!isSelf && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-medium text-th-text">Selskaber</p>
            <button
              onClick={toggleAll}
              className="text-xs text-brand-400 hover:text-brand-300 underline"
            >
              {allSelected ? 'Fravælg alle' : 'Vælg alle'}
            </button>
          </div>
          {noneSelected && (
            <p className="text-xs text-amber-400 mb-2">Brugeren har ikke adgang til nogen selskaber.</p>
          )}
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {companies.map(c => (
              <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!excluded.includes(c.id)}
                  onChange={() => toggleCompany(c.id)}
                  className="rounded border-th-border"
                />
                <span className={`text-sm ${excluded.includes(c.id) ? 'text-th-text-muted line-through' : 'text-th-text-secondary'}`}>{c.name}</span>
                <span className="text-xs text-th-text-muted">({c.cvr})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <Button onClick={save} disabled={saving} className="bg-brand-500 hover:bg-brand-600 text-white text-sm">
        {saving ? 'Gemmer...' : 'Gem rettigheder'}
      </Button>
    </div>
  );
}

function RecipientsView() {
  const [recipients, setRecipients] = useState([]);
  const [invites, setInvites] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const adding = searchParams.get('ny') === '1';
  const expandedId = searchParams.get('expand');

  const updateParams = (patch) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === null || v === undefined || v === '') next.delete(k);
      else next.set(k, v);
    });
    setSearchParams(next, { replace: true });
  };

  const openAdd = () => updateParams({ ny: '1', expand: null });
  const closeAdd = () => updateParams({ ny: null });
  const toggleExpand = (id) => updateParams({ expand: expandedId === id ? null : id, ny: null });
  const closeExpand = () => updateParams({ expand: null });

  const fetchRecipients = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/recipients`),
      axios.get(`${API}/invites`),
    ])
      .then(([recRes, invRes]) => {
        setRecipients(recRes.data);
        setInvites(invRes.data || []);
      })
      .catch(() => toast.error('Kunne ikke hente modtagere'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRecipients();
    axios.get(`${API}/companies/own`).then(r => setCompanies(r.data)).catch(() => {});
  }, []);

  const deleteRecipient = async (id) => {
    try {
      await axios.delete(`${API}/recipients/${id}`);
      setRecipients(r => r.filter(x => x.id !== id));
      toast.success('Modtager slettet');
    } catch {
      toast.error('Kunne ikke slette');
    }
  };

  const cancelInvite = async (id) => {
    if (!window.confirm('Annullér invitationen? Linket virker ikke bagefter.')) return;
    try {
      await axios.delete(`${API}/invites/${id}`);
      setInvites(list => list.filter(x => x.id !== id));
      toast.success('Invitation annulleret');
    } catch {
      toast.error('Kunne ikke annullere');
    }
  };

  const fmtDate = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return ''; }
  };

  // Hvor mange selskaber en invitation giver adgang til.
  // null = alle, [] = ingen, [ids] = kun disse.
  const companySummary = (allowed) => {
    if (allowed === null || allowed === undefined) return 'Alle selskaber';
    if (allowed.length === 0) return 'Ingen selskaber valgt';
    return `${allowed.length} af ${companies.length} selskaber`;
  };

  const roleLabel = { admin: 'Admin', user: 'Bruger' };

  // ── DigitalBox: personerne som lyse raekker paa samme indsaenkede flade som
  // Selskaber, saa de to administrationssider ligner hinanden
  if (TRE_KOLONNER) {
    const rettigheder = (person, isInvite = false) => expandedId === person.id && (
      // RecipientPermissions tegner selv skillelinjen foroven
      <div className="px-4 pb-4">
        <RecipientPermissions
          key={`${person.id}-${companies.length}`}
          recipient={person}
          companies={companies}
          isInvite={isInvite}
          onSaved={() => { closeExpand(); fetchRecipients(); }}
        />
      </div>
    );

    return (
      <div className="max-w-5xl mx-auto">
        <DbSideHoved titel="Modtagere"
          tekst="Kolleger og revisor, der kan se posten, få den tildelt og sende den videre.">
          {!adding && (
            <button onClick={openAdd} className={DB_KNAP_PRIMAER}><Plus size={15} /> Inviter modtager</button>
          )}
        </DbSideHoved>

        {adding && (
          <div className="mb-6">
            <InviteRecipientForm
              onDone={(inviteId) => { updateParams({ ny: null, expand: inviteId || null }); fetchRecipients(); }}
              onCancel={closeAdd}
            />
          </div>
        )}

        <div className="rounded-[20px] bg-th-inset p-3 sm:p-4 flex flex-col gap-2">
          {loading ? (
            <p className="py-8 text-center text-sm text-th-text-muted">Indlæser…</p>
          ) : recipients.length === 0 && invites.length === 0 ? (
            <p className="py-8 text-center text-sm text-th-text-muted">Ingen modtagere endnu. Inviter en kollega eller jeres revisor.</p>
          ) : (
            <>
              {recipients.map(r => {
                const bekraeftet = r.email_verified && r.phone_verified;
                const status = r.is_self ? 'Dig selv' : r.is_primary !== false ? 'Primær' : 'Eskalering';
                return (
                  <div key={r.id} className="bg-th-card border border-th-border-light rounded-xl">
                    <div className="flex items-center gap-3 px-4 py-3">
                      <DbAvatar tekst={r.name || r.email} farve={r.role === 'admin' ? 'bg-db-route' : 'bg-db-label'} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-th-text truncate">{r.email}</p>
                        <p className="text-xs text-th-text-muted truncate">
                          {[r.name, roleLabel[r.role || 'user'], status, companySummary(r.allowed_companies)].filter(Boolean).join(' · ')}
                        </p>
                        {r.email_bounce_at && (
                          <p className="text-xs text-red-600 dark:text-red-400 mt-0.5">Mails til adressen afvises — ret den, eller fjern modtageren.</p>
                        )}
                      </div>
                      <span className="hidden sm:flex shrink-0">
                        {bekraeftet ? (
                          <DbTag variant="faerdig">Adgang</DbTag>
                        ) : (
                          <DbTag variant="advarsel">
                            Mangler {[!r.email_verified && 'e-mail', !r.phone_verified && 'SMS'].filter(Boolean).join(' og ')}
                          </DbTag>
                        )}
                      </span>
                      <button onClick={() => toggleExpand(r.id)} title={r.is_self ? 'Indstillinger' : 'Rettigheder'} aria-expanded={expandedId === r.id} className={DB_IKONKNAP}><Shield size={15} /></button>
                      {!r.is_self && (
                        <button onClick={() => deleteRecipient(r.id)} title="Fjern modtager" className={`${DB_IKONKNAP} hover:text-red-600`}><Trash2 size={15} /></button>
                      )}
                    </div>
                    {rettigheder(r)}
                  </div>
                );
              })}

              {/* Stiplet kant: invitationen er ikke blevet til en person endnu */}
              {invites.map(inv => (
                <div key={inv.id} className="bg-th-card border border-dashed border-th-border rounded-xl">
                  <div className="flex items-center gap-3 px-4 py-3">
                    <span className="w-9 h-9 rounded-full bg-th-hover flex items-center justify-center shrink-0">
                      <Clock size={16} className="text-amber-500" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-th-text truncate">{inv.email}</p>
                      <p className="text-xs text-th-text-muted truncate">
                        {[inv.name, roleLabel[inv.role || 'user'], companySummary(inv.allowed_companies),
                          inv.expired ? `udløb ${fmtDate(inv.expires)}` : `inviteret ${fmtDate(inv.created_at)}`].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <span className="hidden sm:flex shrink-0">
                      <DbTag variant={inv.expired ? 'fejl' : 'advarsel'}>
                        {inv.expired ? 'Invitation udløbet' : 'Afventer svar'}
                      </DbTag>
                    </span>
                    <button onClick={() => toggleExpand(inv.id)} title="Rettigheder" aria-expanded={expandedId === inv.id} className={DB_IKONKNAP}><Shield size={15} /></button>
                    <button onClick={() => cancelInvite(inv.id)} title="Annullér invitation" className={`${DB_IKONKNAP} hover:text-red-600`}><Trash2 size={15} /></button>
                  </div>
                  {rettigheder(inv, true)}
                </div>
              ))}
            </>
          )}
          <div className="px-1 pt-3 pb-1">
            <p className="text-sm font-medium text-th-text">Kolleger og revisor behøver ikke MitID</p>
            <p className="mt-1 text-sm text-th-text-muted max-w-2xl">
              I giver adgang pr. selskab. Kun den, der tilslutter Digital Post, skal bruge MitID — og kun én gang.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Modtagere</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">Modtagere der kan modtage videresendt post. Afventende invitationer kan tildeles selskaber inden de accepteres.</p>
        </div>
        {!adding && (
          <Button onClick={openAdd} size="sm" className="bg-red-500 hover:bg-red-600 text-white flex items-center gap-2 w-fit">
            <Plus size={16} /> Inviter modtager
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-6">
          <InviteRecipientForm
            onDone={(inviteId) => {
              updateParams({ ny: null, expand: inviteId || null });
              fetchRecipients();
            }}
            onCancel={closeAdd}
          />
        </div>
      )}

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : recipients.length === 0 && invites.length === 0 && !adding ? (
        <div className="bg-th-input border border-th-border rounded-xl p-12 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-th-hover flex items-center justify-center mx-auto">
            <Users size={22} className="text-th-text-muted" />
          </div>
          <p className="text-th-text-secondary font-medium">Ingen modtagere endnu</p>
          <p className="text-th-text-muted text-sm">Inviter en modtager for at give dem adgang til indbakken</p>
        </div>
      ) : (
        <div className="space-y-3">
          {recipients.map(r => (
            <div key={r.id} className="bg-th-input border border-th-border rounded-xl px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="p-2 bg-th-hover rounded-full shrink-0">
                    <User size={18} className="text-th-text-secondary" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-th-text font-medium">{r.name}</p>
                      <span className={`text-xs px-2 py-0.5 rounded border ${
                        r.role === 'admin'
                          ? 'bg-brand-100 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 border-brand-300 dark:border-brand-500/20'
                          : 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-500/20'
                      }`}>
                        {roleLabel[r.role || 'user']}
                      </span>
                      {r.is_self ? (
                        <span className="text-xs px-2 py-0.5 rounded border bg-highlight-100 dark:bg-highlight-500/10 text-highlight-700 dark:text-highlight-400 border-highlight-300 dark:border-highlight-500/20">
                          Dig selv
                        </span>
                      ) : r.is_primary !== false ? (
                        <span className="text-xs px-2 py-0.5 rounded border bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/20">
                          Primær
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded border bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/20">
                          Eskalering
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      <span className="text-xs text-th-text-secondary">{r.email}</span>
                      <span className="text-xs text-th-text-muted">·</span>
                      <span className="text-xs text-th-text-secondary">{r.phone}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <span title="Email" className={`text-xs px-2 py-0.5 rounded border ${r.email_verified ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                    {r.email_verified ? '✓ Email' : '○ Email'}
                  </span>
                  <span title="SMS" className={`text-xs px-2 py-0.5 rounded border ${r.phone_verified ? 'bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/20' : 'bg-amber-500/10 text-amber-400 border-amber-500/20'}`}>
                    {r.phone_verified ? '✓ SMS' : '○ SMS'}
                  </span>
                  <button
                    onClick={() => toggleExpand(r.id)}
                    className="ml-1 text-th-text-faint hover:text-brand-400 transition-colors"
                    title={r.is_self ? 'Indstillinger' : 'Rettigheder'}
                  >
                    <Shield size={16} />
                  </button>
                  {!r.is_self && (
                    <button onClick={() => deleteRecipient(r.id)} className="ml-1 text-th-text-faint hover:text-red-400 transition-colors">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
              {expandedId === r.id && (
                <RecipientPermissions
                  key={`${r.id}-${companies.length}`}
                  recipient={r}
                  companies={companies}
                  onSaved={() => { closeExpand(); fetchRecipients(); }}
                />
              )}
            </div>
          ))}

          {/* Invitationer der endnu ikke er accepteret. De kan tildeles
              selskaber allerede nu — valget træder i kraft ved aktivering. */}
          {invites.map(inv => (
            <div key={inv.id} className="bg-th-input border border-dashed border-th-border rounded-xl px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="p-2 bg-th-hover rounded-full shrink-0">
                    <Clock size={18} className="text-amber-500" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-th-text font-medium">{inv.name || inv.email}</p>
                      <span className={`text-xs px-2 py-0.5 rounded border ${
                        inv.role === 'admin'
                          ? 'bg-brand-100 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 border-brand-300 dark:border-brand-500/20'
                          : 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-500/20'
                      }`}>
                        {roleLabel[inv.role || 'user']}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded border ${
                        inv.expired
                          ? 'bg-red-100 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-300 dark:border-red-500/20'
                          : 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-300 dark:border-amber-500/20'
                      }`}>
                        {inv.expired ? 'Udløbet' : 'Afventer'}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                      {inv.name && <span className="text-xs text-th-text-secondary">{inv.email}</span>}
                      <span className="text-xs text-th-text-muted">
                        {inv.expired
                          ? `Udløb ${fmtDate(inv.expires)}`
                          : `Inviteret ${fmtDate(inv.created_at)} · udløber ${fmtDate(inv.expires)}`}
                      </span>
                    </div>
                    <p className={`text-xs mt-1 ${
                      (inv.allowed_companies || []).length === 0 && inv.allowed_companies !== null
                        ? 'text-amber-500'
                        : 'text-th-text-muted'
                    }`}>
                      {companySummary(inv.allowed_companies)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => toggleExpand(inv.id)}
                    className="ml-1 text-th-text-faint hover:text-brand-400 transition-colors"
                    title="Rettigheder"
                  >
                    <Shield size={16} />
                  </button>
                  <button
                    onClick={() => cancelInvite(inv.id)}
                    className="ml-1 text-th-text-faint hover:text-red-400 transition-colors"
                    title="Annullér invitation"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              {expandedId === inv.id && (
                <RecipientPermissions
                  key={`${inv.id}-${companies.length}`}
                  recipient={inv}
                  companies={companies}
                  isInvite
                  onSaved={() => { closeExpand(); fetchRecipients(); }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Companies ───────────────────────────────────────────────────────────────

function CompaniesView() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchCompanies = () => {
    setLoading(true);
    axios.get(`${API}/admin/companies`)
      .then(r => setCompanies(r.data))
      .catch(() => toast.error('Ikke adgang'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchCompanies(); }, []);

  const deleteCompany = async (id) => {
    if (!window.confirm('Slet dette selskab?')) return;
    try {
      await axios.delete(`${API}/companies/${id}`);
      setCompanies(prev => prev.filter(c => c.id !== id));
      toast.success('Selskab slettet');
    } catch (e) { toast.error('Fejl ved sletning'); }
  };

  const filtered = companies.filter(c => {
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.cvr || '').includes(q) ||
      (c.user_email || '').toLowerCase().includes(q) ||
      (c.user_contact_name || '').toLowerCase().includes(q)
    );
  });

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">Selskaber</h1>
          <p className="text-sm sm:text-base text-th-text-secondary">{companies.length} selskaber registreret</p>
        </div>
        <input
          type="text"
          placeholder="Søg navn, CVR eller email..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="bg-th-hover border border-th-border-light text-th-text-secondary placeholder-th-text-muted rounded-lg px-4 py-2 text-sm w-full sm:w-64 focus:outline-none focus:border-th-border-light"
        />
      </div>

      {loading ? (
        <div className="text-th-text-secondary py-12 text-center">Indlæser...</div>
      ) : filtered.length === 0 ? (
        <div className="text-th-text-muted py-12 text-center">Ingen selskaber fundet</div>
      ) : (
        <div className="space-y-2">
          {filtered.map((c) => (
            <div key={c.id} className="bg-th-input border border-th-border rounded-xl px-5 py-4">
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-medium text-th-text-secondary">{c.name}</span>
                    <span className="font-mono text-th-text-secondary bg-th-hover border border-th-border-light px-2 py-0.5 rounded text-xs">{c.cvr}</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-th-text-muted flex-wrap">
                    {c.user_contact_name && <span>{c.user_contact_name}</span>}
                    {c.user_email && <span>{c.user_email}</span>}
                    {c.created_at && <span>{formatDate(c.created_at)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => deleteCompany(c.id)} className="text-th-text-faint hover:text-red-400 transition-colors" title="Slet">
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/></svg>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Guide ────────────────────────────────────────────────────────────────────

function CopyButton({ value, size = 14 }) {
  const [copied, setCopied] = React.useState(false);
  const handle = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button onClick={handle} className="text-th-text-muted hover:text-th-text-secondary transition-colors" title="Kopiér">
      {copied
        ? <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
        : <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
      }
    </button>
  );
}

function CodeValue({ value }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="bg-th-hover border border-th-border-light text-th-text-secondary px-2 py-0.5 rounded text-sm font-mono">{value}</span>
      <CopyButton value={value} />
    </span>
  );
}

function GuideView({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = React.useState(1);
  const [flowType, setFlowType] = React.useState(null); // 'eboks' | 'digitalpost'
  const [searchQuery, setSearchQuery] = React.useState('');
  const [suggestions, setSuggestions] = React.useState([]);
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [selectedCompany, setSelectedCompany] = React.useState(null);
  const [companyId, setCompanyId] = React.useState(null);
  const [stepLoading, setStepLoading] = React.useState(false);
  const [dpApiKey, setDpApiKey] = React.useState('');
  const inboxUrl = inboxEndpoint(companyId || user?.id || '...');
  const searchDebounceRef = React.useRef(null);

  // Guiden kan aabnes for et selskab der ALLEREDE er oprettet — fx fra
  // "Opsæt Digital Post" på selskabskortet. Så springes de to første trin
  // over: selskabet er fundet, og posttypen er valgt af linket.
  const linketSelskab = searchParams.get('selskab');
  // Selskabet fra registreringen: backenden opretter det med det samme, saa en
  // ny kunde ikke skal soege sit eget CVR frem igen. Findes det, laases guiden
  // paa samme maade som naar den aabnes fra et selskabskort.
  const [autoSelskab, setAutoSelskab] = React.useState(null);
  const laastSelskab = !!linketSelskab || !!autoSelskab;
  const FOERSTE_FLOWTRIN = 3;
  const linkBrugt = React.useRef(false);
  const gemtTrin = React.useRef(null);
  React.useEffect(() => {
    if (!linketSelskab || linkBrugt.current) return;
    linkBrugt.current = true;
    const navn = searchParams.get('navn') || '';
    setCompanyId(linketSelskab);
    setSelectedCompany({ cvr: searchParams.get('cvr') || '', name: navn });
    setSearchQuery(navn);
    setFlowType(searchParams.get('flow') === 'eboks' ? 'eboks' : 'digitalpost');
    setStep(FOERSTE_FLOWTRIN);
    // ?trin= genoptages, naar antallet af trin kendes — se effekten ved TOTAL
    const trin = parseInt(searchParams.get('trin'), 10);
    if (trin > 1) gemtTrin.current = trin;
  }, [linketSelskab, searchParams]);

  // Koeres kun naar guiden aabnes uden ?selskab= — altsaa den vej en frisk
  // bruger kommer ind. Vi matcher paa brugerens eget registrerings-CVR frem for
  // "det foerste selskab", saa en kunde med flere selskaber ikke faar et
  // vilkaarligt af dem valgt for sig.
  const autoBrugt = React.useRef(false);
  React.useEffect(() => {
    if (linketSelskab || autoBrugt.current) return;
    const regCvr = (user?.company_cvr || '').replace(/\D/g, '');
    if (regCvr.length !== 8) return;
    autoBrugt.current = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/companies/own`);
        const eget = (r.data || []).find(c => (c.cvr || '').replace(/\D/g, '') === regCvr);
        if (!eget) return;
        setAutoSelskab(eget);
        setCompanyId(eget.id);
        setSelectedCompany({ cvr: eget.cvr, name: eget.name });
        setSearchQuery(eget.name || '');
        setStep(FOERSTE_FLOWTRIN);
      } catch (e) {
        // Guiden skal stadig kunne bruges manuelt hvis opslaget fejler.
        console.error('Kunne ikke hente eget selskab:', e);
      }
    })();
  }, [linketSelskab, user]);

  React.useEffect(() => {
    if (selectedCompany) return;
    if (searchQuery.length < 2) { setSuggestions([]); setShowSuggestions(false); return; }
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(async () => {
      try {
        const params = { q: searchQuery.trim() };
        // Samme grund som paa signup-siden: direkte kald er cross-origin og
        // bliver blokeret paa alle brands undtagen hentpost.
        const r = await axios.get(`${API}/public/search-company`, { params });
        const data = Array.isArray(r.data) ? r.data : (r.data?.results ?? r.data?.companies ?? []);
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      } catch { setSuggestions([]); }
    }, 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedCompany]);

  const selectCompany = (c) => {
    setSelectedCompany(c);
    setSearchQuery(c.name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  const companySearchStep = {
    title: 'Find dit selskab',
    content: (
      <div className="space-y-4 text-th-text-secondary">
        <p>Søg på selskabsnavn eller CVR-nummer for at finde det selskab du vil tilføje.</p>
        <div className="relative">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="absolute left-3 top-1/2 -translate-y-1/2 text-th-text-muted"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            autoFocus
            type="text"
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setSelectedCompany(null); }}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Søg virksomhed eller CVR..."
            className="w-full pl-10 h-12 bg-th-input border border-th-border-light text-th-text placeholder:text-th-text-muted rounded-md text-base focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-colors"
          />
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-th-input border border-th-border-light rounded-lg shadow-xl z-[100] overflow-hidden">
              {suggestions.map((c, i) => (
                <button key={i} type="button" onClick={() => selectCompany(c)}
                  className="w-full text-left px-4 py-3 hover:bg-th-hover transition-colors border-b border-th-border last:border-0">
                  <div className="flex items-center gap-3">
                    <div className="p-1.5 bg-brand-500/10 rounded"><Building size={16} className="text-brand-500" /></div>
                    <div>
                      <p className="text-th-text font-medium text-sm">{c.name}</p>
                      <p className="text-xs text-th-text-secondary">{c.cvr && `CVR: ${c.cvr}`}{c.city && ` · ${c.city}`}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        {selectedCompany && (
          <div className="flex items-center gap-2 bg-green-100 dark:bg-green-500/10 border border-green-300 dark:border-green-500/30 rounded-lg px-4 py-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400 shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            <span className="text-sm text-green-700 dark:text-green-300 font-medium">{selectedCompany.name} — CVR {selectedCompany.cvr}</span>
          </div>
        )}
      </div>
    ),
  };

  const chooseFlowStep = {
    title: 'Vælg posttype',
    content: (
      <div className="space-y-3">
        <p className="text-th-text-secondary">Hvilken type post vil du forbinde?</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={() => setFlowType('eboks')}
            className={`text-left p-4 rounded-xl border-2 transition-colors ${flowType === 'eboks' ? 'border-red-500 bg-red-500/5' : 'border-th-border hover:border-th-border-light'}`}
          >
            <p className="font-semibold text-th-text mb-1">e-Boks</p>
            <p className="text-xs text-th-text-muted">Videresend post fra e-Boks Erhverv via XML-integration</p>
          </button>
          <button
            onClick={() => setFlowType('digitalpost')}
            className={`text-left p-4 rounded-xl border-2 transition-colors ${flowType === 'digitalpost' ? 'border-red-500 bg-red-500/5' : 'border-th-border hover:border-th-border-light'}`}
          >
            <p className="font-semibold text-th-text mb-1">Digital Post</p>
            <p className="text-xs text-th-text-muted">Modtag Digital Post (MeMo) via virk.dk</p>
          </button>
        </div>
        {flowType && (
          <div className="flex items-center gap-2 bg-green-100 dark:bg-green-500/10 border border-green-300 dark:border-green-500/30 rounded-lg px-4 py-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400 shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            <span className="text-sm text-green-700 dark:text-green-300 font-medium">{flowType === 'eboks' ? 'e-Boks' : 'Digital Post'} valgt</span>
          </div>
        )}
      </div>
    ),
  };

  const eboksSteps = [
    {
      title: 'Log ind på e-Boks Erhverv',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Gå til <a href="https://www.e-boks.dk/erhverv" target="_blank" rel="noreferrer" className="text-red-400 hover:underline font-medium">e-boks.dk/erhverv</a> og log ind med <strong className="text-th-text-secondary">MitID</strong>.</p>
          <p className="text-th-text-secondary">Vælg den bruger, der er knyttet til din virksomhed.</p>
        </div>
      ),
    },
    {
      title: 'Find videresendelsesindstillingen',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Følg denne sti i menuen:</p>
          <ol className="space-y-2 text-th-text-secondary">
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">1.</span> Åbn <strong className="text-th-text-secondary">Menu</strong></li>
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">2.</span> Vælg <strong className="text-th-text-secondary">Automatisk videresendelse af post</strong></li>
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">3.</span> Find sektionen <strong className="text-th-text-secondary">Integration til system via API (XML)</strong></li>
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">4.</span> Klik på <span className="text-red-400 font-semibold">Opsæt integration til system</span></li>
          </ol>
        </div>
      ),
    },
    {
      title: 'Indsæt din modtage-URL',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Indsæt følgende URL i <strong className="text-th-text-secondary">URL-feltet</strong>:</p>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-th-hover border border-th-border-light text-th-text-secondary px-3 py-1.5 rounded text-sm font-mono break-all">{inboxUrl}</span>
            <CopyButton value={inboxUrl} size={16} />
          </div>
        </div>
      ),
    },
    {
      title: 'Udfyld kontaktoplysninger',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Udfyld kontaktoplysningerne med følgende værdier:</p>
          <ul className="space-y-2">
            <li className="flex items-center gap-2"><strong className="text-th-text-secondary w-28">Navn:</strong><CodeValue value={BRAND.digitalPost.systemName} /></li>
            <li className="flex items-center gap-2"><strong className="text-th-text-secondary w-28">E-mailadresse:</strong><CodeValue value={BRAND.digitalPost.contactEmail} /></li>
            <li className="flex items-center gap-2"><strong className="text-th-text-secondary w-28">Telefonnummer:</strong><CodeValue value={BRAND.digitalPost.contactPhone} /></li>
          </ul>
        </div>
      ),
    },
    {
      title: 'Gem og test integrationen',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Klik <strong className="text-th-text-secondary">Gem</strong> for at aktivere opsætningen.</p>
          <p className="text-th-text-secondary">Bekræft derefter at alt virker:</p>
          <ol className="space-y-2 text-th-text-secondary">
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">1.</span> Klik <strong className="text-th-text-secondary">Rediger</strong></li>
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">2.</span> Klik <strong className="text-th-text-secondary">Test system</strong></li>
            <li className="flex items-start gap-2"><span className="text-red-400 font-bold mt-0.5">3.</span> Tjek at testbeskeden dukker op i din {BRAND.name} indbakke</li>
          </ol>
        </div>
      ),
    },
  ];

  const digitalPostSteps = [
    {
      title: 'Log ind på rettighedsportalen',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Gå til <a href="https://rettighedsportal.digitalpost.dk/" target="_blank" rel="noreferrer" className="text-red-400 hover:underline font-medium">rettighedsportal.digitalpost.dk</a> og log ind med <strong className="text-th-text-secondary">MitID</strong> som <strong className="text-th-text-secondary">erhvervsbruger</strong>.</p>
        </div>
      ),
    },
    {
      title: 'Giv adgang til virksomheden',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Gå til <strong className="text-th-text-secondary">Adgange til virksomheden</strong> og tryk på <strong className="text-th-text-secondary">+ Giv adgang</strong>.</p>
        </div>
      ),
    },
    // DigitalBox: brugertype og CVR er samme skaermbillede i Rettighedsportalen,
    // saa de staar paa samme trin. Hentpost beholder dem hver for sig.
    ...(TRE_KOLONNER ? [{
      title: 'Vælg virksomhed',
      content: (
        <div className="space-y-4 text-th-text-secondary">
          <div className="space-y-2">
            <p><strong className="text-th-text">1.</strong> Vælg brugertype:</p>
            <div className="flex items-center gap-2">
              <span className="bg-th-hover border border-th-border-light text-th-text px-3 py-1.5 rounded text-sm font-medium">Virksomhed</span>
            </div>
          </div>
          <div className="space-y-2">
            <p><strong className="text-th-text">2.</strong> Indtast {BRAND.digitalPost.operatorName} CVR-nummer:</p>
            <div className="flex items-center gap-2">
              <span className="bg-th-hover border border-th-border-light text-th-text px-3 py-1.5 rounded text-sm font-mono">{BRAND.digitalPost.operatorCvr}</span>
              <CopyButton value={BRAND.digitalPost.operatorCvr} size={16} />
            </div>
          </div>
          <p>Tryk på <strong className="text-th-text-secondary">Fremsøg virksomhed</strong> og derefter <strong className="text-th-text-secondary">Næste</strong>.</p>
        </div>
      ),
    }] : [
      {
        title: 'Brugertype',
        content: (
          <div className="space-y-3 text-th-text-secondary">
            <p>Vælg brugertype:</p>
            <div className="flex items-center gap-2">
              <span className="bg-th-hover border border-th-border-light text-th-text px-3 py-1.5 rounded text-sm font-medium">Virksomhed</span>
            </div>
          </div>
        ),
      },
      {
        title: 'CVR-nummer',
        content: (
          <div className="space-y-3 text-th-text-secondary">
            <p>Indtast {BRAND.digitalPost.operatorName} CVR-nummer:</p>
            <div className="flex items-center gap-2">
              <span className="bg-th-hover border border-th-border-light text-th-text px-3 py-1.5 rounded text-sm font-mono">{BRAND.digitalPost.operatorCvr}</span>
              <CopyButton value={BRAND.digitalPost.operatorCvr} size={16} />
            </div>
            <p>Tryk på <strong className="text-th-text-secondary">Fremsøg virksomhed</strong> og derefter <strong className="text-th-text-secondary">Næste</strong>.</p>
          </div>
        ),
      },
    ]),
    {
      title: 'Konfigurer adgangen',
      content: (
        <div className="space-y-3 text-th-text-secondary">
          <p>Udfyld følgende indstillinger:</p>
          <ul className="space-y-2">
            <li className="flex items-start gap-2"><strong className="text-th-text-secondary w-32">Udløbsdato:</strong><span>Lad feltet stå tomt for permanent adgang</span></li>
            <li className="flex items-start gap-2"><strong className="text-th-text-secondary w-32">Rolle:</strong><span>Scroll til bunden og vælg <strong className="text-th-text-secondary">Systemadministrator</strong></span></li>
          </ul>
          <p className="mt-2">Tryk på <strong className="text-th-text-secondary">Send</strong> for at færdiggøre.</p>
          <div className="bg-brand-50 dark:bg-brand-500/10 border border-brand-200 dark:border-brand-500/20 rounded-lg px-4 py-3 text-sm text-brand-700 dark:text-brand-300">
            Vi klarer resten — når vi har modtaget adgangen, opsætter vi Digital Post for dit selskab og giver dig besked.
          </div>
        </div>
      ),
    },
  ];

  // Knappen på selskabskortet spærrer allerede, men adressen kan skrives i
  // hånden. Spærringen hører derfor til her, hvor trinene sættes sammen.
  const maaForbinde = KAN_FORBINDE_KANALER(user?.subscription_tier);
  const opgraderTrin = {
    title: 'Opgrader for at forbinde Digital Post eller e-Boks',
    content: (
      <div className="space-y-4 text-th-text-secondary">
        <p>
          På <strong className="text-th-text">Gratis</strong> modtager du post ved at sende den til
          selskabets e-mailadresse. Det koster ikke noget og kan bruges med det samme.
        </p>
        <p>
          <strong className="text-th-text">Digital Post</strong> og <strong className="text-th-text">e-Boks</strong> er
          integrationer, vi opsætter og driver for dig, så posten kommer ind af sig selv. De kræver
          mindst <strong className="text-th-text">Solo</strong>.
        </p>
        <button
          onClick={() => navigate('/profil')}
          className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
        >
          Se abonnementer
        </button>
      </div>
    ),
  };
  const flowSteps = !maaForbinde
    ? [opgraderTrin]
    : (flowType === 'digitalpost' ? digitalPostSteps : eboksSteps);
  const steps = [companySearchStep, chooseFlowStep, ...flowSteps];
  const TOTAL = steps.length;
  // Kommer man fra et selskab der allerede findes, er "find selskab" og "vælg
  // posttype" ikke trin man skal igennem — de er besvaret af linket. Så tælles
  // der fra 1 i selve opsætningen, og de to bliver hverken vist eller talt med.
  const SPRUNGET_OVER = laastSelskab ? FOERSTE_FLOWTRIN - 1 : 0;
  const visteTrin = steps.slice(SPRUNGET_OVER);
  const current = steps[step - 1];
  const done = step > TOTAL;
  // Trinnet staar i URL'en (?trin=), saa en genindlaesning bliver paa samme
  // trin. Kun naar guiden er aabnet for et eksisterende selskab: ellers er
  // selskabet endnu ikke fundet, og der er intet at vende tilbage til.
  React.useEffect(() => {
    if (!linketSelskab || step < FOERSTE_FLOWTRIN) return;
    if (gemtTrin.current) {
      // Aldrig forbi sidste trin: slutskaermen naas kun ved at trykke Afslut
      const maal = Math.min(FOERSTE_FLOWTRIN + gemtTrin.current - 1, TOTAL);
      gemtTrin.current = null;
      if (maal !== step) { setStep(maal); return; }
    }
    const ny = new URLSearchParams(searchParams);
    if (flowType) ny.set('flow', flowType);
    if (done) ny.delete('trin'); else ny.set('trin', String(step - FOERSTE_FLOWTRIN + 1));
    if (ny.toString() !== searchParams.toString()) setSearchParams(ny, { replace: true });
  }, [linketSelskab, step, done, TOTAL, flowType, searchParams, setSearchParams]);

  // Naar en kanal er sat op, tilbydes den anden — men kun hvis den ikke
  // allerede er tilsluttet. Status hentes foerst paa slutskaermen, efter at
  // "Afslut" har markeret denne kanal, saa svaret gaelder selskabet nu.
  const andenKanal = flowType === 'eboks' ? 'digitalpost' : 'eboks';
  const [andenTilsluttet, setAndenTilsluttet] = React.useState(null);
  React.useEffect(() => {
    if (!done || !companyId || !laastSelskab) return;
    let aktiv = true;
    setAndenTilsluttet(null);
    axios.get(`${API}/companies`).then(r => {
      const c = (r.data || []).find(x => x.id === companyId);
      if (aktiv && c) setAndenTilsluttet(!!c[andenKanal === 'eboks' ? 'setup_eboks' : 'setup_digitalpost']);
    }).catch(() => {});
    return () => { aktiv = false; };
  }, [done, companyId, laastSelskab, andenKanal]);
  const naesteKanal = done && andenTilsluttet === false ? andenKanal : null;

  return (
    <div className={`max-w-2xl mx-auto${TRE_KOLONNER ? ' db-guide' : ''}`}>
      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-heading font-bold text-th-text mb-1 sm:mb-2">
        {laastSelskab ? (flowType === 'eboks' ? 'Opsæt e-Boks' : 'Opsæt Digital Post') : 'Tilføj selskab'}
      </h1>
      <p className="text-th-text-secondary mb-8">
        {laastSelskab
          ? <>Sådan forbinder du <strong className="text-th-text-secondary">{selectedCompany?.name || 'dit selskab'}</strong>{selectedCompany?.cvr ? ` (CVR ${selectedCompany.cvr})` : ''} — trin for trin.</>
          : <>Forbind dit selskab til {BRAND.name} trin for trin.</>}
      </p>

      {/* Progress */}
      {TRE_KOLONNER ? (
        <DbTrinLinje antal={visteTrin.length} aktiv={done ? visteTrin.length + 1 : step - SPRUNGET_OVER} />
      ) : (
      <div className="flex items-center justify-center gap-2 mb-8">
        {visteTrin.map((_, i) => (
          <React.Fragment key={i}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${
              i + 1 + SPRUNGET_OVER < step ? 'bg-green-500 text-white' :
              i + 1 + SPRUNGET_OVER === step && !done ? 'bg-red-500 text-white' :
              'bg-th-hover text-th-text-muted'
            }`}>
              {i + 1 + SPRUNGET_OVER < step
                ? <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                : i + 1}
            </div>
            {i < visteTrin.length - 1 && (
              <div className={`w-8 sm:w-12 h-0.5 shrink-0 transition-colors ${i + 1 + SPRUNGET_OVER < step ? 'bg-green-500' : 'bg-th-hover'}`} />
            )}
          </React.Fragment>
        ))}
      </div>
      )}

      {done ? (
        <div className="bg-th-input border border-green-300 dark:border-green-500/30 rounded-xl p-8 text-center space-y-3">
          <div className="w-14 h-14 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center mx-auto">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 className="text-xl font-semibold text-th-text">Selskab forbundet!</h2>
          <p className="text-th-text-secondary">
            {flowType === 'digitalpost'
              ? `Adgangen er nu sendt til ${BRAND.digitalPost.operatorName}. Vi opsætter Digital Post for dit selskab og giver dig besked når det er klar.`
              : `Din e-Boks er nu forbundet til ${BRAND.name}. Når e-Boks sender en testbesked, vil du kunne se den i din indbakke.`}
          </p>
          {naesteKanal && (
            <button
              onClick={() => {
                // Samme selskab, ny kanal. URL'en foelger med via ?flow= og ?trin=
                setFlowType(naesteKanal);
                setStep(FOERSTE_FLOWTRIN);
              }}
              className="inline-flex items-center gap-2 border border-transparent bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2 mr-2">
              Fortsæt med {naesteKanal === 'eboks' ? 'e-Boks' : 'Digital Post'}
            </button>
          )}
          <button onClick={() => navigate('/indbakke')} className={naesteKanal
            ? 'inline-flex items-center gap-2 border border-th-border text-th-text-secondary hover:bg-th-hover px-5 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2'
            : 'inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors mt-2'}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>
            Gå til indbakke
          </button>
          <div><button onClick={() => { setStep(1); setFlowType(null); setSelectedCompany(null); setSearchQuery(''); setCompanyId(null); setDpApiKey(''); setAutoSelskab(null); }} className="text-sm text-th-text-faint hover:text-th-text-secondary underline">Tilføj endnu et selskab</button></div>
        </div>
      ) : (
        <div className="bg-th-input border border-th-border rounded-xl">
          <div className="px-6 py-4 border-b border-th-border flex items-center gap-3">
            <span className="text-xs font-medium text-th-text-muted uppercase tracking-wide">Trin {step - SPRUNGET_OVER} af {TOTAL - SPRUNGET_OVER}</span>
            {flowType && step > 2 && TRE_KOLONNER && (
              // Samme maerke som kilden i indbakken: moerkt med kanalens prik
              <DbTag variant="moerk" prik={KILDE_PRIK[flowType === 'digitalpost' ? 'memo' : 'eboks']}>
                {flowType === 'digitalpost' ? 'Digital Post' : 'e-Boks'}
              </DbTag>
            )}
            {flowType && step > 2 && !TRE_KOLONNER && (
              <span className={`text-xs px-2 py-0.5 rounded border ${flowType === 'digitalpost' ? 'bg-brand-100 dark:bg-brand-500/10 text-brand-700 dark:text-brand-400 border-brand-300 dark:border-brand-500/20' : 'bg-orange-100 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-500/20'}`}>
                {flowType === 'digitalpost' ? 'Digital Post' : 'e-Boks'}
              </span>
            )}
          </div>
          <div className="p-6 space-y-5">
            <h2 className="text-lg font-semibold text-th-text">{current.title}</h2>
            {current.content}
          </div>
          <div className="px-6 py-4 border-t border-th-border flex items-center justify-between">
            {step > (laastSelskab ? FOERSTE_FLOWTRIN : 1)
              ? <button onClick={() => setStep(s => s - 1)} className="text-sm text-th-text-muted hover:text-th-text-secondary transition-colors">← Forrige</button>
              : <span />
            }
            <button
              onClick={async () => {
                if (step === 1) {
                  // Selskabet er allerede oprettet — enten i et tidligere klik
                  // eller fordi guiden blev aabnet fra et eksisterende selskab.
                  // Uden dette gav "← Forrige" og frem igen et dublet-selskab.
                  if (companyId) { setStep(s => s + 1); return; }
                  // Trin 1: Opret selskab
                  setStepLoading(true);
                  try {
                    const r = await axios.post(`${API}/companies`, { cvr: selectedCompany.cvr, name: selectedCompany.name });
                    setCompanyId(r.data.id);
                    setStep(s => s + 1);
                  } catch (e) {
                    if (e.response?.data?.detail === 'upgrade_required') {
                      toast.error('Du skal opgradere dit abonnement for at oprette selskaber.', { duration: 5000 });
                      navigate('/profil');
                    } else {
                      toast.error(e.response?.data?.detail || 'Fejl ved oprettelse');
                    }
                  } finally { setStepLoading(false); }
                } else if (step === 2) {
                  // Trin 2: Vælg flow
                  setStep(s => s + 1);
                } else if (step === TOTAL && !maaForbinde) {
                  navigate('/profil');
                } else if (step === TOTAL) {
                  // Sidste trin — notify admin
                  setStepLoading(true);
                  try {
                    await axios.post(`${API}/companies/${companyId}/onboarding-complete`, { flow_type: flowType });
                  } catch (e) {
                    console.error('Onboarding notification failed:', e);
                  } finally { setStepLoading(false); }
                  setStep(s => s + 1);
                } else {
                  setStep(s => s + 1);
                }
              }}
              disabled={(step === 1 && !selectedCompany) || (step === 2 && !flowType) || stepLoading}
              className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {stepLoading ? 'Gemmer...' : step === TOTAL ? 'Afslut' : 'Færdig, næste trin'} →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ────────────────────────────────────────────────────────────────

// ─── Paamindelse om e-mailbekraeftelse ────────────────────────────────────────
//
// Verifikationen spaerrer ikke adgangen — den ville skraemme nye kunder vaek,
// foer de har set produktet. I stedet staar den her, over alt indhold og paa
// hver side, indtil adressen er bekraeftet. Kodefeltet er i banneret, saa der
// ikke er en side at finde frem til foerst.
function EmailBekraeftBanner({ user, fetchUser }) {
  const [kode, setKode] = React.useState('');
  const [travl, setTravl] = React.useState(false);

  if (!user || user.email_verified) return null;

  const sendIgen = async () => {
    setTravl(true);
    try {
      await axios.post(`${API}/profile/verify-email/send`);
      toast.success(`Ny kode sendt til ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende koden');
    } finally { setTravl(false); }
  };

  const bekraeft = async () => {
    setTravl(true);
    try {
      await axios.post(`${API}/profile/verify-email/confirm`, { code: kode });
      toast.success('Din e-mailadresse er bekræftet');
      setKode('');
      await fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert kode');
    } finally { setTravl(false); }
  };

  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <AlertTriangle size={20} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Bekræft din e-mailadresse</p>
            <p className="text-sm text-amber-800 dark:text-amber-300/90 break-words">
              Uden en bekræftet adresse kan vi ikke levere din post sikkert. Indtast koden fra {user.email} —
              har du ingen, så tryk "Send kode".
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input
            value={kode}
            onChange={(e) => setKode(e.target.value.replace(/\D/g, ''))}
            onKeyDown={(e) => e.key === 'Enter' && kode.length >= 4 && !travl && bekraeft()}
            inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="Kode"
            className="w-24 h-9 px-3 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-white dark:bg-th-input text-th-text tracking-widest text-sm"
          />
          <button onClick={bekraeft} disabled={kode.length < 4 || travl}
            className="h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50">
            Bekræft
          </button>
          <button onClick={sendIgen} disabled={travl}
            className="h-9 px-2 text-sm text-amber-800 dark:text-amber-300 underline disabled:opacity-50">
            Send kode
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Paamindelse om mobilnummer ───────────────────────────────────────────────
//
// Vises foerst naar planen faktisk sender SMS. Paa Gratis og Solo er et
// ubekraeftet nummer uden betydning, og en paamindelse om noget, der ikke
// bruges, laerer folk at overse bannere. Skifter kunden til Holding eller Pro,
// dukker den op af sig selv — den er udregnet, ikke udloest af en haendelse.
function TelefonBekraeftBanner({ user, fetchUser }) {
  const [nummer, setNummer] = React.useState(user?.phone || '');
  const [kode, setKode] = React.useState('');
  const [trin, setTrin] = React.useState('start');   // start | kode
  const [travl, setTravl] = React.useState(false);

  const plan = getTierLabel(user?.subscription_tier);
  // Planen afgoer, om der SENDES notifikationer pr. SMS — ikke om vi skal kunne
  // faa fat i brugeren. Derfor er der ingen planspaerre her laengere: kan vi
  // ikke naa nogen, naar noget haster, hjaelper det ikke at hun er paa en plan
  // uden SMS-notifikationer.
  //
  // En spaerre paa netop 'invited' ville ikke have raekket: flere konti har
  // slet intet subscription_tier, og getTierLabel falder saa tilbage til solo
  // (sms: false). Den inviterede bruger, det hele startede med, var netop en
  // af dem.
  if (!user || user.phone_verified) return null;

  const sendKode = async () => {
    const tlf = (nummer || '').trim();
    if (tlf.replace(/\D/g, '').length < 8) { toast.error('Indtast dit mobilnummer'); return; }
    setTravl(true);
    try {
      await axios.post(`${API}/profile/phone/send-sms`, { phone: tlf });
      toast.success(`Kode sendt til ${tlf}`);
      setTrin('kode');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sende SMS');
    } finally { setTravl(false); }
  };

  const bekraeft = async () => {
    setTravl(true);
    try {
      await axios.post(`${API}/profile/phone/verify`, { phone: (nummer || '').trim(), code: kode });
      toast.success('Mobilnummeret er bekræftet');
      await fetchUser();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Forkert kode');
    } finally { setTravl(false); }
  };

  const felt = "h-9 px-3 rounded-lg border border-amber-300 dark:border-amber-500/40 bg-white dark:bg-th-input text-th-text text-sm";
  return (
    <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/10 px-4 py-3">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3 min-w-0">
          <Phone size={20} className="shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div className="min-w-0">
            <p className="font-semibold text-amber-900 dark:text-amber-200">Bekræft dit mobilnummer</p>
            <p className="text-sm text-amber-800 dark:text-amber-300/90">
              {plan.sms
                ? `Din plan ${plan.name} sender SMS om ulæst post. Uden et bekræftet nummer får du dem ikke.`
                : 'Vi bruger nummeret, hvis vi får brug for at få fat i dig hurtigt. Bekræft det, så vi ved, det virker.'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {trin === 'start' ? (
            <>
              <input value={nummer} onChange={(e) => setNummer(e.target.value)} type="tel"
                placeholder="Mobilnummer" className={`${felt} w-36`} />
              <button onClick={sendKode} disabled={travl}
                className="h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50">
                Send kode
              </button>
            </>
          ) : (
            <>
              <input value={kode} onChange={(e) => setKode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => e.key === 'Enter' && kode.length >= 4 && !travl && bekraeft()}
                inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="Kode"
                className={`${felt} w-24 tracking-widest`} autoFocus />
              <button onClick={bekraeft} disabled={kode.length < 4 || travl}
                className="h-9 px-3 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium disabled:opacity-50">
                Bekræft
              </button>
              <button onClick={() => { setTrin('start'); setKode(''); }}
                className="h-9 px-2 text-sm text-amber-800 dark:text-amber-300 underline">
                Skift nummer
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Selskabsindstillinger ────────────────────────────────────────────────────
//
// Alt det udadvendte hang paa KONTOEN: navn, logo og tekst i underskriftsmails.
// En holdingkonto sendte derfor alle sine selskabers post i det foerste
// selskabs navn. Her saettes det pr. selskab, af den der ejer det.
// Hvor den underskrevne kontrakt sendes hen. Det er den samme adresse som
// retur_url_signed pr. dokument — forskellen er, hvem der bestemmer den: her
// gaelder den alt, selskabet faar underskrevet, ogsaa det der bliver sendt
// fra brugerfladen, hvor ingen har haft mulighed for at saette et felt.
function Webhook({ selskab }) {
  const [url, setUrl] = useState('');
  const [gemtUrl, setGemtUrl] = useState('');
  const [harHemmelighed, setHarHemmelighed] = useState(false);
  const [henter, setHenter] = useState(true);
  const [travl, setTravl] = useState(false);
  const [egenVises, setEgenVises] = useState(false);
  const [egen, setEgen] = useState('');
  // Hemmeligheden vises én gang. Den lever her, indtil siden forlades.
  const [frisk, setFrisk] = useState(null);
  const navigate = useNavigate();

  const hent = React.useCallback(async () => {
    setHenter(true); setFrisk(null); setEgenVises(false); setEgen('');
    try {
      const r = await axios.get(`${API}/companies/${selskab.id}/settings`);
      setUrl(r.data.webhook_url || '');
      setGemtUrl(r.data.webhook_url || '');
      setHarHemmelighed(!!r.data.webhook_hemmelighed_sat);
    } catch {
      toast.error('Kunne ikke hente webhook-indstillingen');
    } finally { setHenter(false); }
  }, [selskab.id]);

  useEffect(() => { hent(); }, [hent]);

  const gem = async () => {
    setTravl(true);
    try {
      await axios.put(`${API}/companies/${selskab.id}/settings`, { webhook_url: url.trim() });
      setGemtUrl(url.trim());
      toast.success(url.trim() ? 'Webhook gemt' : 'Webhook slået fra');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme webhook');
    } finally { setTravl(false); }
  };

  const saetHemmelighed = async (vaerdi) => {
    if (harHemmelighed && !window.confirm(
      `Erstat den nuværende hemmelighed for ${selskab.name}?\n\nKaldene bliver ved med at virke, men modtageren kan ikke kontrollere signaturen, før den nye er lagt ind i deres system.`
    )) return;
    setTravl(true);
    try {
      const r = await axios.post(`${API}/companies/${selskab.id}/webhook/hemmelighed`,
                                 vaerdi ? { hemmelighed: vaerdi } : {});
      setFrisk(r.data.hemmelighed);
      setHarHemmelighed(true);
      setEgenVises(false); setEgen('');
      toast.success(r.data.egen ? 'Hemmeligheden er gemt' : 'Ny hemmelighed lavet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke sætte hemmeligheden');
    } finally { setTravl(false); }
  };

  const aendret = url.trim() !== gemtUrl;

  return (
    <Card className="p-6 bg-th-sidebar border-th-border mb-4">
      <h2 className="text-sm font-semibold text-th-text flex items-center gap-1.5 mb-1">
        <Share2 size={14} className="text-brand-500" /> Webhook — når kontrakten er underskrevet
      </h2>
      <p className="text-xs text-th-text-muted mb-4">
        Vi sender det underskrevne dokument hertil, så snart sidste underskrift er faldet.
        Det er samme adresse som <code className="font-mono">retur_url_signed</code> på det
        enkelte dokument — sender et kald sin egen med, gælder den; ellers bruges den her.{' '}
        <button onClick={() => navigate('/api-adgang')} className="text-brand-500 hover:underline">
          Se hvad vi sender
        </button>
      </p>

      {henter ? (
        <div className="h-11 rounded-lg bg-th-hover/40 animate-pulse" />
      ) : (
        <>
          <label className="text-sm font-medium text-th-text">Adresse</label>
          <div className="mt-1 flex flex-col sm:flex-row gap-2">
            <Input value={url} onChange={(e) => setUrl(e.target.value)}
              placeholder="https://ditsystem.dk/hentpost/underskrevet" maxLength={500} />
            <Button onClick={gem} disabled={!aendret || travl}
              className="shrink-0 bg-btn hover:bg-btn-hover text-btn-fg">
              {travl ? 'Gemmer…' : 'Gem'}
            </Button>
          </div>
          <p className="text-xs text-th-text-muted mt-1">
            Skal være en fuld https-adresse. Tøm feltet og gem for at slå den fra.
            Svarer din server ikke 2xx, prøver vi igen fem gange over godt fem timer.
          </p>

          <div className="border-t border-th-border mt-5 pt-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <label className="text-sm font-medium text-th-text flex items-center gap-1.5">
                <Lock size={13} className="text-brand-500" /> Hemmelighed
              </label>
              {!egenVises && (
                <div className="flex items-center gap-3 shrink-0">
                  <button onClick={() => setEgenVises(true)} disabled={travl}
                    className="text-xs text-th-text-muted hover:text-th-text transition-colors disabled:opacity-50">
                    Indsæt min egen
                  </button>
                  <button onClick={() => saetHemmelighed(null)} disabled={travl}
                    className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-th-text transition-colors disabled:opacity-50">
                    <RefreshCw size={13} /> {harHemmelighed ? 'Ny hemmelighed' : 'Lav hemmelighed'}
                  </button>
                </div>
              )}
            </div>
            <p className="text-xs text-th-text-muted mb-3">
              Vi signerer hvert kald med den, så dit system kan se, at det kommer fra os:{' '}
              <code className="font-mono">X-Signature: base64(HMAC-SHA256(rå krop, hemmelighed))</code>.
              Uden en hemmelighed kan enhver, der kender adressen, sende en falsk underskrevet
              kontrakt ind i dit system.
            </p>

            {egenVises && (
              <div className="flex flex-col sm:flex-row gap-2 mb-3">
                <Input value={egen} onChange={(e) => setEgen(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && egen.trim().length >= 16) saetHemmelighed(egen.trim()); }}
                  placeholder="Mindst 16 tegn, ingen mellemrum" maxLength={200} autoFocus />
                <div className="flex gap-2 shrink-0">
                  <Button onClick={() => saetHemmelighed(egen.trim())}
                    disabled={travl || egen.trim().length < 16}>Gem</Button>
                  <Button variant="ghost" onClick={() => { setEgenVises(false); setEgen(''); }}>
                    Annullér
                  </Button>
                </div>
              </div>
            )}

            {frisk ? (
              <div className="border-2 border-brand-500 rounded-lg p-4 bg-brand-500/5">
                <div className="flex items-start gap-2 mb-3">
                  <AlertCircle size={16} className="text-brand-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-th-text">Læg hemmeligheden ind i dit system nu</p>
                    <p className="text-xs text-th-text-secondary">
                      Det er eneste gang, den vises. Er den væk, må du lave en ny.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-th-hover border border-th-border rounded-lg px-3 py-2.5 text-xs font-mono text-th-text break-all">
                    {frisk}
                  </code>
                  <Button onClick={() => kopierTekst(frisk, 'Hemmeligheden er kopieret')} className="shrink-0">
                    <Copy size={15} className="mr-1.5" /> Kopiér
                  </Button>
                </div>
                <button onClick={() => setFrisk(null)}
                  className="text-xs text-th-text-muted hover:text-th-text mt-3 transition-colors">
                  Jeg har lagt den ind — skjul
                </button>
              </div>
            ) : harHemmelighed ? (
              <p className="text-xs text-th-text-muted italic">
                Sat — vist én gang, da den blev lavet. Lav en ny, hvis den er væk.
              </p>
            ) : (
              <p className="text-xs text-amber-600 dark:text-amber-400">
                Ingen hemmelighed endnu. Kaldene sendes usignerede, og dit system kan ikke
                kontrollere, at de kommer fra os.
              </p>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

// Nøglen hører til selskabet, ikke til kontoen: den bestemmer, hvilket navn
// dokumenterne sendes i. Derfor står den her ved siden af visningsnavn og logo
// — det er de tre ting, der tilsammen afgør, hvad modtageren ser.
function ApiNoegle({ selskab }) {
  const [noegle, setNoegle] = useState(null);
  const [henter, setHenter] = useState(true);
  const [travl, setTravl] = useState(false);
  // Klarteksten kommer kun med i svaret paa oprettelsen. Den holdes her og
  // forsvinder, saa snart der skiftes selskab — der er ingen vej til den bagefter.
  const [frisk, setFrisk] = useState(null);
  const navigate = useNavigate();

  const hent = React.useCallback(async () => {
    setHenter(true);
    setFrisk(null);
    try {
      const r = await axios.get(`${API}/companies/${selskab.id}/api-key`);
      setNoegle(r.data && r.data.id ? r.data : null);
    } catch {
      toast.error('Kunne ikke hente API-nøglen');
    } finally { setHenter(false); }
  }, [selskab.id]);

  useEffect(() => { hent(); }, [hent]);

  const opret = async () => {
    if (noegle && !window.confirm(
      `Lav en ny API-nøgle til ${selskab.name}?\n\nDen nuværende holder op med at virke med det samme, så systemer der bruger den skal rettes bagefter.`
    )) return;
    setTravl(true);
    try {
      const r = await axios.post(`${API}/companies/${selskab.id}/api-key`);
      setFrisk(r.data.noegle);
      setNoegle(r.data);
      toast.success(r.data.erstattede ? 'Ny nøgle oprettet — den gamle er lukket' : 'Nøgle oprettet');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke oprette nøglen');
    } finally { setTravl(false); }
  };

  const tilbagekald = async () => {
    if (!window.confirm(
      `Tilbagekald API-nøglen til ${selskab.name}?\n\nSystemer der bruger den holder op med at virke med det samme. Det kan ikke fortrydes.`
    )) return;
    setTravl(true);
    try {
      await axios.delete(`${API}/companies/${selskab.id}/api-key`);
      setNoegle(null); setFrisk(null);
      toast.success('Nøglen er tilbagekaldt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke tilbagekalde nøglen');
    } finally { setTravl(false); }
  };

  const dato = (iso) => {
    if (!iso) return null;
    try {
      return new Date(iso).toLocaleDateString('da-DK',
        { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <Card className="p-6 bg-th-sidebar border-th-border mb-4">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="text-sm font-semibold text-th-text flex items-center gap-1.5">
          <Key size={14} className="text-brand-500" /> API-nøgle
        </h2>
        {!henter && noegle && (
          <div className="flex items-center gap-3 shrink-0">
            <button onClick={opret} disabled={travl}
              className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-th-text transition-colors disabled:opacity-50"
              title="Lav en ny nøgle — den gamle holder op med at virke">
              <RefreshCw size={13} /> {travl ? 'Arbejder…' : 'Ny nøgle'}
            </button>
            <button onClick={tilbagekald} disabled={travl}
              className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-red-500 transition-colors disabled:opacity-50">
              <Trash2 size={13} /> Tilbagekald
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-th-text-muted mb-3">
        Lad et andet system aflevere dokumenter til underskrift i {selskab.name}s navn — uden
        at nogen skal logge ind. Nøglen sendes i hovedet{' '}
        <code className="font-mono">X-Api-Key</code>.{' '}
        <button onClick={() => navigate('/api-adgang')}
          className="text-brand-500 hover:underline">Se dokumentationen</button>
      </p>

      {/* Klarteksten vises én gang. Serveren gemmer kun et hash. */}
      {frisk && (
        <div className="border-2 border-brand-500 rounded-lg p-4 mb-3 bg-brand-500/5">
          <div className="flex items-start gap-2 mb-3">
            <AlertCircle size={16} className="text-brand-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-th-text">Gem nøglen nu</p>
              <p className="text-xs text-th-text-secondary">
                Det er eneste gang den vises. Kan du ikke finde den igen, må du lave en ny.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-th-hover border border-th-border rounded-lg px-3 py-2.5 text-xs font-mono text-th-text break-all">
              {frisk}
            </code>
            <Button onClick={() => kopierTekst(frisk, 'Nøglen er kopieret')} className="shrink-0">
              <Copy size={15} className="mr-1.5" /> Kopiér
            </Button>
          </div>
          <button onClick={() => setFrisk(null)}
            className="text-xs text-th-text-muted hover:text-th-text mt-3 transition-colors">
            Jeg har gemt den — skjul
          </button>
        </div>
      )}

      {henter ? (
        <div className="h-12 rounded-lg bg-th-hover/40 animate-pulse" />
      ) : noegle ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-th-text-muted">
          <code className="font-mono text-th-text-secondary">{noegle.praefiks}…</code>
          <span>oprettet {dato(noegle.oprettet_at)}</span>
          <span>
            {noegle.sidst_brugt_at ? `sidst brugt ${dato(noegle.sidst_brugt_at)}` : 'aldrig brugt'}
          </span>
        </div>
      ) : (
        <Button onClick={opret} disabled={travl}
          className="bg-btn hover:bg-btn-hover text-btn-fg">
          <Plus size={15} className="mr-1.5" /> {travl ? 'Laver nøgle…' : 'Lav API-nøgle'}
        </Button>
      )}
    </Card>
  );
}

// Nøgler fra dengang en nøgle hørte til kontoen frem for til et selskab. De
// virker uændret — derfor lukkes de ikke af sig selv — men de kan ikke laves
// igen, og de sender i kontoens navn. Kortet her findes for at få dem ryddet
// af vejen, og forsvinder af sig selv, når den sidste er væk.
function GamleNoegler({ selskab, onAendret }) {
  const [noegler, setNoegler] = useState([]);
  const [travl, setTravl] = useState(false);

  const hent = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api-keys`);
      setNoegler(Array.isArray(r.data) ? r.data : []);
    } catch { setNoegler([]); }
  }, []);

  useEffect(() => { hent(); }, [hent]);

  const knyt = async (n) => {
    if (!selskab) return;
    if (!window.confirm(
      `Knyt "${n.navn}" til ${selskab.name}?\n\nDokumenter sendt med nøglen kommer derefter fra ${selskab.name} — også dem, hvor systemet selv sender et andet CVR med.`
    )) return;
    setTravl(true);
    try {
      await axios.put(`${API}/api-keys/${n.id}/selskab`, { company_id: selskab.id });
      toast.success(`Nøglen hører nu til ${selskab.name}`);
      await hent(); onAendret?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke knytte nøglen');
    } finally { setTravl(false); }
  };

  const tilbagekald = async (n) => {
    if (!window.confirm(
      `Tilbagekald "${n.navn}"?\n\nSystemer der bruger nøglen holder op med at virke med det samme. Det kan ikke fortrydes.`
    )) return;
    setTravl(true);
    try {
      await axios.delete(`${API}/api-keys/${n.id}`);
      toast.success('Nøglen er tilbagekaldt');
      await hent();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke tilbagekalde nøglen');
    } finally { setTravl(false); }
  };

  if (noegler.length === 0) return null;

  return (
    <Card className="p-6 bg-th-sidebar border-th-border mb-4">
      <h2 className="text-sm font-semibold text-th-text flex items-center gap-1.5 mb-1">
        <AlertCircle size={14} className="text-amber-500" /> Nøgler uden selskab
      </h2>
      <p className="text-xs text-th-text-muted mb-4">
        Disse blev lavet, dengang en nøgle hørte til kontoen. De virker stadig, men sender
        i kontoens navn og kan ikke laves igen. Knyt dem til det selskab, de sender for,
        eller luk dem.
      </p>
      <div className="space-y-2">
        {noegler.map(n => (
          <div key={n.id} className="flex flex-wrap items-center justify-between gap-3 border border-th-border rounded-lg p-3">
            <div className="min-w-0">
              <p className="text-sm font-medium text-th-text truncate">{n.navn || 'Uden navn'}</p>
              <p className="text-xs text-th-text-muted font-mono mt-0.5">{n.praefiks}…</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {selskab && (
                <button onClick={() => knyt(n)} disabled={travl}
                  className="text-xs text-brand-500 hover:underline disabled:opacity-50">
                  Knyt til {selskab.name}
                </button>
              )}
              <button onClick={() => tilbagekald(n)} disabled={travl}
                className="inline-flex items-center gap-1.5 text-xs text-th-text-muted hover:text-red-500 transition-colors disabled:opacity-50">
                <Trash2 size={13} /> Tilbagekald
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// Rykkere gaelder BRUGEREN og ikke det enkelte selskab, saa kortet staar
// uden for selskabsvalget. Et dokument hoerer til den, der sendte det.
function Rykkere() {
  const [v, setV] = React.useState(null);
  const [standard, setStandard] = React.useState(null);
  const [gemt, setGemt] = React.useState(null);
  const [travl, setTravl] = React.useState(false);

  React.useEffect(() => {
    axios.get(`${API}/dokument-indstillinger`)
      .then(r => { setV(r.data.indstillinger); setGemt(r.data.indstillinger);
                   setStandard(r.data.standard); })
      .catch(() => toast.error('Kunne ikke hente forsendelsesindstillinger'));
  }, []);

  if (!v) return null;
  const aendret = JSON.stringify(v) !== JSON.stringify(gemt);
  const erStandard = standard && JSON.stringify(v) === JSON.stringify(standard);
  const saet = (k, val) => setV(p => ({ ...p, [k]: val }));

  const gem = async () => {
    setTravl(true);
    try {
      const r = await axios.put(`${API}/dokument-indstillinger`, v);
      setGemt(r.data.indstillinger); setV(r.data.indstillinger);
      toast.success('Forsendelsesindstillinger gemt');
    } catch { toast.error('Kunne ikke gemmes'); }
    finally { setTravl(false); }
  };

  // Den vigtigste oplysning paa siden: hvad der faktisk sker med en
  // underskriver, der ikke reagerer. Regnet ud af vaerdierne, saa den ikke
  // kan komme til at lyve, naar nogen aendrer dem.
  const trinListe = [];
  for (let i = 1; i <= v.antal_rykkere; i++) {
    const n = v.interval * i;
    const enhed = n === 1 ? { timer: 'time', dage: 'dag', uger: 'uge' }[v.enhed] : v.enhed;
    trinListe.push({ i, efter: `${n} ${enhed}`, sms: v.sms_ved_rykker > 0 && i >= v.sms_ved_rykker });
  }
  const forloeb = trinListe.map(t => `${t.i}. påmindelse efter ${t.efter} ${t.sms ? '(SMS)' : '(e-mail)'}`);

  if (TRE_KOLONNER) {
    // Tal vaelges med − og + som i designet. Graenserne er de samme, som
    // backenden haandhaever, saa feltet aldrig viser noget, der ikke gemmes.
    const taeller = (k, min, max, label) => (
      <div className="inline-flex items-center rounded-lg border border-th-border overflow-hidden bg-th-card">
        <button type="button" aria-label={`Færre ${label}`} disabled={v[k] <= min}
          onClick={() => saet(k, Math.max(min, v[k] - 1))}
          className="w-9 h-9 flex items-center justify-center text-th-text-muted hover:bg-th-hover disabled:opacity-40"><Minus size={14} /></button>
        <input type="number" min={min} max={max} value={v[k]} aria-label={label}
          onChange={(e) => saet(k, Math.max(min, Math.min(max, Number(e.target.value) || min)))}
          className="w-10 text-center bg-transparent text-sm font-medium text-th-text outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" />
        <button type="button" aria-label={`Flere ${label}`} disabled={v[k] >= max}
          onClick={() => saet(k, Math.min(max, v[k] + 1))}
          className="w-9 h-9 flex items-center justify-center text-th-text-muted hover:bg-th-hover disabled:opacity-40"><Plus size={14} /></button>
      </div>
    );
    const raekke = (titel, tekst, kontrol) => (
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 py-4 border-t border-th-border-light">
        <div className="min-w-0">
          <p className="text-sm font-medium text-th-text">{titel}</p>
          {tekst && <p className="text-xs text-th-text-muted mt-0.5">{tekst}</p>}
        </div>
        <div className="flex items-center gap-2 shrink-0">{kontrol}</div>
      </div>
    );
    return (
      <div className="rounded-xl bg-th-card border border-th-border-light p-5 sm:p-6 mb-4">
        <div className="flex items-start justify-between gap-4 pb-4">
          <div className="min-w-0">
            <h2 className="font-heading text-lg font-semibold text-th-text">Forsendelsesindstillinger</h2>
            <p className="text-sm text-th-text-muted mt-0.5">Påmind dem, der endnu ikke har skrevet under.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0 pt-1">
            <span className="text-xs text-th-text-muted">{v.rykker_til ? 'Automatisk' : 'Slået fra'}</span>
            <DbSwitch til={v.rykker_til} onSkift={() => saet('rykker_til', !v.rykker_til)} titel="Send påmindelser automatisk" />
          </div>
        </div>

        {!v.rykker_til ? (
          <p className="text-sm text-th-text-muted py-4 border-t border-th-border-light">
            Der sendes ingen påmindelser. Du kan stadig sende i hånden fra det enkelte dokument.
          </p>
        ) : (
          <>
            {raekke('Interval', 'Tid mellem påmindelserne', <>
              {taeller('interval', 1, 99, 'interval')}
              <select value={v.enhed} onChange={(e) => saet('enhed', e.target.value)} aria-label="Enhed"
                className="h-9 rounded-lg border border-th-border bg-th-card text-th-text px-3 text-sm">
                <option value="timer">timer</option>
                <option value="dage">dage</option>
                <option value="uger">uger</option>
              </select>
            </>)}
            {raekke('Antal påmindelser', 'Højst så mange til hver underskriver', taeller('antal_rykkere', 0, 10, 'påmindelser'))}
            {raekke('SMS fra påmindelse nr.', v.sms_ved_rykker > 0
              ? 'Kræver et mobilnummer på underskriveren — ellers sendes en e-mail'
              : '0 betyder, at alle påmindelser sendes som e-mail',
              taeller('sms_ved_rykker', 0, 10, 'SMS fra nr.'))}
            {raekke('Kun på hverdage', 'Ingen påmindelser lørdag og søndag',
              <DbSwitch til={v.kun_hverdage} onSkift={() => saet('kun_hverdage', !v.kun_hverdage)} titel="Kun på hverdage" />)}

            {/* Forloebet regnes ud af vaerdierne, saa det ikke kan lyve */}
            <div className="rounded-lg bg-th-inset p-4 mt-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-th-text-muted mb-3">Sådan bliver forløbet</p>
              {trinListe.length === 0 ? (
                <p className="text-sm text-th-text-muted">Der sendes ingen påmindelser.</p>
              ) : (
                <ol className="flex flex-col gap-2">
                  {trinListe.map(t => (
                    <li key={t.i} className="flex items-center gap-3 text-sm">
                      <span className="w-6 h-6 rounded-full border-2 border-db-label text-[11px] font-semibold text-th-text flex items-center justify-center shrink-0">{t.i}</span>
                      <span className="flex-1 text-th-text">Efter {t.efter}</span>
                      <DbTag variant={t.sms ? 'moerk' : 'lys'} prik={t.sms ? 'bg-db-route' : undefined}>{t.sms ? 'SMS' : 'E-mail'}</DbTag>
                    </li>
                  ))}
                </ol>
              )}
              <p className="text-xs text-th-text-muted mt-3">
                Påmindelser sendes kl. 10. En underskriver, hvis link er udløbet, påmindes ikke —
                der ville ikke være nogen vej videre for vedkommende.
              </p>
            </div>
          </>
        )}

        <div className="flex flex-wrap items-center gap-3 pt-5">
          <button onClick={gem} disabled={!aendret || travl} className={DB_KNAP_PRIMAER}>
            {travl ? 'Gemmer…' : 'Gem indstillinger'}
          </button>
          {aendret && <DbTag variant="advarsel">Ugemte ændringer</DbTag>}
          {!aendret && erStandard && <span className="text-xs text-th-text-muted">Standardindstillinger</span>}
          {!aendret && !erStandard && standard && (
            <button onClick={() => setV(standard)} className={DB_KNAP}>Nulstil til standard</button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Card className="p-6 bg-th-sidebar border-th-border mb-4 space-y-5">
      <div>
        <h2 className="text-lg font-heading font-semibold text-th-text">Forsendelsesindstillinger</h2>
        <p className="text-sm text-th-text-secondary mt-0.5">
          Påmind dem, der endnu ikke har skrevet under.
        </p>
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={v.rykker_til} className="mt-1"
               onChange={(e) => saet('rykker_til', e.target.checked)} />
        <span>
          <span className="text-sm font-medium text-th-text">Send påmindelser automatisk</span>
          <span className="block text-xs text-th-text-muted">
            Slået fra sker der ingenting — du kan stadig sende i hånden fra det enkelte dokument.
          </span>
        </span>
      </label>

      {v.rykker_til && (
        <>
          <div>
            <span className="text-sm font-medium text-th-text">Standard påmindelsesinterval</span>
            <div className="flex items-center gap-2 mt-1.5">
              {/* Tal og enhed staar sammen: et interval uden enhed betyder
                  ingenting, og de to maa ikke kunne komme fra hinanden. */}
              <div className="flex items-center rounded-md border border-th-border overflow-hidden">
                <button type="button" aria-label="Færre"
                        onClick={() => saet('interval', Math.max(1, v.interval - 1))}
                        className="px-3 py-1.5 text-th-text-muted hover:bg-th-hover">−</button>
                <input type="number" min="1" max="99" value={v.interval}
                       onChange={(e) => saet('interval', Math.max(1, Math.min(99, Number(e.target.value) || 1)))}
                       className="w-14 text-center bg-transparent text-th-text outline-none py-1.5" />
                <button type="button" aria-label="Flere"
                        onClick={() => saet('interval', Math.min(99, v.interval + 1))}
                        className="px-3 py-1.5 text-th-text-muted hover:bg-th-hover">+</button>
              </div>
              <select value={v.enhed} onChange={(e) => saet('enhed', e.target.value)}
                      className="rounded-md border border-th-border bg-th-input text-th-text px-3 py-1.5 text-sm">
                <option value="timer">timer</option>
                <option value="dage">dage</option>
                <option value="uger">uger</option>
              </select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs text-th-text-muted">Antal påmindelser</span>
              <Input type="number" min="0" max="10" value={v.antal_rykkere}
                     onChange={(e) => saet('antal_rykkere', Number(e.target.value))} />
            </label>
            <label className="block">
              <span className="text-xs text-th-text-muted">SMS fra og med påmindelse nr.</span>
              <Input type="number" min="0" max="10" value={v.sms_ved_rykker}
                     onChange={(e) => saet('sms_ved_rykker', Number(e.target.value))} />
            </label>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input type="checkbox" checked={v.kun_hverdage} className="mt-1"
                   onChange={(e) => saet('kun_hverdage', e.target.checked)} />
            <span>
              <span className="text-sm font-medium text-th-text">Kun på hverdage</span>
              <span className="block text-xs text-th-text-muted">
                Ingen påmindelser lørdag og søndag.
              </span>
            </span>
          </label>

          <div className="rounded-md border border-th-border-light p-3">
            <p className="text-xs text-th-text-muted mb-1">Sådan bliver forløbet:</p>
            {forloeb.length === 0
              ? <p className="text-sm text-th-text-secondary">Der sendes ingen påmindelser.</p>
              : <ul className="text-sm text-th-text-secondary space-y-0.5">
                  {forloeb.map((t, i) => <li key={i}>· {t}</li>)}
                </ul>}
            <p className="text-xs text-th-text-faint mt-2">
              Påmindelser sendes kl. 10. En underskriver, hvis link er udløbet, påmindes ikke —
              der ville ikke være nogen vej videre for vedkommende.
            </p>
          </div>

          {v.sms_ved_rykker > 0 && (
            <p className="text-xs text-th-text-muted">
              SMS kræver, at underskriveren er oprettet med et mobilnummer. Mangler det,
              sendes en e-mail i stedet.
            </p>
          )}
        </>
      )}

      <div className="flex items-center gap-3 pt-1">
        <Button onClick={gem} disabled={!aendret || travl}
                className="bg-btn hover:bg-btn-hover text-btn-fg">
          {travl ? 'Gemmer…' : 'Gem forsendelsesindstillinger'}
        </Button>
        {aendret && <span className="text-xs text-th-text-muted">Ugemte ændringer</span>}
        {!aendret && erStandard && <span className="text-xs text-th-text-faint">Standard</span>}
        {!aendret && !erStandard && standard && (
          <button onClick={() => setV(standard)}
                  className="text-xs text-th-text-muted hover:text-th-text underline">
            Nulstil til standard
          </button>
        )}
      </div>
    </Card>
  );
}

// Mærket paa et selskab, der spejles med den anden instans.
//
// Der skelnes mellem "spejling er slaaet til" og "de to ER i synk". Et flag
// kan staa og vaere sandt, mens modparten har vaeret utilgaengelig i en uge —
// og saa ville et groent maerke lyve. sidst_spejlet_at saettes kun, naar
// modparten faktisk har svaret for netop dette selskab.
function SpejlMaerke({ selskab }) {
  if (!selskab?.spejl) return null;
  const partner = BRAND.partner_navn || 'den anden instans';
  const sidst = selskab.sidst_spejlet_at ? new Date(selskab.sidst_spejlet_at) : null;
  const minutter = sidst ? Math.floor((Date.now() - sidst.getTime()) / 60000) : null;
  const friskt = minutter !== null && minutter < 15;

  const naar = minutter === null ? 'afventer første afstemning'
    : minutter < 1 ? 'afstemt lige nu'
    : minutter < 60 ? `afstemt for ${minutter} min. siden`
    : minutter < 1440 ? `afstemt for ${Math.floor(minutter / 60)} t. siden`
    : `afstemt for ${Math.floor(minutter / 1440)} dage siden`;

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs mt-1.5 px-2 py-0.5 rounded-full border
      ${friskt ? 'text-emerald-700 dark:text-emerald-400 border-emerald-600/30 bg-emerald-500/10'
               : 'text-amber-700 dark:text-amber-400 border-amber-600/30 bg-amber-500/10'}`}
          title={sidst ? sidst.toLocaleString('da-DK') : undefined}>
      <RefreshCw size={11} />
      {friskt ? `I synk med ${partner}` : `Spejlet med ${partner}`}
      <span className="opacity-70">· {naar}</span>
    </span>
  );
}

function SettingsView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selskaber, setSelskaber] = React.useState([]);
  const [valgt, setValgt] = React.useState(null);
  const [v, setV] = React.useState({ visningsnavn: '', mail_tekst: '',
                                     afsender_sidst: false, afsender_email: '' });
  const [gemt, setGemt] = React.useState(null);
  const [logoUrl, setLogoUrl] = React.useState(null);
  const [travl, setTravl] = React.useState(false);
  const filRef = React.useRef(null);

  React.useEffect(() => {
    axios.get(`${API}/companies/own`)
      .then(r => {
        const liste = Array.isArray(r.data) ? r.data : [];
        setSelskaber(liste);
        if (liste.length && !valgt) setValgt(liste[0].id);
      })
      .catch(() => toast.error('Kunne ikke hente selskaber'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hentLogo = React.useCallback(async (id) => {
    // Logoet ligger bag login, saa det kan ikke saettes direkte i en <img src>.
    // Det hentes som blob og vises derfra.
    setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
    try {
      const r = await axios.get(`${API}/companies/${id}/logo`, { responseType: 'blob' });
      setLogoUrl(URL.createObjectURL(r.data));
    } catch { /* intet logo — helt normalt */ }
  }, []);

  React.useEffect(() => {
    if (!valgt) return;
    axios.get(`${API}/companies/${valgt}/settings`)
      .then(r => {
        // Feltet staar udfyldt med selskabets navn, naar der ikke er valgt et
        // andet. Det er naesten altid det, man vil have, og et tomt felt med
        // navnet i graat faar folk til at skrive det samme ind i haanden.
        //
        // Det samme laegges i "gemt", saa siden ikke aabner med "Ugemte
        // aendringer" over noget, brugeren ikke har roert. Gemmes der aldrig,
        // staar der fortsat ingenting i databasen — og mailen falder tilbage
        // til det registrerede navn, praecis som foer.
        const registreret = (selskaber.find(c => c.id === valgt) || {}).name || '';
        const d = { visningsnavn: r.data.visningsnavn || registreret,
                    mail_tekst: r.data.mail_tekst || '',
                    afsender_sidst: !!r.data.afsender_sidst,
                    afsender_email: r.data.afsender_email || '' };
        setV(d); setGemt(d);
      })
      .catch(() => toast.error('Kunne ikke hente indstillinger'));
    hentLogo(valgt);
  }, [valgt, hentLogo, selskaber]);

  const aendret = gemt && JSON.stringify(v) !== JSON.stringify(gemt);
  const selskab = selskaber.find(c => c.id === valgt);

  const gem = async () => {
    setTravl(true);
    try {
      await axios.put(`${API}/companies/${valgt}/settings`, v);
      setGemt(v);
      toast.success('Indstillinger gemt');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme');
    } finally { setTravl(false); }
  };

  const uploadLogo = async (fil) => {
    if (!fil) return;
    const fd = new FormData();
    fd.append('fil', fil);
    setTravl(true);
    try {
      await axios.post(`${API}/companies/${valgt}/logo`, fd);
      toast.success('Logo gemt');
      hentLogo(valgt);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunne ikke gemme logoet');
    } finally { setTravl(false); if (filRef.current) filRef.current.value = ''; }
  };

  const sletLogo = async () => {
    if (!window.confirm('Fjern selskabets logo? Mails falder tilbage til kontoens.')) return;
    try {
      await axios.delete(`${API}/companies/${valgt}/logo`);
      setLogoUrl(prev => { if (prev) URL.revokeObjectURL(prev); return null; });
      toast.success('Logo fjernet');
    } catch { toast.error('Kunne ikke fjerne logoet'); }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={() => navigate('/dokumenter')}
        className="text-sm text-th-text-muted hover:text-th-text-secondary mb-4 inline-flex items-center gap-1">
        ← Tilbage til dokumenter
      </button>
      <h1 className="text-3xl font-heading font-bold text-th-text mb-1">Indstillinger</h1>
      <p className="text-th-text-secondary mb-8">Sådan ser dine underskriftsmails ud</p>

      {selskaber.length === 0 ? (
        <Card className="p-8 bg-th-sidebar border-th-border text-center text-th-text-muted">
          Du har ingen selskaber endnu.
        </Card>
      ) : (
        <>
          <Card className="p-6 bg-th-sidebar border-th-border mb-4">
            <label className="text-xs uppercase tracking-wider text-th-text-secondary font-semibold block mb-2">Selskab</label>
            <select value={valgt || ''} onChange={(e) => setValgt(e.target.value)}
              className="w-full h-11 px-3 rounded-lg bg-th-input border border-th-border text-th-text">
              {selskaber.map(c => <option key={c.id} value={c.id}>{c.name} · CVR {c.cvr}</option>)}
            </select>
          </Card>

          {/* DigitalBox: noeglen hoerer til det valgte selskab og staar derfor
              lige under vaelgeren, ikke nederst efter mailindstillingerne */}
          {TRE_KOLONNER && selskab && (
            <div className="mb-4">
              <ApiNoegle key={selskab.id} selskab={selskab} />
              <GamleNoegler selskab={selskab} />
            </div>
          )}

          <Card className="p-6 bg-th-sidebar border-th-border mb-4 space-y-5">
            <div>
              <label className="text-sm font-medium text-th-text">Visningsnavn</label>
              <input value={v.visningsnavn} maxLength={120}
                onChange={(e) => setV(x => ({ ...x, visningsnavn: e.target.value }))}
                placeholder={selskab?.name || ''}
                className="mt-1 w-full h-11 px-3 rounded-lg bg-th-input border border-th-border text-th-text" />
              <p className="text-xs text-th-text-muted mt-1">
                Navnet modtagerne ser. Det er udfyldt med selskabets registrerede navn — ret
                det, hvis jeres kunder kender jer som noget andet. Ryddes feltet, bruges
                “{selskab?.name}” igen. Registret ændres ikke — det bruges til at finde jeres post.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-th-text">Logo</label>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-40 h-20 rounded-lg border border-th-border bg-th-input flex items-center justify-center overflow-hidden">
                  {logoUrl
                    ? <img src={logoUrl} alt="Logo" className="max-h-16 max-w-36 object-contain" />
                    : <span className="text-xs text-th-text-muted">Intet logo</span>}
                </div>
                <div className="flex flex-col gap-2">
                  <input ref={filRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={(e) => uploadLogo(e.target.files?.[0])} className="hidden" id="selskabslogo" />
                  <label htmlFor="selskabslogo"
                    className="cursor-pointer text-sm px-3 py-2 rounded-lg bg-btn hover:bg-btn-hover text-btn-fg text-center">
                    Vælg logo
                  </label>
                  {logoUrl && (
                    <button onClick={sletLogo} className="text-sm text-th-text-muted hover:text-red-500">Fjern</button>
                  )}
                </div>
              </div>
              <p className="text-xs text-th-text-muted mt-2">
                Vises øverst i mails om dette selskabs dokumenter. Uden et logo bruges kontoens.
              </p>
            </div>

            <div>
              <label className="text-sm font-medium text-th-text">Tekst i underskriftsmails</label>
              <textarea value={v.mail_tekst} maxLength={1200} rows={4}
                onChange={(e) => setV(x => ({ ...x, mail_tekst: e.target.value }))}
                placeholder="Fx: Vi glæder os til samarbejdet. Har du spørgsmål, er du velkommen til at ringe."
                className="mt-1 w-full px-3 py-2 rounded-lg bg-th-input border border-th-border text-th-text text-sm" />
              <p className="text-xs text-th-text-muted mt-1">
                Står øverst i mailen, før den faste tekst. Du kan bruge{' '}
                <code className="font-mono">{'{selskab}'}</code>,{' '}
                <code className="font-mono">{'{dokument}'}</code> og{' '}
                <code className="font-mono">{'{afsender}'}</code>. Formatering fjernes —
                mailen sendes i vores navn, så kun almindelig tekst slipper igennem.
              </p>
            </div>

            <div className="border-t border-th-border pt-5">
              <NotifKontakt til={v.afsender_sidst} onSkift={() => setV(x => ({ ...x, afsender_sidst: !x.afsender_sidst }))}
                titel="Du underskriver til sidst"
                beskrivelse="Modparterne får alle deres link med det samme. Dit eget kommer først, når de alle har skrevet under — så du ikke underskriver noget, de endnu ikke har accepteret. Afviser nogen, får du intet link." />

              {/* Uden den her adresse kan reglen ikke virke for andre end den,
                  der selv er logget ind. Opretter et bureau kontrakter for sine
                  kunder, er kontoen bureauets, mens den der skriver under for
                  selskabet er en helt anden — og saa fandt reglen ingen
                  afsender og sendte alle links paa én gang. */}
              {v.afsender_sidst && (
                <div className="mt-4 pl-0 sm:pl-12">
                  <label className="text-sm font-medium text-th-text">Jeres underskriver</label>
                  <input type="email" value={v.afsender_email} maxLength={200}
                    onChange={(e) => setV(x => ({ ...x, afsender_email: e.target.value }))}
                    placeholder={user?.email || 'navn@selskab.dk'}
                    className="mt-1 w-full h-11 px-3 rounded-lg bg-th-input border border-th-border text-th-text" />
                  <p className="text-xs text-th-text-muted mt-1">
                    Den underskriver, der skriver under <strong>for selskabet</strong>. Det er
                    hende, der venter til sidst. Står feltet tomt, bruges kontoens egen adresse
                    ({user?.email}) — og opretter du kontrakter på andres vegne, er det
                    sjældent den rigtige.
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Button onClick={gem} disabled={!aendret || travl} className="bg-btn hover:bg-btn-hover text-btn-fg">
                {travl ? 'Gemmer…' : 'Gem indstillinger'}
              </Button>
              {aendret && <span className="text-xs text-th-text-muted">Ugemte ændringer</span>}
            </div>
          </Card>

          {selskab && <Webhook key={`wh-${selskab.id}`} selskab={selskab} />}
          {/* DigitalBox har noeglerne oeverst, under selskabsvaelgeren */}
          {!TRE_KOLONNER && selskab && <ApiNoegle key={selskab.id} selskab={selskab} />}
          {!TRE_KOLONNER && <GamleNoegler selskab={selskab} />}
        </>
      )}

      <Rykkere />
    </div>
  );
}

function DashboardPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, logout, fetchUser } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [permissions, setPermissions] = useState(null);
  const [ulaesteTal, setUlaesteTal] = useState({});
  // DigitalBox: uden et eneste selskab (eget eller tilknyttet) er der intet at
  // se i appen, saa man sendes til Selskaber — uanset om man kom ind med e-mail-
  // link, SMS, Google eller Microsoft. null = ved det ikke endnu.
  const [harSelskab, setHarSelskab] = useState(null);
  useEffect(() => {
    if (!TRE_KOLONNER || !user || harSelskab === true) return;
    // Spoerg igen ved hvert sideskift, indtil der er et: saa slipper man fri,
    // saa snart det foerste er tilfoejet. "Ved ikke" imens, saa man ikke
    // sendes tilbage paa et foraeldet svar.
    setHarSelskab(prev => (prev === false ? null : prev));
    axios.get(`${API}/companies`).then(r => setHarSelskab((r.data || []).length > 0)).catch(() => {});
  }, [user, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const fri = ['/selskaber', '/opret', '/profil'];
    // Admin-siderne handler om ALLE selskaber, ikke adminens egne. En
    // adminkonto uden eget selskab skal stadig kunne arbejde der.
    const adminSide = user?.is_admin && location.pathname.startsWith('/admin');
    if (harSelskab === false && !fri.includes(location.pathname) && !adminSide) navigate('/selskaber', { replace: true });
  }, [harSelskab, location.pathname, navigate, user]);

  useEffect(() => {
    if (!loading && !user) navigate('/login');
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      axios.get(`${API}/my-permissions`).then(r => setPermissions(r.data)).catch(() => setPermissions({ role: 'owner', allowed_pages: [] }));
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen bg-th-body flex items-center justify-center">
        <div className="text-th-text-secondary">Indlæser...</div>
      </div>
    );
  }

  const iIndbakken = location.pathname === '/indbakke' || location.pathname.startsWith('/indbakke/');
  // DigitalBox' indbakke fylder hele skaermen i tre kolonner og har derfor
  // hverken margen eller sideruller; alle andre sider ser ud som foer.
  const fuldHoejde = TRE_KOLONNER && iIndbakken;
  const SidebarKomponent = TRE_KOLONNER ? DbSidebar : Sidebar;

  // Fra 1400 px staar DigitalBox som et kort paa den moerke baggrund, som i
  // designet. Graensen ligger over den stoerste iPad paa langs (1366 px): paa
  // tablet og mobil er pladsen for dyr til en ramme, og appen gaar til kanten.
  const indhold = (
    <>
      {/* Mobile menu button */}
      <button
        className={TRE_KOLONNER
          ? 'fixed top-3 left-3 z-50 lg:hidden bg-th-card border border-th-border-light p-2.5 rounded-lg text-th-text'
          : 'always-dark fixed top-3 left-3 z-50 lg:hidden bg-[#1e293b] p-2.5 rounded-lg text-[#94a3b8]'}
        onClick={() => setMobileOpen(true)}
      >
        <Menu size={20} />
      </button>

      <SidebarKomponent
        user={user}
        logout={logout}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
        permissions={permissions}
        ulaesteTal={ulaesteTal}
      />

      <main className={fuldHoejde
        ? 'flex-1 lg:ml-[248px] min-[1400px]:ml-0 min-w-0 h-screen min-[1400px]:h-full flex flex-col pt-14 lg:pt-0'
        : `flex-1 ${TRE_KOLONNER ? 'lg:ml-[248px] min-[1400px]:ml-0 min-[1400px]:h-full min-[1400px]:overflow-y-auto' : 'lg:ml-64'} px-3 pt-14 pb-4 sm:px-4 sm:pt-16 lg:pt-8 lg:px-8 lg:pb-8 min-w-0 overflow-x-hidden`}>
        <EmailBekraeftBanner user={user} fetchUser={fetchUser} />
        <TelefonBekraeftBanner user={user} fetchUser={fetchUser} />
        {iIndbakken && (fuldHoejde
          ? <div className="flex-1 min-h-0"><InboxView onUlaesteTal={setUlaesteTal} /></div>
          : <InboxView />)}
        {location.pathname === '/videresend' && <RulesView />}
        {location.pathname === '/profil' && <ProfileView user={user} fetchUser={fetchUser} permissions={permissions} />}
        {/* DigitalBox har API'et under Dokumenter; den gamle adresse sender videre */}
        {location.pathname === '/api-adgang' && (TRE_KOLONNER ? <Navigate to="/dokumenter/api" replace /> : <ApiView user={user} />)}
        {TRE_KOLONNER && location.pathname === '/dokumenter/api' && <ApiView user={user} />}
        {location.pathname === '/selskaber' && <MyCompaniesView user={user} fetchUser={fetchUser} />}
        {location.pathname === '/modtagere' && <RecipientsView />}
        {location.pathname === '/adressebog' && <ContactsView />}
        {(location.pathname === '/admin' || (location.pathname === '/admin/selskaber' && !TRE_KOLONNER)) && <AdminView />}
        {location.pathname === '/admin/selskaber' && TRE_KOLONNER && <AdminSelskaberView />}
        {location.pathname === '/admin/onboarding' && TRE_KOLONNER && <AdminOnboardingView />}
        {location.pathname === '/admin/indbakke' && <AdminInboxView />}
        {location.pathname === '/opret' && <GuideView user={user} />}
        {location.pathname === '/dokumenter' && <DocumentsView />}
        {location.pathname === '/dokumenter/indstillinger' && <SettingsView />}
      </main>
    </>
  );

  if (!TRE_KOLONNER) return <div className="min-h-screen bg-th-body flex">{indhold}</div>;
  return (
    <div className="min-h-screen bg-th-body min-[1400px]:h-screen min-[1400px]:bg-db-ink min-[1400px]:p-8">
      <div className="flex min-h-screen min-[1400px]:min-h-0 min-[1400px]:h-full min-[1400px]:bg-th-body min-[1400px]:rounded-[20px] min-[1400px]:overflow-hidden min-[1400px]:shadow-[0_40px_80px_-20px_rgba(0,0,0,0.4)]">
        {indhold}
      </div>
    </div>
  );
}

export default DashboardPage;
